import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { DraftNoteSection, FormSection } from '@/components/documents/DocumentFormBits';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
/*
 * The note box at the foot of this form, shared with `QuoteFormModal`.
 *
 * ⚠️ It is not a column on `d_invoices` — it is a `d_notes` row, the same
 * activity system the Tasks & notes drawer shows — so it is a SECOND request
 * against an invoice that has to exist first. `writeDraftNote` never throws and
 * never sends an empty body; the component owns every sentence about what became
 * of it, so the two forms cannot word one outcome two ways. Read `draft-note.ts`
 * before changing either half.
 */
import { writeDraftNote, type DraftNoteOutcome } from '@/components/documents/draft-note';
/*
 * ⚠️ `DOCUMENT_LINE_AMOUNT_OPTIONS` and `USE_COMPANY_DEFAULT` are no longer
 * imported here — 2026-08-31, with the Tax SELECT. They were the option list and
 * the empty option's label, and there is no list left to build: the tax
 * treatment is derived from the company — else from the company's BILLING
 * REGION — and read-only.
 *
 * ⚠️ **`documentLineAmountLabel` replaces both and is the SAME vocabulary read
 * from the other end** — one module still decides how this enum is worded across
 * a quote and the invoice it converts into. Do not put a local `EXCLUSIVE` →
 * "Tax exclusive" map in this file to save an import; that is the drift the
 * module exists to stop, and it prints on a customer's document.
 */
import {
  AMOUNT_PATTERN,
  CURRENCY_PATTERN,
  TOTAL_KEYS,
  TOTAL_LABELS,
  UNCLEARABLE_TOTALS,
  documentLineAmountLabel,
  toDocumentLineAmountType,
} from '@/components/documents/document-form';
/*
 * ⚠️ READ-ONLY, and it is the third of the three ways this form's line band can
 * render. Converting a quote copies its lines server-side, in the same
 * transaction that makes the invoice — so in that mode the rows below are a
 * preview of somebody else's write, not a grid anybody is filling in. Read that
 * file's header before making it editable: an editable preview here could only
 * discard what was typed or re-apply it afterwards against a document that
 * already exists.
 */
import { ApplyCreditButton } from '@/components/invoices/ApplyCreditButton';
import { InvoiceCreditSummary } from '@/components/invoices/InvoiceCreditSummary';
import { InvoiceMoney } from '@/components/invoices/InvoiceBits';
/*
 * ⚠️ **The quote conversion's override grammar.** `POST /quotes/{id}/convert`
 * gained an optional `lines` key on 2026-08-31; everything about what it means
 * — and about why `quoteProductIds` is deliberately NOT on that endpoint — is
 * in the module header.
 */
import {
  quoteConvertLineFieldErrors,
  type QuoteConvertInput,
  type QuoteConvertLineOverride,
} from '@/lib/quote-convert-lines';
/*
 * ⚠️ Mounted in BOTH of its modes by this one form — `draft` before the invoice
 * exists and `saved` the moment it does. See the line-items band below: the
 * draft rows are handed up here as exactly the bodies `POST
 * /invoices/{id}/products` takes, and this form writes them after the create.
 */
import {
  InvoiceLineItemsTable,
  /*
   * ⚠️ **THREE states, and a boolean would lose the third.** `known: false` is
   * *the company record has not been read* — in flight, failed, or a form that has
   * moved to another company — and is NOT the same fact as a company that states
   * no rate. On this route the second one also predicts a refusal, since
   * `POST /deals/{id}/invoices` answers `COMPANY_HAS_NO_TAX_RATE` when a deal with
   * lines meets a company with no rate, so collapsing them would turn "we have not
   * asked" into "this will be refused".
   */
  type CompanySalesTax,
} from '@/components/invoices/InvoiceLineItemsTable';
/*
 * ⚠️ The push REPORT is rendered by the component the list rows already use, and
 * every string in it comes from `rowPushReport`. This form does not word a Xero
 * outcome of its own: one refusal described two ways across a list row, a detail
 * page and this modal is the exact drift `@/lib/xero` was consolidated to stop.
 */
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
import { ApiError, api } from '@/lib/api';
/*
 * The planned basket a raise seeds the line grid with. Imported rather than
 * restated — `plannedProductLines()` owns the one judgement a seed needs, which
 * is whether a planned decimal quantity can be a line quantity.
 */
import type { PlannedProductLine } from '@/lib/api';
import {
  CONTACT_NOT_ON_COMPANY,
  contactScopeIsAuthoritative,
  primaryContactSeed,
} from '@/lib/contact-scope';
import { ContactPickerField } from '@/components/contacts/ContactPickerField';
/*
 * 🔴 **`@/lib/xero-tracking-location` is no longer imported here — 2026-08-31.**
 * `trackingLocationOf` fed the derived-Location readout under the tracking
 * override boxes, and both went together.
 *
 * ⚠️ **It is very much alive elsewhere and must not be inlined if it comes back
 * here.** It is the one copy of the rule that a project covering two or more
 * regions pushes with NO Location — `regions[0].name` is the wrong shortcut, in
 * the reassuring direction.
 */
import { companyDocumentName, pluralise } from '@/lib/format';
/*
 * ⚠️ The rate's validity rule and every reading of a rate live in ONE module,
 * and this form does neither by hand. A rate is an exact decimal string with up
 * to six places: `Number(rate)` is the float round-trip every money path in
 * this app exists to avoid, and it fails quietly. This form holds the string,
 * checks its SHAPE with `isValidCurrencyRate`, and sends it verbatim — the only
 * code that parses one is `@/lib/currency-conversion`.
 */
import {
  CURRENCY_RATE_MAX_DP,
  exchangeRateOf,
  formatRateLine,
  isValidCurrencyRate,
  type ExchangeRate,
} from '@/lib/currency-conversion';
/*
 * ⚠️ `todayIsoDate` and `addDaysIso`, and NEVER `new Date()` inline. A bill date
 * is a bare calendar day with no time and no zone; `toISOString().slice(0,10)`
 * and `new Date('2026-08-28')` are the same off-by-one mirrored, and either one
 * dates an invoice a day wrong for anyone west of Greenwich. Both helpers build
 * from LOCAL parts on the way in and on the way out — see their own comments.
 */
import { normaliseStatusName, todayIsoDate } from '@/lib/invoice-utils';
import { denialReason } from '@/lib/permissions';
/*
 * ⚠️ The prefill and the follow-up key list are ONE module, and it is pure on
 * purpose — it reproduces the SERVER's own convert copy field for field, so a
 * value invented here rather than read out of that module would make this form
 * produce a different document from the button it replaced. Read its header
 * before changing any seeded value.
 */
import {
  CONVERT_FOLLOW_UP_PATCH_KEYS,
  invoicePrefillFromQuote,
} from '@/lib/quote-to-invoice';
/*
 * ⚠️ ONE copy of each, and both moved here out of `QuoteDetailPage` when Convert
 * became this modal — a component importing a page is an import cycle. The code
 * is the discriminator between two 409s that mean opposite things; the sentence
 * is the single wording every rendering of the staged refusal reads.
 */
import {
  QUOTE_ALREADY_CONVERTED_CODE,
  QUOTE_IS_STAGED_CODE,
  STAGED_CONVERT_REASON,
  addDaysIso,
} from '@/lib/quote-utils';
import { useAuth } from '@/lib/use-auth';
/*
 * ⚠️ `currencyOptions` and `useCurrencyOptions` are no longer imported —
 * 2026-08-31, with the Currency picker. Both are very much alive elsewhere
 * (`InvoiceLineItemsTable`, `UserTargetsModal`, `/catalog`); this form simply
 * has no list to build. See the note where the hook used to be called for why
 * restoring it to "label" the code would be a rate-limited call that adds
 * nothing.
 */
import { selectableStatuses, useInvoiceStatusOptions } from '@/lib/use-invoice-options';
/*
 * 🔴 **`@/lib/use-project-options` is no longer imported here — 2026-08-31.**
 * *SUPERSEDED: "Only the hook now … the one thing it still reads a project for
 * is the region and intake behind the two DERIVED Xero tracking hints."* Those
 * hints were the last reader and went with the tracking overrides, so the whole
 * project fetch left with them. `QuoteFormModal` dropped its copy in the same
 * pass, for the same reason.
 */
/*
 * ⚠️ `use-user-options` is deliberately NOT imported here any more. It fed the
 * Owner Combobox, which the read-only Account lead row replaced — and the
 * account lead is resolved on the record, so there is no staff directory to
 * read and no request to spend against the shared limiter. ⚠️ *SUPERSEDED:
 * "120/60s."* It is **12,000/60s** (corrected 2026-09-17); there is still
 * nothing to read, which is the whole of the reason.
 */
import { brandingThemeOptions, useXeroBrandingThemes } from '@/lib/use-xero-branding-themes';
/*
 * ⚠️ The Xero GUID is read off the LINK ENVELOPE and from nowhere else.
 * `Invoice.xeroInvoiceId` is a reserved column that nothing populates, so
 * rendering it would show an empty box on every invoice in the system while the
 * real identifier sat one helper call away. `linkOf` is the single reader — see
 * the note in `@/lib/xero`, and `InvoiceDetailPage`, which answers "is this in
 * Xero?" the same way.
 */
import {
  autoPushOutcomeOf,
  conversionPushReport,
  rowPushReport,
  type XeroPushReport,
  type XeroRowPushReport,
} from '@/lib/xero';
import { useXero } from '@/lib/xero-context';
/*
 * ⚠️ Nothing from `@/lib/xero-tax-rates` is imported here — re-importing it
 * would revive a second, obsolete display of the company's Xero Sales
 * defaults. Those defaults are collected by `XeroSalesDefaultsDialog`, at push
 * time, onto the COMPANY; a company recording none is refused loudly by
 * `XERO_NO_TAX_TYPE`, which is the real guarantee.
 */
/*
 * ⚠️ **NOTHING FROM `@/lib/xero-cooldown` IS IMPORTED ANY MORE, AS OF
 * 2026-08-31.** `noteXeroRateLimit`, `xeroCooldownNotSentMessage` and
 * `xeroCooldownSecondsRemaining` were read here to pace, and to report on,
 * THIS FORM's own Xero call. It makes none: the server pushes inside the save.
 * The comment that stood here read *"Imported for the WAIT, not for
 * suppression … nothing here consults the cooldown before pushing"*, which is
 * now true in the stronger sense that there is no push here to consult it for.
 * A 429 met by a save is the SERVER's to record and the server's to pace.
 */
/*
 * ⚠️ **The deal→invoice conversion's shared vocabulary, imported rather than
 * re-worded.** All four are facts a reader cannot see anywhere on this form,
 * and three of them are also rendered by another screen — so a local copy would
 * be a second place for a sentence about a customer-facing tax figure to drift.
 *
 * ⚠️ **`CONVERTED_INVOICE_LINE_TAX_RATE`, NOT `CONVERTED_LINE_TAX_RATE`.** The
 * quote-route sentence carries a "with none recorded, lines arrive at 0.00"
 * branch that is UNREACHABLE here: `COMPANY_HAS_NO_TAX_RATE` fires before the
 * transaction whenever the deal has lines and the company records no rate, so
 * an invoice conversion that produced a line is one whose company had a rate.
 * Rendering the quote sentence would send somebody looking for a rate to set on
 * lines that already carry one. See `deal-conversion.ts`, where the two are
 * held side by side so neither can be reworded without the other being read.
 */
import {
  INVOICE_CONVERSION_MOVES_THE_DEAL,
  CONVERTED_INVOICE_LINE_TAX_RATE,
  companyTaxRateRefusalOf,
  invoiceBaselineFromDeal,
} from '@/lib/deal-conversion';
/*
 * ⚠️ The deal→document line overrides live in ONE module, shared with
 * `QuoteFormModal`, and this form must not grow a second copy of any of it. The
 * discount pair in particular is resolved there and nowhere else: `discount`
 * and `discountAmount` are mutually exclusive columns, so a second place
 * deciding which of them a row means is a second place that can decide it
 * differently — on a document a customer is billed against.
 */
/*
 * 🔴 **THE `lines` HALF OF THIS MODULE IS NO LONGER IMPORTED HERE, AND THAT IS
 * THE WHOLE CHANGE.** *SUPERSEDED, the import list: `convertLineEditOf`,
 * `convertLineEditsMatch`, `convertLineFieldErrors`, `convertLineOverrides`,
 * `validateConvertLineEdit`, `ConvertLineEdit`, `ConvertLineField`,
 * `DealConvertLineOverride`.* Every one of those served the per-line `lines`
 * override this form used to build itself. It sends **`lineSet`** now — the
 * complete final list — which `InvoiceLineItemsTable` assembles in convert mode,
 * so the only pieces this file still needs are the request type and the refusal
 * router.
 *
 * ⚠️ **`convertLineSetFieldErrors` STAYS THIS FORM'S TO CALL**, and it cannot
 * move into the grid: the array index a 422 names is a position in the `lineSet`
 * that was SENT, and this form is what holds that array and the `rowKeys` emitted
 * beside it.
 */
import {
  convertLineSetFieldErrors,
  type DealConvertInput,
  type DealConvertLineSetEntry,
} from '@/lib/deal-convert-lines';
import {
  Button,
  Combobox,
  Field,
  Input,
  Modal,
  /*
   * ⚠️ `Select` is no longer imported — 2026-08-31. This form had exactly one,
   * the Tax treatment, and that is a derived read-only row now. `Combobox` stays:
   * Status, Company, Contact and Deal are all still chosen here.
   *
   * ⚠️ **`Skeleton`, the seven table primitives and `Textarea` went with the
   * hand-built deal-convert table on 2026-08-31.** That table is
   * `InvoiceLineItemsTable mode="convert"` now, which owns its own skeletons,
   * its own `<colgroup>` and its own description box. `inclusionLine` went with
   * it too — the grid holds the one copy of the singular/plural rule.
   */
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { billingGateRefusalOf } from '@/lib/company-approval';
import type {
  Deal,
  DealProduct,
  Invoice,
  InvoiceCreateInput,
  InvoiceProductCreateInput,
  InvoiceUpdateInput,
  ProjectCity,
  Quote,
  QuoteProduct,
  XeroDocumentLineAmountType,
  XeroLineAmountType,
} from '@/lib/types';

/**
 * What a caller may seed a NEW invoice with. See
 * {@link InvoiceFormModalProps.prefill}.
 *
 * Every field is optional and an absent one seeds nothing — an empty object is
 * a blank form, not a form full of blanks.
 */
export interface InvoiceFormPrefill {
  subject?: string;
  company?: { id: number; name: string };
  contact?: { id: number; name: string };
  deal?: { id: number; name: string };
  /** A three-letter ISO code. Uppercased on the way in, as the picker does. */
  currency?: string;
  /**
   * The planned basket the new invoice OPENS WITH — rows on the line grid,
   * visible and adjustable, before anything is created.
   *
   * ⚠️ **THIS IS THE ONE PREFILL KEY THAT DOES NOT LAND IN `values`.** Every
   * other key here is a header field and goes through {@link withPrefill}. A
   * line item is `POST /invoices/{id}/products` and **cannot exist before the
   * invoice does**, so this key seeds the DRAFT GRID instead and is written by
   * the existing two-phase create: invoice first, then each line, then a report
   * naming any that were refused. Nothing new happens on save.
   *
   * ⚠️ **PREFILLING IS NOT RAISING.** These are a seed and not a commitment —
   * the person still sees the modal, still edits the rows, and still presses
   * Save. Nothing on this path creates a document without that, which matters
   * more here than on a quote: an invoice, once created, cannot be un-created,
   * and this tenant's Xero does not enforce invoice-number uniqueness.
   *
   * ⚠️ **`PlannedProductLine` is imported from `@/lib/api`, deliberately, and
   * is not restated here.** `plannedProductLines()` already decides whether a
   * planned decimal quantity can be a line quantity; a look-alike type in this
   * file would be a second home for that decision.
   *
   * ⚠️ **A row whose `lineQuantity` is `null` opens with an EMPTY quantity box
   * and BLOCKS SAVE**, naming the planned figure. It is not rounded to 1 and it
   * is not quietly omitted — an omitted `quantity` is 1 on the server, which is
   * the same wrong number arriving by a longer route. See
   * `InvoiceLineItemsTable`'s `seededRowOf`.
   *
   * ⚠️ **It is part of the REMOUNT KEY** — see {@link prefillKeyOf}. Two planned
   * rows for the same company produce the same key on every other field, so
   * without it the second raise would open carrying the first plan's basket.
   *
   * 🔴 **AND IT IS THE ONLY WAY A PROJECT CAN TRAVEL INTO A NEW INVOICE —
   * 2026-09-11.** `PlannedProductLine.project` seeds the LINE's project picker.
   * Read it with the note below, which is about the HEADER and is unchanged.
   */
  lines?: readonly PlannedProductLine[];
  /*
   * ⚠️ **There is no `project` key here, and there cannot be one.** This form
   * holds no `projectId` — see `FormValues` and `ID_KEYS` — so a project seed
   * would have nowhere to land. `QuoteFormModal`'s prefill says the same thing
   * in the same words. A caller that still passes one is seeding a field that no
   * longer exists, and the type is what tells it so at compile time rather than
   * letting the value be accepted and silently dropped.
   *
   * 🔴 **READ THIS AS "NO HEADER PROJECT", NOT AS "NO PROJECT" — narrowed
   * 2026-09-11, because the wider reading became false and this is exactly the
   * sentence shape that rots.** A project CAN reach a new invoice now: it goes
   * on a LINE, through `lines` above, which is the model
   * `f_invoice_products.project_id` actually has. What stays true, and is the
   * whole content of this note, is that **the HEADER takes none** — that
   * removal is an explicit owner decision, `projectId` is structurally absent
   * from `FormValues` so no payload can express it, and an explicit `null`
   * there CLEARS a live document's project with a `200`. ⛔ **Do not "close the
   * gap" by adding a header key.**
   */
}

export interface InvoiceFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  invoice?: Invoice;
  /**
   * Pre-selects and locks the company when the form is opened from a company
   * record — `/invoices?companyId=N&new=1`.
   *
   * The name is carried alongside the id on purpose: the picker is not rendered
   * in this mode, so there is nothing to look a name up with, and the caller is
   * the only side that knows one.
   */
  lockedCompany?: { id: number; name: string };
  /**
   * Values a NEW invoice STARTS from — a seed, and emphatically not a lock.
   *
   * ⚠️ **This is not `lockedCompany` with a wider surface, and it must never
   * grow into one.** `lockedCompany` replaces the company Combobox with a
   * read-only Input because the company is the *reason* that form was opened.
   * An invoice raised from a deal is the opposite case: the person is at the
   * moment of deciding, and the thing they are most likely to change is
   * precisely the thing this filled in — a different contact at the same
   * company, a currency the deal was priced in a year ago. So every key here
   * lands in `values` and in nothing else. Every control renders exactly as it
   * would on a blank form, and every one of them is editable before Save.
   *
   * ⚠️ **CREATE ONLY.** It is applied in the `useState` initialiser and never
   * again, and only when there is no `invoice`. Seeding an edit would overwrite
   * a stored value with a caller's guess; re-applying it later would fight the
   * user's own typing.
   *
   * The three lookups carry their NAMES as well as their ids because they are
   * searched pickers — the id alone selects nothing until a page of the
   * directory happens to contain it, so the name is pinned into the option list
   * the same way a record's own link is.
   */
  prefill?: InvoiceFormPrefill;
  /**
   * The quote this form is CONVERTING — which makes Save a conversion rather
   * than a create, and is a different thing again from `prefill`.
   *
   * ⚠️ **Save calls `POST /quotes/{id}/convert`, not `POST /invoices`.** That is
   * the whole reason this is its own prop rather than a fatter prefill: convert
   * copies every line item, with its project, its cities and its campaign
   * inclusions, in ONE server-side transaction. A client-side line copy would be
   * one request per line and the entry it is most likely to drop is the
   * inclusion set — whose read and write shapes differ under the same key — so
   * the failure is a document that looks complete and bills for less than the
   * customer agreed to.
   *
   * ⚠️ **Convert's body schema is `z.object({}).strict()`: it accepts no
   * overrides at all.** So the header fields on this form cannot ride along with
   * the conversion. Whatever the person changed here is sent as a SECOND write,
   * `PATCH /invoices/{newId}`, bounded by `CONVERT_FOLLOW_UP_PATCH_KEYS` — which
   * means the invoice is real before those edits land, and a failure between the
   * two is reported as exactly that rather than as "nothing was saved".
   *
   * ⚠️ **CREATE ONLY, and it never combines with `invoice`.** Ignored outright
   * when an `invoice` is given; the header seeds come from
   * `invoicePrefillFromQuote`, which mirrors the server's own copy field for
   * field, and the company-derived seeds are switched off so a company default
   * cannot overwrite what the quote actually holds.
   */
  convertFrom?: Quote;
  /**
   * The DEAL this form is converting — `POST /deals/{id}/invoices`.
   *
   * ⚠️ **A SECOND, SEPARATE convert mode, and it is its own prop rather than a
   * union with {@link InvoiceFormModalProps.convertFrom} on purpose.** The two
   * differ in every way that matters at a call site: a different endpoint, a
   * different source record type, a different baseline builder, a different
   * refusal (`COMPANY_HAS_NO_TAX_RATE`, which quote→invoice cannot raise), a
   * different repeatability (this one answers **no 409** and a deal may be
   * invoiced many times, where a second quote convert is refused), and — since
   * 2026-08-30 — **editable line figures**, which the quote path has none of.
   * A union prop would make every one of those a runtime narrowing inside a
   * form that is already among the largest in this app; two props make the
   * wrong combination a `tsc` error and passing both at once impossible.
   *
   * ⚠️ **Save calls `POST /deals/{id}/invoices`, not `POST /invoices`.** That
   * route copies each line's project, city SET, campaign inclusions and region
   * set in ONE server-side transaction, freezes the RESOLVED account lead into
   * the override column, seeds each line's `taxRate` from the company, dates the
   * invoice today with `dueDate = billDate + 30`, and recomputes all four header
   * totals from the copied lines. A create-plus-`draftLines` flow would lose
   * several of those **and would leave a real invoice behind on a failed line**,
   * so it is not an alternative implementation of the same thing.
   *
   * ⚠️ **It DOES take overrides**, unlike the quote convert: the optional
   * `lines` key, per-line and keyed by `dealLineId`. The header edits still
   * cannot ride along, so they follow as `PATCH /invoices/{newId}` bounded by
   * `CONVERT_FOLLOW_UP_PATCH_KEYS` — two requests, no transaction across them.
   *
   * ⚠️ **CREATE ONLY, and it never combines with `invoice`.** Ignored outright
   * when an `invoice` is given; the header seeds come from
   * `invoiceBaselineFromDeal`, which mirrors the server's own copy field for
   * field.
   */
  convertFromDeal?: Deal;
  onSaved: (invoice: Invoice) => void;
  /**
   * **The LINE GRID saved on its own; whatever this modal was opened from now
   * holds a stale copy of this invoice.**
   *
   * ⚠️ **IT IS NOT COVERED BY {@link InvoiceFormModalProps.onSaved}, WHICH IS
   * THE WHOLE REASON IT EXISTS.** The grid renders its own "Save N rows", and
   * a run started from THAT button reports nothing up this form: `created`
   * stays null on an edit, so `finish()` falls through to `onClose`, and the
   * list behind renders the value it fetched before the modal opened. Nothing
   * errors, and the trap is that after such a save the footer Save is
   * correctly DISABLED and the secondary button reads "Cancel" — so the only
   * way out is also the word that makes leaving feel safe.
   *
   * What goes stale is anything the server derives from the LINES rather than
   * from the invoice row: `lineProjectStatus` on the list response is computed
   * live from the line items, so a project changed on a line moves it.
   *
   * ⚠️ **IT MAY FIRE WHILE THIS MODAL IS STILL OPEN, and that is correct
   * rather than premature.** The list genuinely is stale at that instant. A
   * caller should re-read; it must NOT close anything, navigate, or treat this
   * as a completed save — {@link InvoiceFormModalProps.onSaved} remains the
   * only signal that means "this form is finished with the record".
   *
   * ⚠️ **`onSaved` was deliberately NOT reused for this.** It has eleven mount
   * points across the app and several of them NAVIGATE, so firing it from a
   * grid-only save would take somebody away mid-edit.
   *
   * ⚠️ **It fires WHERE THE WRITE HAPPENS, not where the modal closes.** One
   * call site that cannot be missed, rather than four exit paths — Cancel, the
   * X, Escape, the overlay — of which one always gets forgotten.
   */
  onLinesSaved?: () => void;
  /**
   * Hands the caller the SERVER'S report of what an EDIT save did to Xero.
   * Added 2026-08-30.
   *
   * ⚠️ **It does not cause a push and it cannot withhold one.** `PATCH
   * /invoices/{id}` goes to Xero on the server, inside the save, whether or not
   * this prop is passed — so omitting it buys silence, never safety. What it
   * decides is only whether the person is told what happened, which is why it
   * is on the two callers that have somewhere to render that and absent from
   * the ones that do not.
   *
   * ⚠️ **A `failed` report is NOT a failed save**, and this is the endpoint
   * where that matters most: Xero does not enforce invoice-number uniqueness,
   * so a person who reads a refusal as "my save was lost" and saves again is
   * the path to two live demands for money against one customer. Whoever
   * renders this must lead with the invoice having been saved. `InvoiceDetailPage`
   * does, on every branch of `pushNoticeOf`.
   *
   * ⚠️ **Never on a CREATE.** A `POST` answers a skip for an invoice that is
   * not in Xero yet, so there would be nothing to report; the create's own
   * deliberate first push has its own report band.
   *
   * `undefined` is what a server predating `xeroPush`, or a rollback, sends,
   * and it is an ordinary answer. `autoPushOutcomeOf` in `@/lib/xero` decides
   * what that means, once.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  /**
   * Whether the invoice already has line items — or `'unknown'` when the caller
   * cannot tell.
   *
   * The invoice itself does not say, so the caller has to. It flips the totals
   * section from four inputs to a read-only note, because on an invoice with
   * lines the lines own the totals and anything typed here is overwritten by
   * the next line write. Defaults to false, which is the right answer for a
   * create and for the seven legacy imports that carry no lines at all.
   *
   * `'unknown'` is treated exactly like true, because an unknown line count has
   * to fail safe: offering the inputs when the lines might own the totals is
   * the destructive direction — a header PATCH writes what it is sent and the
   * next line write silently replaces it — while refusing them merely sends the
   * user to the detail page, which knows the count and offers them there. The
   * invoice list needs this: /invoices returns no line count.
   */
  hasLineItems?: boolean | 'unknown';
}

/**
 * Server-enforced maximums, mirrored so the control stops where the API would.
 *
 * ⚠️ **No `xeroInvoiceNumber` and no `xeroInvoiceId`, and neither may come
 * back.** Both are DISPLAYED by this form and written by neither: the number is
 * Xero's, stamped onto the record by a successful push, and the GUID lives on
 * the link envelope. Neither key exists in `FormValues` at all, so a limit for
 * them would be a rule with no control to enforce it against — and a control
 * would be a second writer on a column Xero owns.
 */
const MAX_LENGTHS = {
  subject: 255,
  /** The CUSTOMER's PO reference. `VARCHAR(255)`. */
  reference: 255,
  /** Customer-facing wording. `TEXT`, and genuinely 16,000 — not a typo. */
  terms: 16000,
} as const;

/**
 * The keys `validate()` length-checks.
 *
 * Not simply `TEXT_KEYS`: `xeroBrandingThemeId` is a `CHAR(36)` GUID that only
 * ever arrives from a picker, so a character-count message for it would be
 * unreachable noise, and the server checks GUID *shape* rather than length.
 */
const LENGTH_LIMITED = ['reference', 'terms'] as const;

/**
 * How long a new invoice has to be paid, counted in CALENDAR DAYS from its bill
 * date — so the due date lands on bill date + 30 whatever the month lengths and
 * leap years in between. `addDaysIso` does that arithmetic; nothing here adds
 * `30 * 86_400_000` to anything.
 *
 * ⚠️ **A default, and only that.** It is not the customer's payment terms —
 * those are the wording in the Terms box, which no field on this form parses —
 * and it is not read from the company, which stores no term. Somebody who knows
 * the real terms changes the due date, and from that moment the field stops
 * following the bill date at all.
 */
const DEFAULT_PAYMENT_TERM_DAYS = 30;

/**
 * The status a new invoice starts on, matched by NAME against the loaded master.
 *
 * ⚠️ **Never an id.** `f_invoice_statuses` is an admin-managed reference table
 * whose ids are per-environment; a literal `1` here would silently mean "Draft"
 * on one database and something else on the next. It is matched through
 * `normaliseStatusName`, the same case-folding and whitespace-collapsing
 * comparison `invoice-utils` uses everywhere else, and matched EXACTLY rather
 * than by containment — "Not a Draft" must never satisfy it.
 *
 * ⚠️ **No row, no status.** If the list holds no active Draft the field is left
 * unset, which is a real and savable state. Guessing at the first row instead
 * would put an invoice into whatever an admin happened to sort first.
 */
const DRAFT_STATUS_NAME = 'draft';

/*
 * ⚠️ `DOCUMENT_LINE_AMOUNT_OPTIONS`, `toDocumentLineAmountType`,
 * `USE_COMPANY_DEFAULT`, `AMOUNT_PATTERN`, `CURRENCY_PATTERN` and the four
 * `TOTAL_*` constants live in `@/components/documents/document-form`, shared
 * with `QuoteFormModal` — a local copy drifted once already and left one tax
 * treatment worded two ways across a quote and the invoice it converts into.
 * Do not re-declare any of them locally.
 */

/** Every value is a string, so the edit baseline can be diffed key by key. */
interface FormValues {
  subject: string;
  /*
   * ⚠️ There is deliberately no `xeroInvoiceNumber` and no `xeroInvoiceId`
   * here, and the freeze on the two is STRUCTURAL rather than a `readOnly`
   * attribute on an input. Because neither key exists, `changedKeys` cannot
   * reach them and no payload this form builds can carry them — see the note in
   * `MAX_LENGTHS`. What the section below renders is the RECORD's value, not a
   * form value, which is also why the number appears after a push without
   * anything here having to know a push happened.
   */
  statusId: string;
  contactId: string;
  companyId: string;
  dealId: string;
  /*
   * ⚠️ **There is deliberately no `ownerId` key here either, and its absence is
   * the same STRUCTURAL guard as `projectId`'s below.**
   *
   * The Owner picker was replaced by a read-only ACCOUNT LEAD row on the
   * owner's instruction — the person a document is "pulled from the company"
   * for is the company's account lead, which the server resolves on every read
   * (the invoice's own override where it has one, otherwise
   * `company_id -> d_companies.account_lead_id`). **That row is read-only
   * because the owner retired lead-assignment on this document**, so there is
   * nothing to seed and nothing to send, and no key for it exists here.
   *
   * ⚠️ **CORRECTION (2026-08-30): this said the value is "read-only on an
   * invoice: `accountLeadId` in a write body is a 422". That is FALSE.**
   * Migration 082 added `d_invoices.account_lead_id` and the key sits on the
   * write schema as `optionalUserId()` (`invoices.schema.ts:200`, verified from
   * backend source); `null` there clears the override rather than blanking the
   * field. It is a 422 only on a DELIVERABLE. The sentence was true when
   * written, was falsified by the migration, and nothing failed — so the
   * behaviour below is right and its stated reason was not. Keep the key off
   * `FormValues` regardless; that guard earns its place whatever the server
   * accepts, and an override would be a frontend build, not a backend gap.
   *
   * ⚠️ **Removing the CONTROL is not removing the WRITE, which is why the key
   * had to go too.** Left in `FormValues` with no control behind it, `ownerId`
   * would sit at `''` while the record held an owner, `changedKeys` would see
   * the difference the first time anything else was edited, `ID_KEYS` would
   * turn `''` into an explicit `null` — and an explicit `null` CLEARS the
   * column. An invoice would silently lose its owner the first time somebody
   * corrected its subject: a 200, no error, nothing to restore from. Same
   * failure, same fix, as the header project.
   *
   * `invoice.owner` is still READ — the record keeps whatever owner it has, and
   * this form simply cannot express a write to it.
   */
  /*
   * ⚠️ **There is deliberately no `projectId` key here.** The header project
   * picker was removed on the owner's instruction — an invoice is not connected
   * to a project as a whole; each LINE ITEM is, exactly as on a quote. The
   * header column still exists on the API and is still READ (`invoice.project`
   * is rendered below, read-only, and is what the two Xero tracking hints derive
   * from); this form simply cannot express a write to it.
   *
   * The absence of the key IS the guard, and it has to be, because the failure
   * it prevents is silent: a `projectId` left in the payload builder with no
   * control behind it emits `''`/`null` on every edit, an explicit `null`
   * CLEARS the column, and the invoice loses its project — and the `cities` it
   * derives through that project — the first time somebody edits its subject. A
   * 200, no error, and nothing to restore from. An `if` or a
   * `delete payload.projectId` is a guard a later edit can drop; a missing key
   * makes `changedKeys` (which walks `Object.keys(baseline)`) unable to reach
   * it, makes every `buildPayload` branch unable to name it, and makes `tsc`
   * refuse any call site that tries. Same structural shape as the two Xero
   * identity columns above, and as `QuoteFormModal` and `DealFormModal`.
   */
  billDate: string;
  dueDate: string;
  currency: string;
  /**
   * The exchange rate this invoice is PINNED to, as an exact decimal string.
   *
   * ⚠️ **`''` is not a rate of 1.** It is the absence of a pin, stored as
   * `null`, and it means Xero applies its own daily rate when the document is
   * pushed. Defaulting an empty box to `1` would assert that the document
   * currency and the organisation's base currency are at parity — a claim
   * nobody made, on a document a customer is billed against — so nothing in
   * this form ever substitutes a figure for a blank. Same family of mistake as
   * `''` on `xeroLineAmountType` meaning "no tax" rather than "use the
   * company's default".
   *
   * ⚠️ **And it is not a display preference.** The Xero push sends this column
   * UNCONVERTED as `Invoice.CurrencyRate`, so whatever is typed here lands on a
   * real document in SEED's accounting system.
   *
   * A string end to end, like every other money value here: `DECIMAL(18,6)`
   * server-side, and `Number()` on it is a float round-trip. It is only ever
   * parsed inside `@/lib/currency-conversion`.
   */
  currencyRate: string;
  subTotal: string;
  discountTotal: string;
  taxTotal: string;
  grandTotal: string;
  /*
   * The Xero document fields. Every one of them is `'' → null` on the wire, but
   * `null` is a MEANINGFUL value on three of them rather than an absence — see
   * the section that renders them, and `types.ts` for what each null asserts.
   */
  terms: string;
  reference: string;
  xeroLineAmountType: string;
  xeroBrandingThemeId: string;
}

/**
 * Keys that carry a foreign key id.
 *
 * ⚠️ **`projectId` is NOT in this list, and its absence is load-bearing.** This
 * loop is the ONLY thing that turns an id key into a payload entry — `null` on
 * the PATCH branch, `Number(next)` on the create branch — so a `projectId` here
 * would be a write on every edit that touched anything, and on this resource an
 * explicit `null` CLEARS the column. It is absent from `FormValues` as well, so
 * the list could not name it even if somebody typed it: `(typeof ID_KEYS)[number]`
 * is indexed straight into `values`, and `tsc` refuses a key that is not there.
 * See the note in `FormValues`.
 *
 * ⚠️ There is deliberately no city key here either, and there cannot be one:
 * **an invoice has no `cityIds`, and sending one is a 422 naming the key** —
 * every write schema on this resource is `.strict()`. Cities are written on a
 * LINE ITEM (`cityIds`, against that line's own project) and on the project
 * itself. The invoice's own `cities` is derived read-only through the stored
 * header project and is never sent.
 */
const ID_KEYS = ['statusId', 'contactId', 'companyId', 'dealId'] as const;

/**
 * Keys that are plain nullable text — `''` is sent as `null`.
 *
 * `xeroLineAmountType` is NOT here: it is a three-member enum and is narrowed
 * separately, so a stray string can never reach the payload typed as one.
 */
const TEXT_KEYS = ['terms', 'reference', 'xeroBrandingThemeId'] as const;

/*
 * 🔴 **`DOCUMENT_KEYS` is GONE — 2026-08-31**, with the "Xero tracking and
 * references" disclosure it existed to force open. *SUPERSEDED: "The keys that
 * live inside the collapsible Xero section, and only those … it must track the
 * section's WRITABLE contents exactly."* It held exactly
 * `xeroTrackingLocation` and `xeroTrackingTourType`, and both are off
 * `FormValues` now, so there is neither a section to open nor a key to open it
 * for. The TOTALS disclosure keeps its own equivalent — that section is still
 * here.
 */

/**
 * The co-validated date pair.
 *
 * `billDate > dueDate` is a 422 reported at path `dueDate`, but the server only
 * runs that check when BOTH dates are present in the same body. A PATCH that
 * moves only `billDate` past a stored `dueDate` therefore succeeds and leaves
 * the invoice inconsistent. So whenever either one changed, the payload carries
 * both — the same reason a deliverable sends its company and contact together,
 * because the server only re-checks the pair when it can see the pair.
 */
const DATE_KEYS = ['billDate', 'dueDate'] as const;

/**
 * The header project's cities on a CREATE — empty, permanently.
 *
 * An invoice raised from this form carries no header project (see `ID_KEYS`), so
 * there is no set to derive and `[]` is the true answer rather than a
 * placeholder. Hoisted to module scope so the identity is stable across renders:
 * a fresh `[]` in the JSX is a new prop value every time, which is a needless
 * invalidation in whatever the grid memoises on it. Copied from
 * `QuoteFormModal`, which reached the same state one change earlier.
 */
const NO_HEADER_CITIES: ProjectCity[] = [];

/**
 * What Save says when the line half of an edit did not go through.
 *
 * ⚠️ **NEITHER SENTENCE REPEATS WHAT THE GRID ALREADY SAID.** The grid renders
 * the refusal on the row it belongs to, in the server's own words; a second
 * account of it up here would be a paraphrase competing with the real one. What
 * these add is the part the grid cannot know — whether anything ELSE was
 * written — and where to look.
 *
 * ⚠️ **The second is the one that matters and the one that is easy to get
 * wrong.** "Nothing was saved" over a header PATCH that returned 200 is a lie a
 * person acts on: they re-enter details that are already stored, or abandon a
 * record believing it untouched. The house rule for the two-phase create is the
 * same rule — say which half landed.
 *
 * ⚠️ **On an INVOICE the cost of getting it wrong is higher than on a quote.**
 * This is a document that demands money and the header PATCH has already been
 * pushed to Xero by the server; somebody who reads "nothing was saved" and
 * raises the invoice again ends up with two live demands against one customer,
 * which is the failure `onXeroPush`'s own contract is written around.
 */
const LINES_NOT_SAVED = 'The line items were not saved — see the report on the rows above.';
const HEADER_SAVED_LINES_NOT =
  'The invoice’s details were saved. Its line items were not — see the report on the rows above.';

function toFormValues(invoice?: Invoice): FormValues {
  return {
    subject: invoice?.subject ?? '',
    /*
     * ⚠️ No `xeroInvoiceNumber` or `xeroInvoiceId` baseline. Seeding either
     * would put the stored value back into `values`, and from there into
     * `changedKeys` the moment anything touched it — which is exactly the
     * second write on a Xero-owned column that removing the two inputs exists
     * to prevent. Both are read straight off the record where they are
     * displayed, and never mirrored into form state.
     */
    statusId: invoice?.status ? String(invoice.status.id) : '',
    contactId: invoice?.contact ? String(invoice.contact.id) : '',
    companyId: invoice?.company ? String(invoice.company.id) : '',
    dealId: invoice?.deal ? String(invoice.deal.id) : '',
    /*
     * ⚠️ No `ownerId` baseline, and no `accountLeadId` one either. Seeding the
     * owner would put the stored id back into `values`, and from there into
     * `changedKeys` — the clearing write that removing the picker exists to
     * prevent. The account lead has no baseline for the same reason
     * `xeroInvoiceNumber` has none: this form does not write it, so it is read
     * off the record where it is displayed and never mirrored into form state.
     * `KNOWN_WRITE_KEYS` is derived from this object, so both are also kept off
     * `fieldErrors`.
     *
     * (This previously justified the account lead with "`accountLeadId` is a
     * 422". ⚠️ FALSE since migration 082 — the key is on the invoice write
     * schema; this form declines to send it on the owner's instruction. See
     * `FormValues` for the correction.)
     */
    /*
     * ⚠️ No `projectId` baseline. Seeding one here would put the stored project
     * back into `values`, and from there into `changedKeys` the moment anything
     * touched it — which is exactly the clearing write that removing the picker
     * exists to prevent. The stored project is read straight off
     * `invoice.project` where it is displayed, and never mirrored into form
     * state; `KNOWN_WRITE_KEYS` is derived from this object, so leaving it out
     * here also keeps a 422 naming `projectId` off a control that is not there.
     */
    billDate: invoice?.billDate ?? '',
    dueDate: invoice?.dueDate ?? '',
    currency: invoice?.currency ?? '',
    /*
     * `null` becomes '' for the same reason every other nullable does — the
     * control holds a string — and here the two genuinely mean the same thing:
     * no rate is pinned. It must never become '1'.
     */
    currencyRate: invoice?.currencyRate ?? '',
    subTotal: invoice?.subTotal ?? '',
    discountTotal: invoice?.discountTotal ?? '',
    taxTotal: invoice?.taxTotal ?? '',
    grandTotal: invoice?.grandTotal ?? '',
    terms: invoice?.terms ?? '',
    reference: invoice?.reference ?? '',
    /*
     * 🔴 **A NEW INVOICE NO LONGER STARTS AT `EXCLUSIVE` — 2026-08-31.**
     * *SUPERSEDED: `invoice ? (invoice.xeroLineAmountType ?? '') : 'EXCLUSIVE'`,
     * "A NEW invoice starts at `EXCLUSIVE` … Same ternary, same reason, as
     * `QuoteFormModal`."*
     *
     * The tax treatment is DERIVED FROM THE COMPANY and read-only on this form
     * now (owner's decision), so a hardcoded constant sitting in the field is a
     * value the row would present as the company's when it is nothing of the
     * kind. Two ways that bites, both silent:
     *
     * - the row reads "Tax exclusive" for the length of the company read, under
     *   a label whose whole claim is that the value came from the company;
     * - somebody who saves inside that window ships `EXCLUSIVE` on the wire — a
     *   VAT treatment nobody chose, on a document a customer is invoiced
     *   against. `''` omits the key instead, and the server falls back to the
     *   company's own Xero sales default, which is the source the owner asked
     *   for. A company recording none is refused LOUDLY at push time by
     *   `XERO_NO_LINE_AMOUNT_TYPE`, which is the right direction: a blank
     *   default is a loud refusal, a guessed one is a successful push carrying
     *   the wrong tax.
     *
     * ⚠️ **`QuoteFormModal` STILL STARTS AT `EXCLUSIVE` and must not be
     * "brought into line".** Its tax control is still a Select somebody can
     * change, and a flat starting point a reader can correct is a different
     * thing from a flat value a reader cannot. The two forms diverge here
     * because the CONTROLS diverge, not because one of them was missed.
     *
     * The EDIT half is untouched: an existing invoice keeps whatever it stores,
     * and a stored `null` still means "use the company's Xero sales default"
     * and still renders as `''`. Re-deriving it would rewrite a tax treatment
     * already printed on a customer's PDF.
     */
    xeroLineAmountType: invoice?.xeroLineAmountType ?? '',
    xeroBrandingThemeId: invoice?.xeroBrandingThemeId ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS.** A 422's `path` is a request key,
 * and on this resource every one of them is also a `FormValues` key — so the
 * list is derived from `toFormValues()` and cannot drift from the controls.
 * There is deliberately no `cityIds` in it: an invoice has no city field (they
 * are derived through the project) and every write schema here is `.strict()`,
 * so such a path could only ever be something this form cannot mark.
 *
 * ⚠️ **And no `projectId`, which is the half that is easy to leave behind.**
 * It falls out for free — the list is `Object.keys(toFormValues())` and the key
 * is gone from there — but the consequence is worth stating: a 422 naming
 * `projectId` (from a server that still validates the key, or a payload this
 * form did not build) now goes to the form-level banner, where a reader can see
 * it, instead of being marked against a control that no longer exists. That is
 * the "one field needs attention, highlighted below" lie with nothing
 * highlighted, and the derivation is what makes it impossible here.
 *
 * Anything outside the list goes to the form-level banner, because a path can
 * be non-empty and still bind to nothing — the worse case, since it looks
 * bindable: it is routed to an input that does not exist, vanishes from the
 * banner, and still inflates the "fields need attention" count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

/**
 * This app's own refusal for the company on a NEW invoice — `null` when there
 * is nothing to refuse.
 *
 * Owner's rule, 2026-08-31: **a new invoice requires a company.** Create-time
 * only; see the call site in `validate`, which also excludes BOTH convert paths.
 *
 * ⚠️ **THE REQUIRED-COMPANY HALF REALLY IS OURS, and it is all that is left
 * here.** `companyId` is genuinely nullable server-side, and the approval guard
 * SKIPS a document that names no company at all — so nothing on the API refuses
 * a company-less invoice and this sentence is the only thing that does.
 *
 * ⚠️ **THE APPROVAL HALF WAS NEVER OURS AND HAS BEEN REMOVED — corrected
 * 2026-08-31.** *SUPERSEDED, and it was written into this comment as a fact:
 * "`POST /invoices` … accepts one against a company in any approval state".* It
 * does not. `assertCompanyApproved` (migration 099) is called from
 * `createInvoice` and throws a **409** carrying one of three codes and a
 * sentence strictly better than the two invented here — it NAMES the company,
 * says an approver has been emailed a link, and says everything else on the
 * company still works. See `billingGateRefusalOf` in `@/lib/company-approval`,
 * which is where that refusal is now read, and `reportFailure`, which banners it.
 *
 * > A client rule and a server rule wording the same refusal is two sentences
 * > about one fact, and the client's is the one that goes stale — silently,
 * > because it renders perfectly either way.
 */
function newInvoiceCompanyError(companyId: string): string | null {
  if (companyId.trim() === '') {
    return 'Choose a company — a new invoice has to be raised against one.';
  }
  return null;
}

/**
 * The Combobox footnote reporting companies withheld from the list because they
 * are not approved — `undefined` when none were.
 *
 * ⚠️ It exists because a company somebody can see on `/companies` and cannot
 * find in this dropdown reads as a broken fetch, and they go hunting. A narrowed
 * list that says nothing is the silent failure this codebase already records
 * against retired picker options.
 *
 * ⚠️ It says the company is not APPROVED, never that it does not exist. The
 * wording covers `pending` and `rejected` alike without claiming which, because
 * the list cannot say which without naming the company — and the count is the
 * part that stops the hunt.
 */
function withheldCompanyNote(withheld: number): string | undefined {
  if (withheld <= 0) return undefined;
  return withheld === 1
    ? '1 company is not listed — an invoice can only be raised against an approved company.'
    : `${withheld} companies are not listed — an invoice can only be raised against an approved company.`;
}

/**
 * What actually happened when the draft lines were written after a create.
 *
 * ⚠️ **This type exists because the two halves of one Save are two different
 * outcomes, and there is no transaction across them.** `POST /invoices` and
 * each `POST /invoices/{id}/products` are separate requests; the invoice can be
 * created and a line still be refused. Nothing here rolls anything back,
 * because nothing here CAN — deleting an invoice to undo a failed line would
 * destroy the work that succeeded, and describing it as "rolled back" when it
 * was not is the failure the owner explicitly ruled out.
 *
 * So the outcome is recorded per line and reported by position, and the form
 * stops offering to create an invoice the moment one exists.
 */
interface DraftLineOutcome {
  /** How many lines were sent. Never zero — no outcome is recorded otherwise. */
  attempted: number;
  /** How many came back 2xx. */
  saved: number;
  /**
   * The ones that did not, by their **1-based position in the grid as it was
   * submitted**.
   *
   * Position rather than id, because a line that was refused HAS no id — and
   * position rather than product name, because this form holds no product
   * directory and inventing one request to label an error message would be a
   * second thing to go wrong.
   */
  failures: { position: number; message: string }[];
  /**
   * The SERVER's own account of what it did with Xero on the **last line write
   * that landed**, or `undefined` where none did.
   *
   * ⚠️ **The last one, deliberately, and not the first or the create's.** Every
   * line write pushes on the server, so the earlier reports describe a document
   * that was still being assembled — a two-line invoice would report on the
   * one-line version. Only the final write saw the whole thing.
   *
   * `undefined` is ordinary: a server predating `xeroPush`, or a create with no
   * lines. The caller falls back to the create response's own report.
   */
  xeroPush?: XeroPushReport | undefined;
}

/**
 * Why one line was refused, in a sentence a person can act on.
 *
 * ⚠️ **Not `error.message`.** On a 422 that string is only the generic
 * "Validation failed" — the sentence naming the key that was actually refused
 * lives in `details`, and on an invoice line that is where every useful refusal
 * is: the `cityIds` sentences, the `projectId` one, the strict-schema
 * rejections. A report reading "Line 3 was not created: Validation failed"
 * would be worse than the raw error.
 *
 * Everything is form-level here **by construction**: this form renders no
 * control for any line key, so there is nothing on screen for a path to bind
 * to. The key is prefixed exactly as `ApiError.routeFieldErrors` does, and
 * skipped when the message already names it.
 */
function lineFailureMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return 'The server could not be reached, so this line was not sent.';
  }
  const parts = [...caught.formMessages];
  for (const [key, message] of Object.entries(caught.fieldErrors)) {
    parts.push(message.includes(key) ? message : `${key}: ${message}`);
  }
  return parts.length > 0 ? parts.join(' ') : caught.message;
}

/**
 * `All 1 line item was saved` is what a count-first sentence reads like at one,
 * so one and two get their own wording rather than a number and a plural glued
 * together. Never called with 0 — no outcome is recorded when nothing was sent.
 */
function savedLinesSentence(attempted: number): string {
  if (attempted === 1) return 'Its line item was saved with it.';
  if (attempted === 2) return 'Both line items were saved with it.';
  return `All ${attempted} line items were saved with it.`;
}

/**
 * ── PHASE THREE: where the created invoice stands with Xero ────────────
 *
 * ⚠️ **None of these is a state of the SAVE.** By the time any of them can be
 * reached the invoice exists and so do whatever line items landed, and nothing
 * below may be worded — or toned — as the create having failed. The green
 * "Invoice #N was created and is saved" banner is rendered ABOVE this in the
 * same band, and the line-failure report beside it, deliberately: a create can
 * refuse a line AND miss the push, and a reader needs both facts rather than
 * whichever one happened last.
 *
 * ⚠️ **CORRECTED 2026-08-31 — THIS FORM NO LONGER PUSHES, AND THERE IS NO
 * LONGER ANYTHING TO RETRY.** Two members are gone with the client push:
 *
 * > `| { kind: 'sending' }` — *"Sending to Xero… The invoice is saved either
 * > way."* There is no window to report: the server pushes inside the save and
 * > the answer arrives on the same response.
 *
 * > `retryable: boolean` — *"Whether to offer a person-pressed send, and
 * > nothing else … one button, pressed once, by somebody reading the refusal."*
 * > That button repeated a call this form no longer makes. The recovery is
 * > unchanged and is one screen away: "Send to Xero now" on the invoice's own
 * > page, which can only ever UPDATE.
 *
 * What is kept is the REPORTING, re-pointed at the server's `xeroPush` — the
 * whole reason that field exists is that a push which fails silently is the
 * failure worth preventing.
 */
type XeroCreatePush = { kind: 'idle' } | { kind: 'settled'; report: XeroRowPushReport };

/**
 * Turn the SERVER's own account of a save's Xero half into the band this form
 * renders — or `null` for "say nothing".
 *
 * ⚠️ **CORRECTED 2026-08-31. THIS REPLACES `noLinesReport`, `cooldownReport`
 * AND `pushOutcome`**, all three of which described a push THIS FORM made. It
 * makes none now: the server pushes inside the save and answers `xeroPush`
 * beside `data`. The three deleted helpers said, in part:
 *
 * > *"The report for a push that was **not attempted**, because the invoice has
 * > no line items yet … Xero would refuse the document outright
 * > (`XERO_INVOICE_NO_LINES`), so pushing it would greet the most ordinary
 * > create in this form with a red failure."*
 *
 * > *"The report for a push that was **not attempted**, because Xero's shared
 * > call budget is already spent."*
 *
 * Both were client-side PRE-CHECKS standing in for an answer nobody had. The
 * server answers both now, authoritatively, as `skipped` — so the guessing goes
 * and the tone ruling they were written to protect is kept below.
 *
 * ⚠️ **EVERY BRANCH LEADS WITH THE INVOICE HAVING BEEN SAVED**, including the
 * failures, and that is the load-bearing rule rather than a courtesy: Xero does
 * not enforce invoice-number uniqueness, so a person who reads a refused push
 * as "my save was lost" and creates the invoice again has produced two live
 * demands for money against one customer, and nothing anywhere refuses them.
 * `rowPushReport`'s own headings do NOT lead with the save — they are written
 * for a list row, where nothing was just saved — so the heading is overridden
 * on every branch and only its LINES and pointer are taken.
 *
 * ⚠️ **`skipped` is `warning`, NEVER `danger`.** No lines yet, or a company
 * nobody has linked, is an ordinary draft and the commonest answer this form
 * will ever get: this form's own description says only a subject is required.
 * Rendering it red greets the most ordinary create in the system with a failure
 * about something the person has done nothing wrong in.
 *
 * `null` for an absent or unrecognised report, exactly as `autoPushOutcomeOf`
 * decides it: a server predating the contract, or a status this build has never
 * heard of, is not something to guess a tone for.
 */
function serverPushReport(
  recordName: string,
  companyName: string | null,
  report: XeroPushReport | undefined,
): XeroRowPushReport | null {
  const outcome = autoPushOutcomeOf(report);
  if (outcome === null) return null;

  if (outcome.kind === 'skipped') {
    return {
      tone: 'warning',
      heading: `“${recordName}” was saved, and Xero has not been sent it yet.`,
      /* The server's own sentence, stated as safe to show and never matched on. */
      lines: [outcome.reason],
      pointer: null,
    };
  }

  if (outcome.kind === 'updated') {
    return {
      tone: 'success',
      heading: `“${recordName}” was saved and sent to Xero.`,
      lines: [],
      pointer: null,
    };
  }

  /*
   * A refusal. The CLASSIFICATION and the sentences come from `@/lib/xero` —
   * the same pair the list rows and `InvoiceDetailPage` use — because a third
   * wording of one server code is exactly what that module was consolidated to
   * prevent. Only the heading is this form's, for the reason above.
   */
  const base = rowPushReport('invoices', recordName, outcome);

  /*
   * ⚠️ An unlinked company is the COMMON answer on a fresh dataset, not an edge
   * case, and the thing to fix is one record away and has a name. Kept from the
   * deleted `pushOutcome` unchanged apart from its source.
   */
  if (outcome.kind === 'refused' && outcome.code === 'XERO_COMPANY_NOT_LINKED') {
    const named = companyName !== null && companyName.trim() !== '';
    return {
      ...base,
      tone: 'warning',
      heading: `“${recordName}” was saved. ${named ? `“${companyName}”` : 'Its company'} is not linked to a Xero contact yet, so Xero has nobody to bill.`,
      pointer: `Link ${named ? companyName : 'the company'} to its Xero contact on the company record.`,
    };
  }

  return {
    ...base,
    heading: `“${recordName}” was saved, and Xero would not take it.`,
  };
}

export function InvoiceFormModal({
  open,
  onClose,
  invoice,
  lockedCompany,
  prefill,
  convertFrom,
  convertFromDeal,
  onSaved,
  onLinesSaved,
  onXeroPush,
  hasLineItems = false,
}: InvoiceFormModalProps) {
  if (!open) return null;
  return (
    /*
     * The key carries the locked company id but NOT its name. The id decides
     * what the form starts with, so a different one has to remount; the name is
     * only ever rendered, and may sharpen from a placeholder to the real thing
     * once the list holding it loads — remounting on that would throw away
     * whatever had already been typed.
     *
     * ⚠️ The PREFILL ids are in the key for the same reason, and it is the one
     * that bites: a deal page opens this to raise an invoice, the person
     * cancels, and the app navigates to another deal. Same mounted modal, new
     * seed — and without the key the second open would show the FIRST deal's
     * name, company and contact, which is a wrong document raised silently
     * rather than an obviously broken form. Ids only, never names.
     */
    <InvoiceForm
      /*
       * ⚠️ The converted QUOTE's id is in the key for exactly the reason the
       * prefill ids are: a list page opens this over quote 12, the person
       * cancels, and they press Convert on quote 13. Same mounted modal, new
       * source — and without the key the second open would carry quote 12's
       * subject, totals and line preview while Save converted quote 13.
       */
      /*
       * ⚠️ The converted DEAL's id is in the key for the same reason, and the
       * consequence is worse on this side: the deals LIST opens this modal over
       * deal 12, the person cancels, and they press + → Invoice on deal 13.
       * Without the key the second open would carry deal 12's subject, company,
       * currency and — since the figures became editable — deal 12's LINE
       * ROWS, while Save invoiced deal 13. `d:` prefixes it so a deal id and a
       * quote id of the same number cannot collide into one key.
       */
      key={`${invoice?.id ?? 'new'}:${lockedCompany?.id ?? ''}:${prefillKeyOf(prefill)}:${convertFrom?.id ?? ''}:d${convertFromDeal?.id ?? ''}`}
      onClose={onClose}
      invoice={invoice}
      lockedCompany={lockedCompany}
      prefill={prefill}
      convertFrom={convertFrom}
      convertFromDeal={convertFromDeal}
      onSaved={onSaved}
      onLinesSaved={onLinesSaved}
      onXeroPush={onXeroPush}
      hasLineItems={hasLineItems}
    />
  );
}

/** The prefill's identity for the remount key — see the note at the call site. */
function prefillKeyOf(prefill: InvoiceFormPrefill | undefined): string {
  if (!prefill) return '';
  /*
   * ⚠️ **THE LINES ARE IN THE KEY, AND WITHOUT THEM THE PLANNER IS BROKEN.**
   * Two planned rows for the same company agree on every other field here, so
   * the key would be identical and the second open would carry the FIRST plan's
   * basket — an invoice built from one plan and raised against another,
   * silently, for money.
   *
   * ⚠️ **Keyed on the CONTENT, never on the array's identity.** A parent maps
   * `plannedProductLines(row)` inline, so the array is a fresh object on every
   * render; keying on identity would remount the form on every keystroke and
   * throw away whatever had been typed.
   *
   * 🔴 **THE PLANNED AMOUNT JOINED THE FINGERPRINT ON 2026-09-03**, and the
   * quote twin took the identical change — read the longer note there.
   * *SUPERSEDED: "Product and quantity are the only two things a seed carries,
   * so they are the whole of the fingerprint."*
   *
   * ⚠️ A planned line can carry money now, so two plans selling the same
   * products in the same quantities at DIFFERENT AMOUNTS hashed the same and
   * the second open silently reused the first plan's prices — on an invoice,
   * which is the document that demands the money.
   *
   * 🔴 **AND THE PROJECT JOINED IT ON 2026-09-11** — the quote twin took the
   * identical change and carries the longer note. A seed may now name a
   * project, and a sales-tracker pursuit is a `(project, company)` pair, so
   * the same company chased on two projects is exactly the pair that agrees on
   * every other term here. **Any member added to `PlannedProductLine` that a
   * person can differ on belongs in this fingerprint**; this sentence has now
   * been outgrown twice.
   */
  const lines = (prefill.lines ?? [])
    .map(
      (line) =>
        `${line.productId}x${line.quantity}@${line.plannedAmount ?? ''}#${line.project?.id ?? ''}`,
    )
    .join(',');
  return `${prefill.deal?.id ?? ''}.${prefill.company?.id ?? ''}.${
    prefill.contact?.id ?? ''
  }.${lines}`;
}

/**
 * The seed, applied to a blank baseline. Create only — the one caller is the
 * `useState` initialiser, which is what keeps this from ever fighting the user.
 *
 * An absent key seeds nothing. `''` on `subject` is respected as a deliberate
 * blank rather than treated as absent, because the caller had to write it.
 */
function withPrefill(base: FormValues, prefill: InvoiceFormPrefill | undefined): FormValues {
  if (!prefill) return base;
  const next = { ...base };
  if (prefill.subject !== undefined) next.subject = prefill.subject;
  if (prefill.company) next.companyId = String(prefill.company.id);
  if (prefill.contact) next.contactId = String(prefill.contact.id);
  if (prefill.deal) next.dealId = String(prefill.deal.id);
  // No project line, and none may be added: `FormValues` has no `projectId`.
  /*
   * Uppercased because a lower-case code is a 422 on an invoice write — the
   * server does not fold case — and because a code stored in lower case on an
   * older record must not read as a different currency from the same code
   * derived off the company.
   *
   * ⚠️ *SUPERSEDED 2026-08-31: "Uppercased here as the currency picker
   * uppercases what a person types."* There is no currency picker; the currency
   * is derived from the company and read-only. **The uppercasing is NOT
   * redundant now — it is the only one left**, so do not remove it on the
   * grounds that its stated twin has gone. A prefilled currency is the one
   * value here the company derivation does not overwrite.
   */
  if (prefill.currency !== undefined) next.currency = prefill.currency.toUpperCase();
  return next;
}

/**
 * The two dates a NEW invoice starts on: TODAY, and thirty days after it.
 *
 * Applied in the `useState` initialiser, after {@link withPrefill}, and only on
 * a create — the same one-shot discipline every other seed on this form has.
 * Both stay fully editable, and the due date goes on following the bill date
 * until somebody edits it; that half lives on the controls, because it is a
 * response to an event rather than a starting value.
 *
 * ⚠️ **Only fills a BLANK.** A date already in `base` is the caller's or the
 * record's, and this never overwrites one — which is what keeps it safe to run
 * after the prefill rather than before it.
 *
 * ⚠️ **`todayIsoDate()` and `addDaysIso()`, never `new Date()` here.** These are
 * bare `YYYY-MM-DD` calendar days with no time and no zone. `new Date()` piped
 * through `toISOString()` is UTC, so from 00:00 local anywhere west of Greenwich
 * it names YESTERDAY — an invoice dated a day early, on a document a customer is
 * billed against and finance reconciles by date. The two helpers assemble and
 * shift the day from LOCAL calendar parts precisely so that cannot happen.
 */
function withDateDefaults(base: FormValues): FormValues {
  const next = { ...base };
  if (next.billDate.trim() === '') next.billDate = todayIsoDate();
  if (next.dueDate.trim() === '') {
    next.dueDate = addDaysIso(next.billDate, DEFAULT_PAYMENT_TERM_DAYS);
  }
  return next;
}

/**
 * A company's Xero sales line-amount default, translated into the DOCUMENT
 * member this invoice can actually hold — or `null` where the company records
 * none, which seeds nothing.
 *
 * ⚠️ **THE TWO ENUMS ARE NOT THE SAME ENUM, and the difference is one word.** A
 * company's `xeroSalesLineAmountType` is the CONTACT enum
 * (`INCLUSIVE | EXCLUSIVE | NONE`); a quote's or an invoice's is the DOCUMENT
 * enum (`INCLUSIVE | EXCLUSIVE | NOTAX`). `types.ts` is explicit that sending
 * `'NONE'` on an invoice is **a 422 naming the field**.
 *
 * ⚠️ **`NONE` IS THEREFORE NOT TRANSLATED HERE — IT SEEDS NOTHING.** An earlier
 * version of this function mapped it to `NOTAX`, which typechecks, passes the
 * form and reaches Xero looking correct. Two things are wrong with it.
 *
 * It is a SECOND implementation of a translation the server already owns
 * (`contactAmountTypeToDocument`), and the standing rule in `types.ts` and
 * `document-form.ts` is that the server translates on push and is the only
 * place that should. A copy in the client is the thing that drifts.
 *
 * And it FREEZES an answer that is supposed to follow. Blank on this column does
 * not mean "no tax" — it means "use the company's Xero sales default", resolved
 * at push time. Writing `NOTAX` onto the document instead pins today's answer,
 * so a company later corrected to `EXCLUSIVE` would have its quotes follow and
 * its invoices keep saying no tax. That divergence is invisible until an
 * accountant asks why one document disagrees with the other.
 *
 * `EXCLUSIVE` and `INCLUSIVE` still seed, and that is not a translation: they
 * are the SAME WORD in both enums, so this narrows a type rather than mapping a
 * value. `NONE` is the only member that would need mapping, and it is the only
 * one refused. This now matches `QuoteFormModal` exactly — which matters more
 * than either choice on its own, because the invoice raised against a quote must
 * not disagree with it.
 *
 * ⚠️ **NO COUNTRY RULE AND NO RATE.** There is deliberately no "UK ⇒ 20%"
 * anywhere in this file, and there is no percentage of any kind: a rate is a
 * per-LINE `taxRate` paired with a per-line `xeroTaxType`, and the header holds
 * neither. The company's own stored default is the single source, exactly as the
 * owner put it — where it records nothing, the field's existing blank stands,
 * and blank already means "fall back to the company's Xero sales default" rather
 * than "no tax".
 */
function documentLineAmountFromCompany(
  value: XeroLineAmountType | null,
): XeroDocumentLineAmountType | null {
  if (value === null || value === 'NONE') return null;
  return toDocumentLineAmountType(value);
}

/**
 * WHICH RECORD a derived read-only row took its value from.
 *
 * ⚠️ **The server keeps these two apart on the wire on purpose** — a company's
 * own `currency` / `xeroSalesTaxType` / `xeroSalesLineAmountType` /
 * `xeroBrandingThemeId` sit BESIDE `billingRegionDefaults`, never merged into
 * one field — precisely so a client can fall back AND still name the record.
 * They are two different screens to go and edit, and a value shown without its
 * source sends somebody to the wrong one.
 *
 * `'billing-region'` and not `'region'`: this app has THREE things called some
 * form of "region" — a company's SEED regions of focus, a country's upstream
 * geography id, and this commercial billing grouping — and the short word names
 * the wrong one in a codebase that has already been bitten by the confusion.
 */
type DerivedSource = 'company' | 'billing-region';

/** One source per derived row. `null` is "nothing derived this". */
interface DerivedSources {
  currency: DerivedSource | null;
  xeroLineAmountType: DerivedSource | null;
  xeroBrandingThemeId: DerivedSource | null;
}

/**
 * Before any company has been read, and the resting state on an EDIT.
 *
 * ⚠️ A module CONSTANT rather than an object literal in the `useState` call:
 * this is an identity that never changes, so a fresh literal per render would
 * make it impossible to compare cheaply later and would read as though the
 * initial value were meaningful state.
 */
const NO_DERIVED_SOURCES: DerivedSources = {
  currency: null,
  xeroLineAmountType: null,
  xeroBrandingThemeId: null,
};

/**
 * Where a derived row's value is really set — the `title` on the read-only box.
 *
 * ⚠️ **Every branch names a RECORD, never a vague "elsewhere".** The whole job
 * of this sentence is to answer "so where do I change it?", and only the two
 * named branches can. `null` falls back to the company record because that is
 * where a value is SET even when this form did not derive one — on an edit the
 * document carries its own, and pointing at the account is still the honest
 * answer to how it got there.
 */
function derivedSourceTitle(source: DerivedSource | null): string {
  if (source === 'billing-region') {
    return 'From the billing region, not the company — the company records none. Set on the billing-region defaults in the Modules Catalog.';
  }
  return 'From the company record. Set there, not here.';
}

function InvoiceForm({
  onClose,
  invoice: initialInvoice,
  lockedCompany,
  prefill,
  convertFrom,
  convertFromDeal,
  onSaved,
  onLinesSaved,
  onXeroPush,
  hasLineItems,
}: {
  onClose: () => void;
  invoice?: Invoice;
  lockedCompany?: { id: number; name: string };
  prefill?: InvoiceFormPrefill;
  convertFrom?: Quote;
  convertFromDeal?: Deal;
  onSaved: (invoice: Invoice) => void;
  /**
   * See {@link InvoiceFormModalProps.onLinesSaved} — the grid saved on its own,
   * which `onSaved` does not cover and must not be borrowed for.
   */
  onLinesSaved?: () => void;
  /** See {@link InvoiceFormModalProps.onXeroPush} — the report, never a push. */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  hasLineItems: boolean | 'unknown';
}) {
  const formId = useId();
  const subjectRef = useRef<HTMLElement | null>(null);

  /*
   * The record this form is now editing, which is NOT always the one it was
   * opened with.
   *
   * A create leaves the modal open — a line item is
   * `POST /invoices/{id}/products` and cannot exist before the invoice does, so
   * the lines are entered against the record the create just returned. From
   * that moment the form is an EDIT of that record: same baseline, same diff,
   * same PATCH. `created` also carries the re-read after a line write, because
   * a line write rewrites the four header totals in its own transaction and
   * returns none of them.
   */
  const [created, setCreated] = useState<Invoice | null>(null);
  /**
   * True once THIS form created the invoice it is now showing.
   *
   * Separate from `created !== null`, which a post-line-write refetch also
   * sets. Only the create needs to be announced, and only the create turns
   * Cancel into Done.
   */
  const [justCreated, setJustCreated] = useState(false);
  /**
   * The invoice a CONVERT produced whose follow-up PATCH then failed.
   *
   * ⚠️ **It exists because the two halves of one Save are two writes with no
   * transaction across them, and the second one failing does not un-make the
   * first.** `POST /quotes/{id}/convert` creates a real financial record, copies
   * every line onto it and moves the quote's stage; there is no rollback
   * endpoint, and deleting the invoice to "undo" would destroy the copy that
   * succeeded. So when the PATCH is refused the invoice is named, linked, and
   * the only thing offered again is the PATCH — never the conversion.
   *
   * ⚠️ **Held apart from `created`.** `created` turns the form into an edit and
   * mounts the saved line grid, which is exactly wrong here: the lines are the
   * server's copy, they are already written, and the reader's next move is to go
   * and look at the invoice rather than to add to it from this modal.
   */
  const [convertedInvoice, setConvertedInvoice] = useState<Invoice | null>(null);
  /**
   * Whether the follow-up HEADER PATCH is what failed — as opposed to a line.
   *
   * ⚠️ **`convertedInvoice` stopped being able to answer this on 2026-08-31**,
   * when the quote-convert modal gained a draft grid. It used to be set on
   * exactly one occasion, a refused PATCH, so the banner could say "Your header
   * changes were refused" from its presence alone. It is now ALSO set when the
   * conversion landed, the header was fine, and an added LINE was refused —
   * because in that case too the form must stay open and must never convert
   * again.
   *
   * ⚠️ **So the banner had to stop inferring the cause from the fact.** Reading
   * "the invoice exists and we are still here" as "the header was refused" would
   * mark no field, name no reason, and tell somebody to correct something that
   * was never wrong. Classic proxy-field shape: a value written for one purpose,
   * consulted as the answer to another, correct until the day a second writer
   * appears.
   */
  const [convertHeaderRefused, setConvertHeaderRefused] = useState(false);
  /**
   * What Xero did with the invoice a CONVERSION created, where that is worth
   * saying — or `null`, which is the answer for most conversions.
   *
   * ⚠️ **Separate state from `xeroPush`, and the two cannot be merged.** That one
   * is rendered inside `createdNote`, which is gated on `justCreated`, and the
   * convert paths deliberately never set `justCreated` — the lines are the
   * server's copy and the form must not turn into an edit of them. So the create
   * path's band is unreachable from here and this is the convert path's own copy
   * of it, exactly as the refused-added-line block below is.
   *
   * ⚠️ **Non-null is what HOLDS THE MODAL OPEN**, and that is the whole point of
   * it. Both convert paths otherwise call `onSaved` and `onClose`, and both
   * callers navigate to the new invoice — so a band set here and left to render
   * would be unmounted by the navigation before anybody read it, which is the
   * failure the refused-line report already had to solve the same way.
   *
   * ⚠️ **And `failed` is the only outcome that gets here.**
   * {@link conversionPushReport} answers `null` for `pushed` and for `skipped`,
   * which between them are nearly every conversion — the reasoning is at that
   * function and it is not this form's to second-guess.
   */
  const [convertPush, setConvertPush] = useState<XeroRowPushReport | null>(null);
  const invoice = created ?? initialInvoice;
  const isEdit = invoice !== undefined;
  /**
   * The quote being converted, or `undefined`.
   *
   * ⚠️ **Narrowed against `initialInvoice`, not against `invoice`.** Convert is a
   * create-side mode: an edit ignores it outright, and the local `created` never
   * fills in here because the convert path navigates away instead of turning the
   * form into an edit.
   */
  const convertQuote = initialInvoice === undefined ? convertFrom : undefined;
  /**
   * The DEAL being converted, or `undefined`.
   *
   * ⚠️ **Narrowed against `initialInvoice` for the same reason `convertQuote`
   * is**, and the two are mutually exclusive by construction below: a caller
   * that somehow passed both would otherwise get a form whose Save fired one
   * endpoint and whose table described the other.
   */
  const convertDeal =
    initialInvoice === undefined && convertFrom === undefined ? convertFromDeal : undefined;
  const isConvertingQuote = convertQuote !== undefined;
  const isConvertingDeal = convertDeal !== undefined;
  /**
   * Either conversion.
   *
   * ⚠️ **Most of this form only needs to know that Save is a CONVERT**, not
   * which one — the company seed is off, the note box is withheld, the totals
   * are the server's, the button says "Convert". Those sites read this. The
   * handful that genuinely differ (the line table, the refusal, `runConvert`)
   * read `isConvertingQuote` / `isConvertingDeal` explicitly, so adding a mode
   * cannot silently change the meaning of a branch that was written for one.
   */
  const isConverting = isConvertingQuote || isConvertingDeal;
  /*
   * The seed, computed once per source. Every value in it is the SERVER's own
   * convert copy — see `quote-to-invoice.ts` and `invoiceBaselineFromDeal` in
   * `deal-conversion.ts`. It is both the starting `values` and the diff
   * BASELINE, which is what makes "only what the user changed" mean "only what
   * differs from what convert is about to write".
   *
   * ⚠️ **`statusId` is left `''` by BOTH builders, deliberately.** The server
   * resolves Draft by name inside its own transaction; `effectiveStatusId`
   * renders Draft in the picker without writing it into `values`, so an
   * untouched status is absent from the follow-up diff and the server's answer
   * stands. Seeding an id here would send it back as a redundant PATCH key.
   */
  const convertPrefill = useMemo(
    () =>
      convertQuote !== undefined
        ? invoicePrefillFromQuote(convertQuote)
        : convertDeal !== undefined
          ? { values: invoiceBaselineFromDeal(convertDeal) }
          : null,
    [convertQuote, convertDeal],
  );

  /*
   * ── The draft line items, before there is an invoice to attach them to ────
   *
   * ⚠️ A line is `POST /invoices/{id}/products` and there is no bulk endpoint,
   * so a line CANNOT be written before the invoice exists. Creating with lines
   * is therefore necessarily two phases: the grid below holds the rows locally
   * while `invoice` is undefined and hands them up here as exactly the bodies
   * the POST takes; this form writes them, one at a time, straight after the
   * invoice comes back.
   *
   * `blockedDraftRows` is the rows the grid is WITHHOLDING because they would
   * be refused — no product, a bad percentage, a city the line's project does
   * not declare, a `(product, project)` pair another row already claims. It is
   * not cosmetic: submitting with a blocked row would create the invoice and
   * silently drop that row, and **an invoice cannot be un-created**. So Save is
   * disabled until it is zero. Reading `draftLines.length` alone would miss it.
   */
  const [draftLines, setDraftLines] = useState<InvoiceProductCreateInput[]>([]);
  const [blockedDraftRows, setBlockedDraftRows] = useState(0);
  const [lineOutcome, setLineOutcome] = useState<DraftLineOutcome | null>(null);
  /*
   * ── The note, before there is an invoice to attach it to ─────────────────
   *
   * Same shape as the draft lines above, and for the same reason: a note is
   * `POST /notes` carrying an `invoiceId`, so it cannot be written before the
   * invoice exists. It is therefore phase two of one Save, reported honestly —
   * `noteOutcome` is `null` while nothing has been attempted, which is a
   * different fact from a note that was refused and must never render as one.
   *
   * ⚠️ **Nothing here reaches `buildPayload`.** There is no `notes` key on an
   * invoice and sending one would be a `.strict()` 422 naming it; the note is a
   * separate request whose failure belongs beside the note box, never routed
   * through `routeFieldErrors` onto a header control.
   */
  const [noteDraft, setNoteDraft] = useState('');
  const [noteOutcome, setNoteOutcome] = useState<DraftNoteOutcome | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  /* Read and written synchronously, so a double click cannot write two notes. */
  const noteLock = useRef(false);
  /*
   * ── The quote's own lines, read only to be SHOWN ─────────────────────────
   *
   * ⚠️ Nothing here writes them and nothing here may: convert inserts them
   * server-side, in the transaction that makes the invoice. This read exists so
   * the person converting can see what they are billing before it exists, and so
   * the totals band can tell whether the lines will own the four figures.
   *
   * ⚠️ **`lines: null` is "not read", never "none".** A failed read must not
   * render as an empty basket — the lines are whatever they are and every one of
   * them still crosses. The three states are kept apart for that reason alone.
   */
  const [quoteLines, setQuoteLines] = useState<{
    lines: QuoteProduct[] | null;
    loading: boolean;
    error: string | null;
  }>({ lines: null, loading: isConvertingQuote, error: null });
  /*
   * ── The DEAL's own lines, read once — and here they are EDITABLE ──────────
   *
   * ⚠️ **Not the same thing as `quoteLines` above, which is why it is a second
   * piece of state rather than a widened one.** Those are a read-only preview:
   * `POST /quotes/{id}/convert` takes no overrides, so nothing on that screen
   * can change what it writes. `POST /deals/{id}/invoices` takes a `lines`
   * override keyed by `dealLineId`, so these rows are the BASELINE somebody
   * edits before the invoice exists. Merging the two into one union would put a
   * runtime narrowing between a box and whether typing in it does anything.
   *
   * ⚠️ **Nothing here writes a DEAL line, and nothing here may.** The overrides
   * apply to the INVOICE the conversion creates — measured on the wire. The
   * deal's STAGE does move (owner 2026-10-18: to the won stage, server-side,
   * after the invoice is raised), which is what
   * `INVOICE_CONVERSION_MOVES_THE_DEAL` says on screen; the deleted
   * `CONVERSION_LEAVES_THE_DEAL_ALONE` is superseded.
   *
   * ⚠️ **`lines: null` is "not read", never "none".** A failed read must not be
   * described as an empty basket: the lines are whatever they are and every one
   * of them still crosses. The three states are kept apart for that reason.
   */
  const [dealLines, setDealLines] = useState<{
    lines: DealProduct[] | null;
    loading: boolean;
    error: string | null;
  }>({ lines: null, loading: isConvertingDeal, error: null });
  /**
   * The `lineSet` the grid would send, the row key each entry came from, and
   * whether the whole set is still the deal's own.
   *
   * 🔴 *SUPERSEDED, and it was `lineEdits: Record<number, ConvertLineEdit>` — "What
   * each row's boxes hold, keyed by `f_deal_products.id` … A row with no entry
   * here is skipped by `convertLineOverrides`, so a conversion fired before the
   * read lands sends no `lines` key at all rather than one full of empty
   * strings."* That described the `lines` override, which could only respell a
   * row the deal already had. **The grid owns the rows now** and hands up
   * `lineSet` — the COMPLETE FINAL LIST — so an id-keyed map could not even
   * address a row somebody ADDED here, which has no `dealLineId` at all.
   *
   * ⚠️ **`null` is "the grid cannot say yet"**, which is the state before the
   * deal's lines have been read. It is not `[]`: an empty `lineSet` is an invoice
   * with NO LINES, a real instruction, so the guard against a premature
   * conversion is that the key is omitted rather than sent empty.
   */
  const [convertLineSet, setConvertLineSet] = useState<{
    entries: DealConvertLineSetEntry[];
    rowKeys: string[];
    pristine: boolean;
  } | null>(null);
  /**
   * How many grid rows carry a refusal of ours. Client refusals only — the grid
   * says so on the prop, and a server error must not disable the one control
   * that can re-send the correction.
   */
  const [convertBlockedRows, setConvertBlockedRows] = useState(0);
  /**
   * The server's own per-line refusals, **keyed by the grid's own ROW KEY**.
   *
   * ⚠️ **Not by `dealLineId`.** A 422 names `lineSet.2.productId`, and `2` is a
   * position in the array that was SENT — `convertLineSetFieldErrors` is the one
   * place that translation happens, and it is this form's to call because this
   * form is what holds the `rowKeys` array it sent alongside the entries.
   *
   * ⚠️ **Cleared at the start of every attempt**, exactly as `serverErrors` is on
   * the ordinary save path: a refusal about the request before last, left marked
   * on a box somebody has since corrected, is a form that cannot be argued with.
   */
  const [lineServerErrors, setLineServerErrors] = useState<Record<string, Record<string, string>>>(
    {},
  );
  /**
   * The COPIED quote lines' respellings — exactly the `lines` array
   * `POST /quotes/{id}/convert` takes, or `null` until the quote's lines have
   * been read.
   *
   * ⚠️ **`null` is not `[]`, and here the two differ in the OPPOSITE direction
   * from `convertLineSet` above.** There, `[]` is an invoice with no lines. Here,
   * `[]` means "respell none" — which is harmless in itself but must still be
   * sent as an ABSENT key, because an absent `lines` is byte-for-byte the
   * conversion this endpoint has always performed and nothing else is.
   */
  const [quoteOverrides, setQuoteOverrides] = useState<QuoteConvertLineOverride[] | null>(null);
  /**
   * The server's per-line refusals on a quote conversion, **keyed by
   * `f_quote_products.id`**.
   *
   * ⚠️ **By the quote line's id, and NOT by the grid's row key — the opposite of
   * `lineServerErrors` above, and the difference is real.** Only a COPIED row can
   * be refused this way: it is the only kind that appears in `lines`. A row
   * somebody ADDED is written by its own `POST /invoices/{id}/products` after the
   * conversion and reports on itself through `lineOutcome`, so a row-key map
   * could not have addressed it either way, and an id map is the shape that
   * survives a row being added or removed between the request and its refusal.
   *
   * ⚠️ **Cleared at the start of every attempt**, for the reason its sibling is.
   */
  const [quoteLineErrors, setQuoteLineErrors] = useState<Record<number, Record<string, string>>>(
    {},
  );
  /**
   * The COMPANY's own SALES TAX — its rate and its Xero code — TAGGED with the
   * company it was read for.
   *
   * ⚠️ *SUPERSEDED: "**Only the CONVERT grid reads it** — a converted line has no
   * tax rate of its own, so that grid's Tax rate column is a readout of this
   * figure. Every other mode has a live control and never consults it."*
   * **Every mount reads it now**, on the owner's report of 11 September 2026
   * that a new line item shows the Xero default and not the company's rate. A
   * live control is not the same thing as an INFORMED one: the server has always
   * seeded a created line from this figure, and the grid simply had no way to say
   * so, so the empty option read "Xero default" over a 20% save.
   *
   * ⚠️ **The tag is what keeps it honest.** Untagged, a slow response for company
   * A would answer for a form that has since moved to company B — and the answer
   * here is a percentage printed beside an invoice, not a pin that can be
   * ignored.
   *
   * ⚠️ **`null` INSIDE the tag is a real answer** — the company states no rate,
   * which is what makes `POST /deals/{id}/invoices` refuse — while `null` for the
   * whole slot means nothing has been read. The grid is handed those as two
   * different states and must never collapse them: see `CompanySalesTax`.
   * (Measured 11 September 2026 over 100 live companies: all 100 state a rate,
   * 63 of them a genuine `0.00`. The state is rare, not gone.)
   *
   * ⚠️ **READ AND NEVER SENT.** No payload built by this form carries either
   * value; the server owns the figure and seeds the line itself.
   */
  const [companyTaxRate, setCompanyTaxRate] = useState<{
    companyId: number;
    rate: string | null;
    taxType: string | null;
  } | null>(null);
  /*
   * Where the created invoice stands with XERO — a third outcome, kept apart
   * from `lineOutcome` and from the save's own error state on purpose. All three
   * can be true at once: the invoice created, a line refused, and Xero not
   * reached. They are rendered together, in that order.
   */
  const [xeroPush, setXeroPush] = useState<XeroCreatePush>({ kind: 'idle' });

  const { ability } = useAuth();

  /*
   * ⚠️ `/notes` is one of the THREE modules the server genuinely enforces by
   * permission set rather than by role, so this is a real gate and not a view
   * decision — and it is `ability`, never `user.role` and never
   * `/permissions/me` read directly. It stays an optimisation all the same: a
   * set can change under an access token that lasts about five days, so the
   * POST's own 403 is handled in `writeDraftNote` regardless.
   */
  const noteDenial = ability.canCreate('notes')
    ? null
    : (denialReason(ability, 'create', 'notes') ?? 'Your account cannot add notes.');

  /**
   * ⚠️ **Draft lines take ownership of the totals BEFORE either exists.**
   *
   * The moment there is a draft line, this Save is going to `POST` it — and
   * every line write rewrites all four header totals from the lines in its own
   * transaction. Sending `subTotal`/`grandTotal` on the create as well would
   * put two writers on one figure inside a single click: the header's value
   * would land, the first line's write would silently replace it, and the
   * replaced figure is kept nowhere.
   */
  const draftLinesPending = !isEdit && draftLines.length > 0;

  /*
   * Only a definite `false` hands the totals to the header. See the note on the
   * prop: an unknown count is read as "the lines may own these".
   *
   * An invoice created in THIS form is `'unknown'` from the moment it exists:
   * the line-items grid below is live and saved and this form is not told what
   * it holds, so claiming the header still owns the totals would be a guess in
   * the one direction that costs money. `true` while drafts are pending, rather
   * than `'unknown'`, because this form knows perfectly well that lines are
   * coming.
   */
  /*
   * ⚠️ CONVERTING: the answer is the QUOTE's line count, and an unread one is
   * `'unknown'` rather than `false`.
   *
   * Convert copies the header totals verbatim AND inserts the lines in the same
   * statement — it never goes through the line-write path, so it does not
   * re-derive the four figures. But this form's follow-up PATCH does write them
   * if they are offered and changed, and any LATER line write on the invoice
   * would then replace what was typed. So the rule is the form's usual one: a
   * document with lines does not hand its totals to the header, and a count
   * nobody could read fails safe in that same direction.
   */
  /*
   * ⚠️ CONVERTING A DEAL: same rule, different source. The server writes the
   * lines, so the four header totals are theirs the instant the invoice exists;
   * a read that has not landed or that failed is `'unknown'`, which fails safe
   * in the same direction.
   */
  const convertLineState: boolean | 'unknown' = isConvertingDeal
    ? dealLines.loading || dealLines.lines === null
      ? 'unknown'
      : dealLines.lines.length > 0
    : quoteLines.loading || quoteLines.lines === null
      ? 'unknown'
      : quoteLines.lines.length > 0;
  /*
   * ⚠️ **`draftLinesPending` outranks the convert answer, and that is not
   * belt-and-braces — it closes a real hole opened on 2026-08-31.**
   *
   * The quote-convert path can now carry ADDED lines. A quote with no lines of
   * its own gives `convertLineState === false`, which hands the four header
   * totals to the header boxes — and then `runConvert` writes the added lines,
   * every one of which re-derives all four totals server-side. The typed figure
   * would land and be silently replaced inside one click, and the replaced value
   * is kept nowhere. A document that is GOING to have lines does not hand its
   * totals to the header, whoever is about to write them.
   */
  const lineItemState: boolean | 'unknown' = isConverting
    ? draftLinesPending
      ? true
      : convertLineState
    : justCreated
      ? 'unknown'
      : draftLinesPending
        ? true
        : hasLineItems;
  const totalsLocked = lineItemState !== false;
  const countUnknown = lineItemState === 'unknown';

  /*
   * ⚠️ Read and written SYNCHRONOUSLY in `handleSubmit`, before anything
   * awaits. On a create a second click that gets past it is a second INVOICE,
   * not a retry — and this form no longer closes on a create, so the button
   * stays under the cursor while phase two is still running.
   */
  const submitLock = useRef(false);
  /** Aborts an in-flight header re-read when another one starts, or on unmount. */
  const refreshRef = useRef<AbortController | null>(null);
  /**
   * True while this form is on screen.
   *
   * The Xero push outruns the form: it is fired WITHOUT being awaited, so the
   * modal can be closed while it is still in flight. Nothing aborts it — a write
   * into an accounting system is not something to cancel halfway, and a
   * half-sent invoice is not a state anyone can read — so this flag is what
   * stops its result being written onto a component that is gone.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      refreshRef.current?.abort();
    };
  }, []);

  /*
   * Read the quote's lines, once, for the preview and for the totals decision.
   *
   * ⚠️ Aborted on unmount and guarded on `signal.aborted` in both branches, like
   * every other fetch in this app — and the FAILURE is recorded rather than
   * swallowed, because "we could not read them" and "there are none" are the two
   * answers this screen must never confuse.
   */
  useEffect(() => {
    if (convertQuote === undefined) return;
    const controller = new AbortController();
    const { signal } = controller;
    /*
     * ⚠️ No `setQuoteLines({ loading: true })` here, and it is not an omission.
     * The initial state is already `loading: isConverting`, so the very first
     * paint is the loading one — and the modal is KEYED on the quote's id, so a
     * different quote remounts this component rather than re-running the effect
     * over stale rows. Setting state synchronously in an effect for a value the
     * initialiser already has is a second render for nothing.
     */
    api
      .listQuoteProducts(convertQuote.id, signal)
      .then((response) => {
        if (signal.aborted) return;
        setQuoteLines({ lines: response.data, loading: false, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setQuoteLines({
          lines: null,
          loading: false,
          error:
            caught instanceof ApiError
              ? caught.message
              : 'The request did not complete — check your connection.',
        });
      });
    return () => controller.abort();
  }, [convertQuote]);

  /*
   * Read the deal's lines, once — as the preview AND as the edit baseline.
   *
   * ⚠️ Aborted on unmount and guarded on `signal.aborted` in both branches, and
   * the FAILURE is recorded rather than swallowed: "we could not read them" and
   * "there are none" are the two answers this screen must never confuse.
   */
  useEffect(() => {
    if (convertDeal === undefined) return;
    const controller = new AbortController();
    const { signal } = controller;
    /*
     * No `setDealLines({ loading: true })`, and it is not an omission: the
     * initial state is already `loading: isConvertingDeal`, and the modal is
     * KEYED on the deal's id, so a different deal remounts this component
     * rather than re-running the effect over stale rows.
     */
    api
      .listDealProducts(convertDeal.id, signal)
      .then((response) => {
        if (signal.aborted) return;
        /*
         * ⚠️ **Nothing is seeded here any more, and that is the point of the
         * move.** *SUPERSEDED: a `setLineEdits(Object.fromEntries(…convertLineEditOf…))`
         * stood beside this line, because this form owned each row's boxes.* The
         * GRID owns them now: it seeds itself from this array's identity, which is
         * why the array is held in state and handed down whole rather than mapped
         * or filtered inline — a parent that rebuilt it every render would wipe
         * whatever somebody is typing, with nothing on screen to explain it.
         */
        setDealLines({ lines: response.data, loading: false, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setDealLines({
          lines: null,
          loading: false,
          error:
            caught instanceof ApiError
              ? caught.message
              : 'The request did not complete — check your connection.',
        });
      });
    return () => controller.abort();
  }, [convertDeal]);

  /*
   * ⚠️ **CONVERTING, the baseline is the SERVER'S COPY, not a blank form.**
   *
   * The diff taken against it is therefore "what the person changed away from
   * what convert is about to write" — which is precisely, and only, what the
   * follow-up PATCH may carry. A blank baseline here would mark all nineteen
   * seeded values as edits and PATCH the whole document back over itself
   * immediately after the server wrote it.
   */
  const baseline = useMemo(
    () => (convertPrefill === null ? toFormValues(invoice) : convertPrefill.values),
    [invoice, convertPrefill],
  );
  /*
   * A locked company seeds the live values but deliberately NOT the baseline.
   * The baseline is what the record holds and the PATCH diff is taken against
   * it, so on an edit the locked company reads as a genuine change and is sent;
   * on a create the baseline is blank either way.
   */
  /*
   * ⚠️ The prefill is applied HERE and nowhere else, and only on a create.
   *
   * In the initialiser it is a starting point: React runs it once, so every
   * later keystroke wins outright and no effect can come back and re-assert a
   * seeded value under someone's cursor. `invoice` guards the edit branch — a
   * stored subject, company, currency or project is the record's own and a
   * caller's guess must not overwrite it.
   *
   * A locked company still wins over a seeded one. The two never arrive
   * together today, but if they did, the locked one is the one with a read-only
   * control over it, and `values` has to agree with what is on screen.
   */
  const [values, setValues] = useState<FormValues>(() => {
    /*
     * ⚠️ `withDateDefaults` runs AFTER `withPrefill` and only ever fills a
     * BLANK, so a caller that one day carries a date wins outright — and it is
     * on the create branch only, because putting today's date over a stored
     * bill date would re-date a filed document.
     */
    /*
     * ⚠️ A CONVERT takes the baseline unchanged — the dates are already
     * `billDate` / `billDate + 30` from `quote-to-invoice.ts`, and `withPrefill`
     * has nothing to add over a seed that is the whole header. Running either
     * over it would be a second opinion on a value the server has already
     * decided.
     */
    const seeded =
      invoice || convertPrefill ? baseline : withDateDefaults(withPrefill(baseline, prefill));
    return lockedCompany ? { ...seeded, companyId: String(lockedCompany.id) } : seeded;
  });
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * Why a conversion was refused — a `ReactNode`, not a string.
   *
   * ⚠️ Separate from `formError`, which is a field-level summary about THIS
   * form's own keys. A convert refusal is about the quote and about the server's
   * two 409s; it is rendered whole, keeps the server's own sentence underneath
   * its own, and must never be flattened into the "one field needs attention"
   * count, which would highlight nothing.
   */
  const [convertError, setConvertError] = useState<ReactNode>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * The LINE GRID's unsaved state, on an edit — reported up by the grid itself.
   *
   * 🔴 **THIS FORM USED TO CALL A GREYED-OUT SAVE "nothing has changed" WHILE
   * ROWS BESIDE IT READ "Unsaved".** `isDirty` is the HEADER diff and nothing
   * else, so somebody who came in to fix line items — which is most of what
   * this modal is opened for — met a disabled button and concluded the app was
   * broken. Reported by the owner on 2026-09-01 against the QUOTE twin of this
   * form, and the audit log settles what happened rather than leaving it to a
   * theory: two `GET /quotes/392/products` and **not one write**. The control
   * they pressed issued nothing, because the control that could have saved
   * their work was the grid's own, scrolled out of view above them.
   *
   * ⚠️ **The invoice form was never reported and had the identical defect** —
   * same header-only `isDirty`, same in-modal grid, same footer Save. It is
   * fixed here rather than waited for; a defect only one of two twins has been
   * reported against is not a defect only one of them has.
   *
   * ⚠️ **A disabled control is a CLAIM.** This one asserted "there is nothing
   * to save" over a form that had plenty, and nothing anywhere contradicted it
   * — no error, no request, no log line. That is the shape this codebase keeps
   * paying for: the failure looks exactly like the app working.
   *
   * ⚠️ **THE NAME IS REUSED AND THE OLD MEANING IS DEAD.** Two struck comments
   * further down still say `lineEdits`, and there it was
   * `Record<number, ConvertLineEdit>` — the DEAL-CONVERT rows' typed
   * respellings, deleted when the grid took over that judgement. It has nothing
   * to do with this. Named to match `QuoteFormModal`, whose twin of this state
   * carries the same name; grep the declaration, not the word.
   */
  const [lineEdits, setLineEdits] = useState<{ dirty: number; blocked: number }>({
    dirty: 0,
    blocked: 0,
  });
  /**
   * The grid's own save run, handed up by `registerSave`.
   *
   * ⚠️ **`null` means THERE IS NO GRID — never "nothing to save".** It is null
   * on a create (the draft grid registers nothing, because a draft row has
   * nowhere to be saved to), null on both conversion paths, and null once the
   * grid unmounts. Read it beside `lineEdits.dirty`, never instead of it.
   */
  const saveLinesRef = useRef<(() => Promise<boolean>) | null>(null);
  /*
   * Stable, so the grid's registration effect runs on mount and unmount rather
   * than on every render of this form. An inline arrow here would re-register
   * continuously — harmless, but it would make the effect's dependency list a
   * lie about when registration happens.
   */
  const registerLineSave = useCallback((save: (() => Promise<boolean>) | null) => {
    saveLinesRef.current = save;
  }, []);
  const [totalsOpen, setTotalsOpen] = useState(false);
  /** Bumped when credit is applied so the credit summary in Totals refetches. */
  const [creditSummaryKey, setCreditSummaryKey] = useState(0);
  /*
   * 🔴 **`documentOpen` is GONE — 2026-08-31**, with the "Xero tracking and
   * references" disclosure. *SUPERSEDED: "it OPENS ITSELF when anything inside
   * it already holds a value, and that is the owner's explicit choice rather
   * than a nicety … a tracking override that silently redirects where Xero
   * files the sale is precisely the value a closed panel must never hide."*
   *
   * ⚠️ **That reasoning was RIGHT and is now spent rather than overruled.** It
   * argued a set override must never be hidden behind a closed panel — and the
   * answer taken instead is that this form no longer offers overrides at all,
   * so there is no such value for it to hide. **Do not read this as licence to
   * hide a set value behind a disclosure elsewhere**; the rule survives its
   * subject.
   */
  /*
   * The exchange-rate dialog. Open state only — the typed rate lives inside the
   * dialog and reaches `values.currencyRate` on Save and never before, so an
   * abandoned edit cannot leave a half-typed rate in the form's payload.
   */
  const [rateDialogOpen, setRateDialogOpen] = useState(false);

  /*
   * This inner form only exists while the modal is open — the wrapper returns
   * null otherwise — so every option source is simply enabled. The `enabled`
   * flag on these hooks is there for callers that keep a form mounted.
   */
  const {
    statuses,
    loading: statusesLoading,
    // `failed` no longer destructured: its only reader was the Status field's
    // load-failure hint, removed 2026-09-05.
  } = useInvoiceStatusOptions(true);
  /*
   * 🔴 **`useCurrencyOptions` IS NO LONGER CALLED HERE — 2026-08-31.** It fed
   * `currencyChoices`, which fed the Currency Combobox; the currency is derived
   * from the company and read-only now, so there is no list to offer and nothing
   * left to resolve a code against — an invoice's `currency` is a bare `CHAR(3)`
   * that renders as itself.
   *
   * ⚠️ **A request stopped being made, which is a behaviour change rather than a
   * tidy-up**, and it is worth stating rather than discovering: opening this form
   * no longer spends a `/currencies` read out of the budget
   * the whole office shares. The same thing happened when the project fetch left
   * with the header project picker, for the same reason.
   *
   * ⚠️ *SUPERSEDED: "the 120-per-60-seconds budget."* It is **12,000/60s**
   * (corrected 2026-09-17), and the same arithmetic is leant on again below as
   * "the cost of a rate-limited call". ⛔ **Neither reason to leave this alone
   * rests on it**: a resolved list adds nothing to a read-only row printing
   * `GBP`, and it would introduce a state where a code the master no longer
   * offers renders as unavailable. **The budget was the weakest of the three.**
   *
   * ⚠️ **Do not restore it to "label" the code.** `currencyOptions` labels a row
   * with the CODE and puts the name in a hint — so a resolved list would add
   * nothing to a read-only row printing `GBP`, at the cost of a rate-limited call
   * and a state where a code the master no longer offers renders as unavailable.
   * Render the code you were given.
   */
  /*
   * ⚠️ No `useUserOptions` here — see the import note above. The account lead
   * is not a picker in THIS app: the owner retired lead-assignment on invoices
   * in favour of the company's, so what is rendered is `invoice.accountLead`
   * (the resolved value) straight off the record, and there is no directory to
   * resolve it against.
   *
   * (This previously read "is not a picker and never can be … `accountLeadId`
   * on a write to an invoice is a 422". ⚠️ FALSE since migration 082 — the key
   * is accepted on the write schema, so a picker is buildable and merely
   * unwanted. See `FormValues` for the correction.)
   */
  /*
   * 🔴 **THE PROJECT FETCH IS GONE — 2026-08-31.** *SUPERSEDED: "Read for ONE
   * reason now: resolving the invoice's STORED header project into a full
   * record, so the two Xero tracking hints can say what Location and Tour Type
   * it derives."* That was its only reader, and the hints went with the tracking
   * overrides.
   *
   * ⚠️ **A request stopped being made, which is a behaviour change and not a
   * tidy-up**: opening an existing invoice no longer spends a page of up to 100
   * projects out of the budget the office shares. ⚠️ *SUPERSEDED:
   * "120-per-60-seconds."* It is **12,000/60s** (corrected 2026-09-17) — the
   * fetch went because nothing read it, not to save the budget. Strictly
   * better, and worth stating rather than discovering.
   *
   * ⚠️ **The read-only Project box did NOT depend on it** — `headerProjectName`
   * comes straight off `invoice.project.name`. Do not restore the fetch
   * believing that field needs it.
   */

  /*
   * The branding-theme list comes from the LIVE tenant, so it is only asked for
   * once there is a tenant to ask. While the connection read is still settling,
   * or when there is no connection, a request could only fail — and a red block
   * would be the wrong sentence for a form whose real state is "Xero is not
   * connected", which is calm and has a different action.
   */
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  const brandingThemes = useXeroBrandingThemes(xeroConnected);
  /*
   * ⚠️ **The tenant's tax-rate list is deliberately NOT read here.** It was read
   * for exactly one thing — putting a NAME on the company's stored sales tax
   * CODE in the read-only defaults row the owner has since removed — and a Xero
   * reference read is a rate-limited call against a budget shared by the whole
   * office. A line's tax control has its own list; this form's header does not
   * need one.
   */

  /*
   * ── What a NEW invoice takes FROM ITS COMPANY ────────────────────────────
   *
   * One request, one stale-response guard, four derivations — the branding
   * theme, the currency, the tax treatment and the subject. They share this
   * effect rather than each having their own because they share a SOURCE and
   * therefore a set of rules.
   *
   * ⚠️ **THREE OF THE FOUR STOPPED BEING SEEDS — owner's decision, 2026-08-31.**
   * *SUPERSEDED: "One of the four FOLLOWS the company and three only FILL A
   * BLANK … each of the three is a value a reader may have looked at and
   * accepted … Silently changing one of those under somebody who has already
   * read it is the expensive direction."* The currency, the tax treatment and
   * the branding theme are **read-only on this form now**: there is no control
   * for any of the three, so there is no choice for a derivation to overwrite,
   * no `touched` flag left to track, and the "a reader may have accepted it"
   * argument has no reader to protect. All three simply FOLLOW the company —
   * which is what the branding theme always did, and is now the rule rather than
   * the exception.
   *
   * ⚠️ **"Follow" INCLUDES FOLLOWING TO NOTHING, and that is the half most
   * easily got wrong.** *SUPERSEDED: "A company that records nothing for one of
   * them seeds nothing for it. A `null` is not an instruction to clear."* That
   * was right while a picker stood behind the value and wrong the moment one did
   * not: a company recording no currency now CLEARS the field rather than
   * leaving the previous account's code standing. With an editable picker a
   * stale value was one somebody could correct; with a read-only row it is a
   * figure the form asserts and nobody can reach — and a currency decides what a
   * customer is billed in.
   *
   * ⚠️ **The SUBJECT is untouched by all of this and still only FILLS A BLANK.**
   * It is still a control, so the old reasoning still applies to it in full: it
   * names this record on every screen, in the activity drawer and in the delete
   * confirmation, and re-deriving it under somebody who has already typed one is
   * the expensive direction. Do not "harmonise" the four.
   *
   * ⚠️ **Never on an EDIT — and this matters MORE now, not less.** A stored
   * invoice's currency, tax treatment and letterhead are what a customer was
   * invoiced against and what their PDF was printed on. Re-deriving them because
   * an account default has since moved would silently re-price or restyle a
   * document nobody asked to touch, and there would be no control to put it
   * back. `seedCompanyId` is `null` on an edit, which is what stops this before
   * it asks; the read-only rows then print the RECORD's own values.
   *
   * ⚠️ **A derived value stays exactly as legible as a stored one, and the
   * branding theme is the case that proves it.** A theme id whose LIST cannot be
   * read — Xero rate-limited, or disconnected — is still resolved through
   * `storedThemeId` / `themeChoices` below, exactly as a value off the record
   * is. There is no path here that drops a value because a list is unavailable,
   * and one must not be added: a blank row reads as "use the organisation
   * default" and is one save away from reprinting a customer's document on the
   * wrong letterhead.
   *
   * ⚠️ **Failure is silent and non-blocking.** The form opens, works and saves
   * with nothing derived; the read-only rows say so in their own words rather
   * than rendering an empty box. A red block here would be about a request the
   * person never asked for.
   */
  /**
   * A currency the CALLER supplied — a deal-raised invoice carries the currency
   * the deal was priced in, chosen deliberately by whoever pressed the button.
   *
   * ⚠️ **The one thing that still outranks the company here.** Letting the
   * company's default land over it would silently re-price a document at a row
   * the reader cannot argue with. An empty prefill is not a prefill.
   *
   * ⚠️ **A plain `const`, not the `currencyTouched` ref it replaces.** That ref
   * had exactly one writer — the currency picker's `onChange` — and the picker
   * is gone, so a ref would be a mutable box nothing can mutate. It is derived
   * from a prop rather than captured at mount because this form REMOUNTS on a
   * prefill change (see `prefillKeyOf` at the wrapper), so the two are the same
   * value by construction and the const cannot go stale.
   */
  const currencyPrefilled = (prefill?.currency ?? '').trim() !== '';
  const [seededThemeId, setSeededThemeId] = useState<string | null>(null);
  /**
   * The company the derivation has actually READ, settled or failed — **tagged
   * with that company**, never a bare boolean.
   *
   * ⚠️ The tag is what makes the derived rows honest without a second write. A
   * bare `loading` flag has to be turned on somewhere, and the only place to do
   * that is synchronously inside the effect — which is a render behind, so the
   * first paint after a company change shows the PREVIOUS account's answer with
   * nothing saying it is stale. Recording what was answered FOR lets the
   * question be asked during render instead. Same shape as `seededContact`
   * above: state carries what it ANSWERS FOR.
   */
  const [companyDefaultsReadFor, setCompanyDefaultsReadFor] = useState<number | null>(null);
  /**
   * WHICH RECORD each of the three derived rows took its value from — the
   * company itself, or the billing region it falls back to.
   *
   * ⚠️ **A read-only row printing a value nobody on the account set, with
   * nothing saying where it came from, is worse than an empty one.** The reader
   * opens the company record to change it, finds the field blank, and concludes
   * the form is lying. The two sources are two different screens: the company
   * record, and the billing-region defaults on `/catalog`.
   *
   * ⚠️ **It is a SECOND STATE rather than a shape read back off `values`, and
   * it has to be.** `values` holds the resolved answer and nothing else — by
   * the time a currency is `"EUR"` there is no way to tell whether the company
   * said so or its region did. That is exactly the distinction the SERVER goes
   * out of its way to preserve on the wire (`currency` beside
   * `billingRegionDefaults.currencyCode`, never merged), and it would be thrown
   * away here one line after arriving.
   *
   * `null` on a field means nothing derived it: no rung produced a value, the
   * form is on an EDIT (where the value is the record's own), or a PREFILL won.
   */
  const [derivedSources, setDerivedSources] = useState<DerivedSources>(NO_DERIVED_SOURCES);
  /**
   * The company to READ.
   *
   * ⚠️ Every consumer of this read is a create-only SEED, but the request
   * itself is not gated on {@link seedCompanyId} — on an edit or a convert the
   * response is discarded at the `seedCompanyId === null` return below.
   * Narrowing this to `seedCompanyId` would spend one fewer request against
   * the shared limiter; left alone because it changes what this form FETCHES
   * rather than what it shows.
   */
  const companyLookupIdNumber = Number(values.companyId.trim());
  const companyLookupId =
    !Number.isInteger(companyLookupIdNumber) || companyLookupIdNumber <= 0
      ? null
      : companyLookupIdNumber;
  /*
   * ⚠️ **A CONVERT seeds nothing from the company, and this one line is the
   * whole guard.** All four company seeds — letterhead, currency, tax treatment
   * and subject — hang off `seedCompanyId`, and every one of them would be WRONG
   * here: the quote already carries its own answer to each, the server's convert
   * copies exactly that answer, and a company default landing over it would make
   * this form produce a different document from the button it replaced. Worse,
   * it would do so silently — the seeds fire a moment after the company read
   * returns, under a form the person has already read.
   *
   * Switched off at the SOURCE rather than at the four seeds, so a fifth seed
   * added later is covered without anybody remembering to cover it. The READ
   * itself is untouched — on a convert the response is simply read and
   * discarded. See {@link companyLookupId}.
   */
  const seedCompanyId = isEdit || isConverting ? null : companyLookupId;
  /**
   * Whether the company read behind the three derived read-only rows is still
   * outstanding — see {@link companyDefaultsReadFor}.
   *
   * ⚠️ **Without it the form ASSERTS "not recorded" for the length of a network
   * round trip**, in three read-only rows at once, and then contradicts itself a
   * moment later. "We have not read it yet" and "the company records none" are
   * different facts and only one of them is a statement about the company — the
   * same unavailable-versus-empty distinction this app draws on the Xero option
   * lists, on `oldestPendingEmailAt` and on the inclusions link table.
   *
   * It never mattered while these three were seeds: an empty CONTROL is an
   * ordinary state a person is about to fill in, so there was nothing to be
   * premature about. A read-only row has to be right the first time it paints.
   *
   * ⚠️ **DERIVED during render, never a flag an effect switches on.** A flag has
   * to be turned on somewhere, and the only place is inside the effect — one
   * render late — so the first paint after a company change would show the
   * PREVIOUS account's currency with nothing marking it stale. Comparing the two
   * ids answers the question at the moment it is asked.
   *
   * CREATE-ONLY by construction: `seedCompanyId` is `null` on an edit and on a
   * convert, so this is `false` there and the rows print the record's own values
   * with nothing to wait for.
   */
  const companyDefaultsLoading =
    seedCompanyId !== null && companyDefaultsReadFor !== seedCompanyId;
  /*
   * The company the in-flight read is FOR. Aborting on change is most of the
   * answer; the stamp is what makes it structural — a slow response for
   * company A can never seed a form that has since moved to company B. It is
   * the same guard every other asynchronous slot in this app puts on its own
   * results.
   */
  const seedTargetRef = useRef<number | null>(null);

  /*
   * ── The CONTACT seed — the company's designated primary ──────────────────
   *
   * ⚠️ **Touched starts TRUE on an edit, and that is not a long way of writing
   * `isEdit`.** A stored contact is the record's own, and so is a stored
   * ABSENCE: an invoice deliberately left with no contact must not gain one
   * because somebody opened the form. Both are decisions this seed may not
   * overturn — which is why the flag is the record's existence rather than its
   * `contactId`, and why `onCompanyChange` is the one place that hands the field
   * back (see there).
   *
   * ⚠️ A CONVERT counts as touched too, for the reason `seedCompanyId` gives
   * above: `quote-convert.ts` copies the quote's `contact_id`, so the document
   * already carries its answer and a company default landing over it would make
   * this form produce a different invoice from the button it replaced. A
   * prefilled contact counts for the same reason — the caller chose it.
   */
  const contactTouched = useRef(isEdit || isConverting || prefill?.contact !== undefined);
  /**
   * The primary contact read off the chosen company, TAGGED with that company.
   *
   * ⚠️ The tag is what makes the pin correct without a second write. A bare
   * `{id, name}` would go on offering the previous account's primary until a new
   * read landed — and clearing it from the company `onChange` and from the
   * effect's no-company branch would be two more places to keep in step. Same
   * shape as the lookup hook's `result.scope`: state carries what it ANSWERS
   * FOR.
   */
  const [seededContact, setSeededContact] = useState<{
    companyId: number;
    contact: { id: number; name: string } | null;
  } | null>(null);
  /**
   * The company this form OPENED on, so a stored, converted or prefilled contact
   * can be pinned for it and only for it.
   *
   * ⚠️ Captured at mount rather than compared against `baseline.companyId`,
   * which is the blank `''` on a create — a prefilled or converting create would
   * then read as "the company changed" from its first frame, withhold the pin
   * that makes the seed legible, and announce a clearing that never happened.
   *
   * ⚠️ `useState` rather than `useRef`, though nothing ever writes it: it is
   * READ DURING RENDER, and a ref read during render is exactly the value React
   * does not re-render for. Same reason `dueDateTouched` is state while the seed
   * guards around it are refs — those are never rendered, this is.
   */
  const [openedCompanyId] = useState(values.companyId);
  /*
   * ⚠️ **`companyApproval` IS GONE — deleted 2026-08-31**, with the client-side
   * approval refusal it was the only reader of. The server's own gate
   * (`assertCompanyApproved`, migration 099) is the backstop now, and it is a
   * better one: it reads the company's row inside the same transaction as the
   * write, so there is no in-flight window and no tag needed to keep a stale
   * answer for company A from refusing company B.
   *
   * ⚠️ **Do not reinstate it as a "faster" pre-check.** A second reader of the
   * same fact is a second sentence about it, and this form has just been
   * corrected for having one. The picker's withholding is the optimisation; the
   * 409 is the guarantee.
   */

  useEffect(() => {
    seedTargetRef.current = companyLookupId;
    // No company, nothing to derive. The seeded pin does not need clearing here:
    // it is tagged with the company it came from and `seededContactPin` stops
    // resolving the moment the form names a different one.
    if (companyLookupId === null) return;
    const controller = new AbortController();
    api
      .getCompany(companyLookupId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        if (seedTargetRef.current !== companyLookupId) return;
        const company = envelope.data;

        /*
         * ── The CONTACT, above the create-only guard on purpose ────────────
         *
         * ⚠️ **The one seed here that is NOT gated on `seedCompanyId`.** Its
         * guard is `contactTouched`, which starts true on an edit and on a
         * convert and is handed back by `onCompanyChange` and by nothing else —
         * so on an edit this fires exactly when somebody has moved the company,
         * which is the moment the stored contact stopped describing this
         * document. It is not a re-derivation of a saved record: the field it
         * fills was emptied one gesture ago, by the user.
         *
         * ⚠️ `primaryContact: null` seeds NOTHING and is an ordinary state — an
         * account that designates nobody. It is never an instruction to clear,
         * which is why the branch below only ever writes an id.
         */
        /*
         * ⚠️ **Read on EVERY path, not only on a create**, and deliberately above
         * the create-only guard below. The convert grid's Tax rate column is a
         * readout of this figure, and a conversion is exactly the case the guard
         * excludes — so putting it underneath would leave that column reading "not
         * read" for ever, with nothing failing.
         *
         * ⚠️ **And every OTHER mount reads it too now.** A blank percentage on a
         * NEW line is given this figure server-side, so the Tax rate control and
         * the Amount preview both have to know it or they describe a save that is
         * not going to happen — the owner's report of 11 September 2026.
         *
         * It is stored and never sent back: the server derives and owns this
         * number, and a client that computes or re-sends it is a second writer of
         * a figure printed on an invoice — and one that seeded it into a draft
         * would send the value as it stood when this response landed, in
         * preference to the one in force at Save.
         */
        setCompanyTaxRate({
          companyId: companyLookupId,
          rate: company.xeroSalesTaxRate,
          taxType: company.xeroSalesTaxType,
        });

        const primary = primaryContactSeed(company);
        setSeededContact({ companyId: companyLookupId, contact: primary });
        if (primary && !contactTouched.current) {
          setValues((prev) => {
            // Re-checked inside the updater: a pick made between the render
            // that scheduled this and the render that applies it must win.
            if (contactTouched.current || prev.contactId !== '') return prev;
            return { ...prev, contactId: String(primary.id) };
          });
        }

        /*
         * ⚠️ **Everything below is create-only, and this `if` is what keeps it
         * so while the READ still runs on an edit too.** Without this line a
         * stored invoice would be re-derived: a letterhead already printed on a
         * customer's PDF restyled because the account default has since moved,
         * a currency re-set on a filed document. `seedCompanyId` is the value
         * every guard here is written against.
         */
        if (seedCompanyId === null) return;

        /*
         * The read has landed for THIS company, so the three derived rows stop
         * saying "reading…" whatever it turned out to contain. Recorded before
         * the writes below rather than after, because a company recording
         * nothing writes nothing and there would otherwise be no line to hang it
         * on — and "the company records no currency" is exactly the answer that
         * has to be reachable.
         */
        setCompanyDefaultsReadFor(seedCompanyId);

        /*
         * ⚠️ Nothing below short-circuits what follows it. A single early
         * `return` on an absent company field would silently cancel the rest —
         * a bug with no symptom except fields quietly not filling in.
         *
         * The three read-only rows share ONE `setValues` (see below) because
         * they are one derivation from one response; the SUBJECT keeps its own
         * statement after it, because it is still a control and still obeys the
         * old fill-a-blank rule.
         */

        /*
         * ── The three READ-ONLY rows: branding theme, currency, tax ─────────
         *
         * ⚠️ **Assigned WHOLESALE from this company, empty value included.**
         * They are not seeds any more (see the header above): with no control
         * behind them, "leave the previous company's answer standing" is a stale
         * assertion nobody on this form can correct. So a company recording none
         * clears the row, and the row says so in words.
         *
         * One `setValues` for the three, deliberately: they are one derivation
         * from one response, and three updaters would let a re-render land
         * between them with the form showing company A's currency beside company
         * B's letterhead.
         */

        /*
         * ── THE SECOND RUNG: THE COMPANY'S BILLING REGION ──────────────────
         *
         * ⚠️ **`billingRegionDefaults` ARRIVES ON THE COMPANY READ. Do NOT add
         * a `/billing-region-defaults` fetch here.** The server resolves
         * `country_id -> f_countries.billing_region -> f_billing_region_defaults`
         * and serves the row BESIDE the company's own four values without
         * merging them, precisely so this form can fall back AND still say
         * which record a value came from. A second client-side derivation of
         * the same fact is what was just removed from this codebase.
         *
         * ⚠️ **`null` here is the ORDINARY case, not a failure** — it is every
         * company with no country of record, which is nearly all of them until
         * the backfill runs. It also folds "the slug is one this bundle does
         * not know". Neither is an answer, and neither may be read as a region.
         *
         * ⚠️ **Measured, not assumed: 4 of 149 live companies carry a branding
         * theme, a tax type, a line-amount type or a currency of their own.**
         * That is the whole reason this rung exists — read-only rows against a
         * company-only chain would have been EMPTY on 145 of 149 accounts, and
         * a read-only empty row is worse than an editable one because nothing
         * on the screen can be acted on.
         */
        const region = company.billingRegionDefaults;

        /*
         * ⚠️ Only the id. `xeroBrandingThemeName` is NEVER resolved by the
         * server on a COMPANY — it is always null there — so it is not read and
         * must not become a label; the live theme list is what names a theme.
         *
         * ⚠️ **The region's `xeroBrandingThemeName` is NOT read either, though
         * it IS populated on that master.** A name from one record beside an id
         * from another is how a row ends up labelling company A's theme with
         * region B's word. One namer, and it is `themeChoices`.
         */
        const companyThemeId = (company.xeroBrandingThemeId ?? '').trim();
        const regionThemeId = (region?.xeroBrandingThemeId ?? '').trim();
        const themeId = companyThemeId !== '' ? companyThemeId : regionThemeId;
        /*
         * ⚠️ The CODE, never the id. A company's currency arrives as
         * `{ id, code, name }`, and an invoice stores `currency` as a `CHAR(3)`
         * with no foreign key to `/currencies` — an id here would be a 422 or,
         * worse, coerced into a nonsense code. Uppercased for the same reason
         * `withPrefill` uppercases: a code stored in lower case on an older
         * master row must not read as a different currency from the same code.
         *
         * ⚠️ **BOTH RUNGS GET THE SAME TREATMENT, and the `!== ''` test is what
         * makes the fall-through right.** A company holding `''` — which the
         * column can — must fall THROUGH to its region rather than winning the
         * chain and pricing a document in an empty currency. That is the same
         * decision `company-document-defaults.lookup.ts` takes server-side by
         * normalising before its own coalesce; a bare `??` over the raw values
         * would be wrong on exactly that row and right on every other.
         */
        const companyCurrency = (company.currency?.code ?? '').trim().toUpperCase();
        const regionCurrency = (region?.currencyCode ?? '').trim().toUpperCase();
        const derivedCurrency = companyCurrency !== '' ? companyCurrency : regionCurrency;
        /*
         * The Xero sales line-amount default, translated out of the CONTACT
         * enum into the DOCUMENT one — see `documentLineAmountFromCompany`,
         * which is the only place that translation happens and the only reason
         * `'NONE'` can never reach a write. `null` becomes `''`, which means
         * "fall back to the company's Xero sales default" and never "no tax".
         *
         * 🔴 **THE FALL-THROUGH TESTS WHETHER THE COMPANY *STATED* ONE, NOT
         * WHETHER THIS BRANCH COULD TRANSLATE IT — AND THE OBVIOUS VERSION IS
         * WRONG IN A WAY NOTHING WOULD CATCH.** Writing
         * `fromCompany ?? fromRegion` reads correctly and is a real defect: a
         * company whose default is **`NONE`** translates to `null` here (on
         * purpose — see that function), so the coalesce would hand the document
         * its REGION's `EXCLUSIVE`. That is a company's own explicit "no tax"
         * silently overridden by a regional default, on a customer's invoice,
         * with a green build and a value that looks perfectly ordinary.
         *
         * A `NONE` company therefore stays `''`, exactly as before this rung
         * existed, and `''` omits the key so the SERVER resolves it — where
         * `NONE` really does become `NOTAX`. The region is consulted only where
         * the company has answered nothing at all.
         */
        const companyStatesLineAmount = company.xeroSalesLineAmountType !== null;
        const regionLineAmountType = companyStatesLineAmount
          ? null
          : documentLineAmountFromCompany(region?.xeroSalesLineAmountType ?? null);
        const lineAmountType = companyStatesLineAmount
          ? (documentLineAmountFromCompany(company.xeroSalesLineAmountType) ?? '')
          : (regionLineAmountType ?? '');

        /*
         * ⚠️ **WHICH RECORD EACH VALUE CAME FROM, recorded at the one moment
         * both rungs are in hand.** The rows print it, because a person shown a
         * currency nobody on the account set will otherwise open the company
         * record, find the field empty, and conclude the form is broken. It is
         * the same reason the server refuses to merge the two: they are two
         * different screens to go and change.
         *
         * `null` on a field means NEITHER rung produced it — nothing derived,
         * so there is no source to name and the absence sentence renders
         * instead. The currency is also `null` when a PREFILL won, because a
         * prefilled code came from the caller and from no record at all.
         */
        const sources: DerivedSources = {
          currency:
            companyCurrency !== ''
              ? 'company'
              : regionCurrency !== ''
                ? 'billing-region'
                : null,
          xeroLineAmountType: companyStatesLineAmount
            ? 'company'
            : regionLineAmountType !== null
              ? 'billing-region'
              : null,
          xeroBrandingThemeId:
            companyThemeId !== '' ? 'company' : regionThemeId !== '' ? 'billing-region' : null,
        };

        // Kept only while the live theme list cannot name it — see `storedThemeId`.
        setSeededThemeId(themeId === '' ? null : themeId);

        /*
         * ⚠️ **Recorded whatever the read contained**, beside
         * `setCompanyDefaultsReadFor` above and for the same reason: a company
         * that states nothing and sits in no region produces three `null`s,
         * which is an answer the rows have to be able to reach.
         *
         * ⚠️ **The currency source is cleared when a PREFILL wins**, because
         * the value on screen then came from the caller and from neither
         * record — and naming a record it did not come from is the one thing
         * this label exists to prevent.
         */
        setDerivedSources(currencyPrefilled ? { ...sources, currency: null } : sources);

        setValues((prev) => {
          /*
           * ⚠️ **A PREFILLED currency outranks BOTH rungs** — see
           * `currencyPrefilled`. Nothing else here has a caller-supplied
           * counterpart: a prefill carries no branding theme and no tax
           * treatment, so those two follow the company (then its region)
           * unconditionally.
           */
          const currency = currencyPrefilled ? prev.currency : derivedCurrency;

          if (
            prev.xeroBrandingThemeId === themeId &&
            prev.currency === currency &&
            prev.xeroLineAmountType === lineAmountType
          ) {
            return prev;
          }

          /*
           * ⚠️ **A PINNED EXCHANGE RATE DIES WITH THE CURRENCY IT WAS MEASURED
           * AGAINST**, and this is the only place left that can orphan one.
           *
           * Found in production on a real Xero document, not in review. An
           * invoice was set to AUD with a rate of 1.890950 — correct for AUD —
           * and its currency was then changed to INR. The rate survived, so Xero
           * printed "GBP 6,398.90" against a total of 12,100 INR. The true
           * figure is about GBP 93: overstated roughly SIXTY-NINE TIMES, on a
           * document a customer could have received. The arithmetic was right
           * the whole way through — a rate is meaningless without the PAIR it
           * describes, and nothing was tying the two fields together.
           *
           * ⚠️ **The guard used to live in `onCurrencyChange`, which went with
           * the currency picker.** The hazard did not go with it: a rate can
           * still be pinned on a create and the company can still move
           * underneath it, so the rule was carried here rather than deleted with
           * its old home. Clearing loses a rate somebody typed, which is a real
           * cost; keeping it is worse, because an absent rate prints nothing and
           * hands the document to Xero's own daily rate while a stale one prints
           * a confident wrong answer.
           *
           * Only on an actual CHANGE of code — a re-read that lands on the same
           * currency must not wipe the rate.
           */
          const rateOrphaned = currency !== prev.currency && prev.currencyRate.trim() !== '';

          return {
            ...prev,
            xeroBrandingThemeId: themeId,
            currency,
            xeroLineAmountType: lineAmountType,
            ...(rateOrphaned ? { currencyRate: '' } : {}),
          };
        });

        // ── Subject ─────────────────────────────────────────────────────────
        /*
         * ⚠️ **This seed only ever fills an EMPTY subject**, and never
         * overwrites — so changing the company on a form whose subject is
         * already the PREVIOUS company's name does not re-seed it. That is the
         * safe direction: the subject names the record on every page, in the
         * activity drawer and in the delete confirmation.
         *
         * `subjectTouched` is checked as well as the emptiness: `''` after
         * somebody clears the box is a deliberate blank, and putting the
         * company name back into it would undo exactly the thing they just did.
         *
         * The length test matters even though a company name is capped well
         * under 255 today — a seeded value the field then refuses reads as the
         * form breaking, and truncating puts a name cut off mid-word on a
         * financial document.
         */
        /*
         * `companyDocumentName`, not `company.name` — prefers the company's
         * `shortName` where one is set, and shares the answer with the quote
         * form so an invoice raised against a quote carries the same title.
         * `shortName` is null on every company today, so this is `name` for
         * now, at up to 81 characters — which is why the length test below
         * still matters.
         */
        const companyName = companyDocumentName(company);
        if (
          !subjectTouched.current &&
          companyName !== '' &&
          companyName.length <= MAX_LENGTHS.subject
        ) {
          setValues((prev) =>
            prev.subject.trim() === '' ? { ...prev, subject: companyName } : prev,
          );
        }
      })
      .catch(() => {
        /*
         * ⚠️ Silent. No theme, currency, tax treatment or subject derived is a
         * working form, not an error, and a red block here would be about a
         * request the person never asked for.
         *
         * ⚠️ **A FAILED read still has to stop the rows saying "reading…"**, or
         * a company whose fetch 500s leaves three fields claiming to be loading
         * for the life of the form. "We tried and got nothing" and "the company
         * records none" collapse here deliberately — the rows say the value is
         * not set and name where it is set, which is the true and actionable
         * half either way.
         *
         * ⚠️ **An ABORT must NOT record anything**: it fires because a newer
         * read for a DIFFERENT company is already in flight, and recording this
         * one would make that read's rows say "not recorded" while it is still
         * running. Same stale-response reasoning as `seedTargetRef` above, on
         * the other half of the promise.
         */
        if (controller.signal.aborted) return;
        if (seedTargetRef.current !== companyLookupId) return;
        setCompanyDefaultsReadFor(seedCompanyId);
        /*
         * ⚠️ **Cleared, not left standing.** A failed read derives nothing, so
         * naming a source would be a claim about a response that never arrived
         * — and on a company CHANGE whose second read fails, the sources left
         * behind describe the PREVIOUS account. The rows fall back to the
         * absence sentence either way, which is the true and actionable half.
         */
        setDerivedSources(NO_DERIVED_SOURCES);
      });
    return () => controller.abort();
    /*
     * `seedCompanyId` is derived from `companyLookupId` and `isEdit`, neither of
     * which can change without this list changing, so it is not a second
     * dependency — `isEdit` flips only when this form creates the invoice, and
     * that already re-runs nothing here because the company id is unchanged and
     * the seeds have by then done their work.
     */
    /*
     * `seedCompanyId` is `isEdit ? null : companyLookupId`, so it moves only
     * when `companyLookupId` does and cannot go stale here. It is listed anyway
     * rather than left to that reasoning: the derivation is one edit away from
     * gaining a third input, and a seed that silently stopped firing on a
     * company change is invisible — the field just keeps the old account's
     * value.
     */
    /*
     * `currencyPrefilled` is derived from `prefill`, and this form REMOUNTS on a
     * prefill change (`prefillKeyOf` at the wrapper), so it cannot move while
     * this effect is mounted. Listed anyway rather than left to that reasoning —
     * exactly as `seedCompanyId` is, two comments up, and for the same reason:
     * the remount key is one edit away from not covering it, and a derivation
     * that silently read a stale prefill would re-price a document.
     */
  }, [companyLookupId, seedCompanyId, currencyPrefilled]);

  /*
   * ── Draft, on a NEW invoice ──────────────────────────────────────────────
   *
   * A new invoice starts on Draft rather than on nothing — matched by identity
   * against `DRAFT_STATUS_NAME`, see that constant's own comment for why never
   * an id and why no row means no status.
   *
   * ⚠️ **`isActive` as well as the name.** A retired Draft is not offered by
   * `selectableStatuses` on a create — it only pins a status back when the
   * RECORD holds it, and a create holds none — so seeding one would leave the
   * picker rendering blank over a form that is carrying the id.
   *
   * ⚠️ **Not a seed into `values`, and not the initialiser either — DERIVED.**
   * The other defaults on this form are written once because their source is
   * either synchronous (the prefill, the dates) or arrives as a per-company
   * fetch (the letterhead, the currency, the tax treatment). This one's source
   * is a MASTER LIST that is empty on the first paint and lands a moment later,
   * so the initialiser is too early and an effect would be a second render for a
   * value the render already has. See `effectiveStatusId` below: it is computed
   * where it is read, `statusTouched` is what stops it once somebody chooses,
   * and there is no window in which `values` and the picker disagree.
   */
  /**
   * True once a status has been CHOSEN here — including choosing "No status".
   *
   * State rather than a ref, unlike the other `touched` flags on this form,
   * because this one decides what is rendered: the moment it flips, the picker
   * has to stop showing Draft and show the choice. A ref read during render is
   * exactly the value React is not told to re-render for.
   */
  const [statusTouched, setStatusTouched] = useState(false);

  /** The active Draft row's id, or `''` where the master offers none. */
  const draftStatusId = useMemo(() => {
    const draft = statuses.find(
      (status) => status.isActive && normaliseStatusName(status.name) === DRAFT_STATUS_NAME,
    );
    return draft === undefined ? '' : String(draft.id);
  }, [statuses]);

  /**
   * The status this form is actually carrying — the chosen one, or Draft while
   * nothing has been chosen.
   *
   * ⚠️ **DERIVED DURING RENDER, never written into `values` by an effect.** The
   * master is fetched, so it is empty on the first paint and arrives a moment
   * later; seeding it from an effect would be a second render for a value this
   * render already has, and it is the pattern
   * `react(set-state-in-effect)` names. Deriving also removes a whole class of
   * race: there is no window in which `values` and the picker disagree.
   *
   * Two readers and they must not drift: the Combobox below and the create
   * branch of `buildPayload`. An EDIT never sees it — a stored status is the
   * record's own — and neither does a form where somebody has chosen.
   */
  const effectiveStatusId = isEdit || statusTouched ? values.statusId : draftStatusId;

  /**
   * True once the due date has been edited by hand, after which it stops
   * following the bill date.
   *
   * State rather than a ref for the same reason as `statusTouched`: its hint is
   * read while rendering, and a ref is the one thing React will not re-render
   * for. It is set by the Due date input's own `onChange` and by nothing else —
   * `withDateDefaults` and the bill-date follow-through are both writes this
   * form makes, and neither is the user touching it.
   */
  const [dueDateTouched, setDueDateTouched] = useState(false);

  /**
   * A SEEDED subject still counts as TOUCHED — the rule this ref enforces is
   * "never seed over a value somebody meant". A prefilled subject is the same
   * thing arriving from the other direction: an invoice raised from a deal
   * takes the DEAL'S NAME, chosen deliberately by the caller, and the company
   * seed above must not replace it with the company's name.
   *
   * Trimmed, because a seed of `'   '` is as absent as none and must not lock
   * the company seeding out of a subject that is really still empty.
   *
   * ⚠️ There is no per-project subject seed here — a line names its own
   * project and a document may hold several, so the subject would follow
   * whichever row was edited last. Do not add one against a LINE's project.
   */
  const subjectTouched = useRef((prefill?.subject ?? '').trim() !== '');

  /*
   * Pins for the three searched pickers, memoised so the option lists are not
   * rebuilt on every keystroke elsewhere in the form.
   */
  /*
   * ⚠️ A SEEDED link needs pinning exactly as a stored one does, and this is
   * the half of a prefill that is easy to leave out and impossible to see is
   * missing. `values.companyId` holding an id selects nothing until a page of
   * the directory happens to return that company — so an unpinned seed renders
   * as "Unassigned" over a form that is in fact carrying the id, and the person
   * either believes nothing was filled in or clears a field that was never
   * empty. The pin is what makes the seed legible.
   */
  const companyPin = useMemo(() => {
    // The locked company wins over the record it is pinned against: it is the
    // value the form actually holds, and the pin is what keeps a selection from
    // reading as unassigned when the fetched page does not contain it.
    if (lockedCompany) return { id: lockedCompany.id, label: lockedCompany.name };
    if (invoice?.company) return { id: invoice.company.id, label: invoice.company.name };
    /*
     * ⚠️ A CONVERTED quote's counterparty needs pinning exactly as a stored one
     * does. Its id is in `values` from the seed, and without the pin the picker
     * reads "Unassigned" over a form that is carrying it — on the one screen
     * where the reader is checking who is about to be billed.
     */
    if (convertQuote?.company) {
      return { id: convertQuote.company.id, label: convertQuote.company.name };
    }
    /*
     * ⚠️ **AND THE CONVERTED DEAL'S, for exactly the same reason.** Its company
     * is in `values.companyId` from `invoiceBaselineFromDeal`, and an unpinned
     * seed renders "Unassigned" over a form that is carrying the id — on the one
     * screen where the reader is checking who is about to be billed, and where
     * the natural response is to "fill in" a field that was never empty.
     */
    if (convertDeal?.company) {
      return { id: convertDeal.company.id, label: convertDeal.company.name };
    }
    return prefill?.company ? { id: prefill.company.id, label: prefill.company.name } : null;
  }, [invoice, lockedCompany, prefill, convertQuote, convertDeal]);
  /**
   * Whether the company has moved away from the one this form opened on.
   *
   * Derived, not stored, so moving back to the original company restores the
   * original contact's pin rather than leaving the field permanently narrowed by
   * a flag nothing resets.
   */
  const companyMoved = values.companyId !== openedCompanyId;
  /**
   * The seeded primary, but only while it describes the company now chosen.
   *
   * Derived, so a company change drops the previous account's primary with no
   * write at all — see the note on `seededContact`.
   */
  const seededContactPin =
    seededContact !== null && seededContact.companyId === companyLookupId
      ? seededContact.contact
      : null;
  /*
   * ⚠️ FOUR sources, and the SEED is the one that is easy to leave out.
   *
   * The stored, converted and prefilled contacts must stay OFFERED even when
   * they are not among the company's own — a contact dropped from the list
   * renders "Unassigned" over a record that holds one, and the next save writes
   * that emptiness back with a 200.
   *
   * ⚠️ All three are withheld once the company has MOVED, the same rule
   * `intakeOptions` applies to a stored intake across a cycle change — and for a
   * sharper reason: there the stale choice is a guaranteed 422, here it is a
   * guaranteed silent success.
   *
   * The seeded primary needs pinning for the mirror-image reason given on
   * `companyPin` above: `values.contactId` holding an id selects nothing until an
   * option carries that value, and the scoped list has not landed yet at the
   * moment the seed is applied.
   */
  const contactPin = useMemo(() => {
    if (!companyMoved) {
      if (invoice?.contact) return { id: invoice.contact.id, label: invoice.contact.name };
      if (convertQuote?.contact) {
        return { id: convertQuote.contact.id, label: convertQuote.contact.name };
      }
      /* The converted DEAL's contact, pinned on the same reasoning. */
      if (convertDeal?.contact) {
        return { id: convertDeal.contact.id, label: convertDeal.contact.name };
      }
      if (prefill?.contact) return { id: prefill.contact.id, label: prefill.contact.name };
    }
    return seededContactPin ? { id: seededContactPin.id, label: seededContactPin.name } : null;
  }, [invoice, prefill, convertQuote, convertDeal, seededContactPin, companyMoved]);
  // Deals key their title `dealName`, not `name` — the one resource here that
  // does not follow the convention. A prefill has already resolved that, so its
  // `deal.name` is whatever the caller decided to call the deal.
  const dealPin = useMemo(() => {
    if (invoice?.deal) return { id: invoice.deal.id, label: invoice.deal.dealName };
    if (convertQuote?.deal) return { id: convertQuote.deal.id, label: convertQuote.deal.dealName };
    /*
     * ⚠️ **The deal being CONVERTED is its own pin**, and this is the one of the
     * three where the seed is guaranteed rather than conditional:
     * `invoiceBaselineFromDeal` always writes `dealId`, because the conversion
     * always records the provenance link. Without the pin the Deal picker reads
     * "Unassigned" on every single deal→invoice conversion.
     */
    if (convertDeal) return { id: convertDeal.id, label: convertDeal.dealName };
    return prefill?.deal ? { id: prefill.deal.id, label: prefill.deal.name } : null;
  }, [invoice, prefill, convertQuote, convertDeal]);

  // Nothing searches companies while the field is locked — the picker is not
  // rendered — so the request is skipped rather than made and thrown away.
  // ⚠️ SUPERSEDED: "The limiter is 120 requests per 60s per IP, shared across
  // the whole office." It is 12,000/60s (corrected 2026-09-17); the request is
  // skipped because nothing renders it, not to save the budget.
  /*
   * ⚠️ **`!isEdit && !isConverting` — the withholding is CREATE-ONLY**, and the
   * two exclusions are for different reasons.
   *
   * On an EDIT the rule does not apply at all: an invoice already raised against
   * a company since rejected has to stay editable, and narrowing the picker
   * would also make it impossible to move such an invoice onto a company
   * somebody is still getting approved. Over-offering is the safe direction on a
   * field whose refusal is ours rather than the server's.
   *
   * On a CONVERT — from a quote OR from a deal — the field is read-only, the
   * company is copied server-side, and the owner ruled explicitly that a deal
   * with no company stays convertible. A filtered list there would narrow a
   * control nobody can open.
   */
  const companies = useLookupSearch(
    'companies',
    companyPin,
    lockedCompany === undefined,
    undefined,
    !isEdit && !isConverting,
  );
  /*
   * ⚠️ SCOPED. `companyLookupId` is `null` when the form names no company, and
   * the picker is then EMPTY rather than falling back to the whole directory —
   * an unscoped list is the behaviour the owner asked to remove, and it is the
   * only way to pick a contact the company will contradict. `contactHint` says
   * why. Same shape as the intake picker's dependence on the cycle.
   */
  const contacts = useLookupSearch('contacts', contactPin, true, companyLookupId);
  const deals = useLookupSearch('deals', dealPin);

  /*
   * `values.contactId === ''` is part of the "cleared" sentence and not of the
   * pin gate: once somebody has chosen a replacement there is nothing to
   * explain, but the old contact stays off the list either way.
   */
  const contactScope = { ...contacts.scope, cleared: companyMoved && values.contactId === '' };

  /**
   * The chosen company as `{id, name}`, for the Contact field's inline
   * "+ Add a new contact…" row to lock a new contact to.
   *
   * ⚠️ The name is read from the SAME option list the company control draws its
   * own label from, so the two can never disagree — if the field on screen names
   * a company, this resolves it, and if it does not there is nothing to lock to.
   * That covers the locked and converting modes as well, where no search runs
   * and the pinned row is the whole option list.
   */
  const contactCompany = useMemo(() => {
    if (companyLookupId === null) return null;
    const label = companies.options.find((option) => option.value === values.companyId)?.label;
    return label === undefined ? null : { id: companyLookupId, name: label };
  }, [companyLookupId, companies.options, values.companyId]);

  /**
   * The scoped options, with the pinned contact MARKED when it is not one of
   * this company's.
   *
   * ⚠️ The marking is gated on `contactScopeIsAuthoritative`; the OPTION never
   * is. While the list is loading, unreadable, or narrowed by a search term,
   * every stored contact looks absent from it — and accusing a perfectly correct
   * contact of belonging to another account on the strength of a request that
   * has not come back is a confident wrong answer where saying nothing costs
   * nothing. Same gate as `productOptions`' "Retired" reason in
   * `ProjectFormModal`.
   */
  const markOffCompany = contactScopeIsAuthoritative(contactScope) && contacts.pinInjected;
  const pinnedContactValue = contactPin === null ? null : String(contactPin.id);
  const contactOptions = useMemo<ComboboxOption[]>(() => {
    if (!markOffCompany || pinnedContactValue === null) return contacts.options;
    return contacts.options.map((option) =>
      option.value === pinnedContactValue ? { ...option, hint: CONTACT_NOT_ON_COMPANY } : option,
    );
  }, [contacts.options, markOffCompany, pinnedContactValue]);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => {
      /*
       * ⚠️ Guard one of three: a locked total is not even a CHANGE.
       *
       * The inputs are not rendered while `totalsLocked`, so a total can only
       * differ from the baseline because the record moved underneath — which is
       * exactly what a line write does, since it rewrites all four in its own
       * transaction. Counting that as the user's edit would light up Save, and
       * `buildPayload` would then refuse to send it and report "Nothing has
       * changed yet" over a form that plainly says it has. Dropping the key here
       * is the honest reading: the lines changed it, not the person.
       *
       * `QuoteFormModal` carries this guard verbatim. The two forms share
       * `documents/document-form.ts` BECAUSE they diverged once before, so a
       * guard present in one and missing from the other is that divergence
       * starting again — which is why this went in while `buildPayload` still
       * makes it unreachable, rather than after something made it reachable.
       */
      if (totalsLocked && (TOTAL_KEYS as readonly string[]).includes(key)) return false;
      return values[key].trim() !== baseline[key].trim();
    });
  }, [values, baseline, totalsLocked]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /**
   * Changing the company CLEARS the contact, and hands the field back to the
   * primary-contact seed.
   *
   * ⚠️ **The server will not catch this one.** `invoices.service.ts` checks a
   * contact through `contactsExist` — does the row exist — and never against the
   * company; the pair that `deliverables.service.ts` refuses is accepted here
   * with a `200`. So a contact left behind by a company change is not a 422
   * somebody reports, it is a stored, plausible, wrong person on a document that
   * demands money from a customer, and this function is the only thing standing
   * in front of it.
   *
   * Resetting `contactTouched` is the second half and is what makes the seed
   * correct on an EDIT: the previous choice was a choice about the previous
   * company, so it stops counting the moment that company goes. Without it the
   * field would be cleared and then left empty, which is worse than either end —
   * the person loses a value AND is offered nothing in its place.
   *
   * ⚠️ Only on an actual CHANGE. Re-picking the same company must not discard a
   * contact somebody deliberately chose, the same way the company derivation
   * below only clears a pinned exchange rate when the CODE really moved.
   * (*SUPERSEDED 2026-08-31: "the same way `onCurrencyChange` below…"* — that
   * function went with the currency picker; the rule it names moved into the
   * derivation and did not go with it.)
   *
   * ⚠️ **`companyId` and `contactId` do NOT have to travel together in the
   * PATCH**, unlike `{cycleId, intakeId}` or a deliverable's
   * `{companyId, pointOfContactId}`: no service here judges the pair on the
   * post-merge record, so a lone `companyId` is an ordinary write. The diff in
   * `buildPayload` sends whichever of the two actually moved, and that is
   * correct — which is exactly why the clearing has to happen HERE, in the form,
   * where it is the only guard there is.
   */
  function onCompanyChange(next: string) {
    if (next === values.companyId) return;
    contactTouched.current = false;
    // `seededContact` is NOT cleared here: it carries the company it was read
    // for, so it simply stops resolving. One writer, no second place to forget.
    setValues((prev) => ({ ...prev, companyId: next, contactId: '' }));
    setClientErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
    setServerErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
  }

  /**
   * The ONLY writer of `contactTouched` besides `onCompanyChange` above.
   *
   * Set even when the box is CLEARED, because clearing is a decision: without
   * that, emptying the contact would hand the field straight back to the seeder,
   * which refills it on the next company change and reads as the control
   * fighting the user. Same rule as `subjectTouched` — which is now the ONLY
   * other one left. (*SUPERSEDED 2026-08-31: "Same rule as `subjectTouched` and
   * `themeTouched`."* `themeTouched`, `currencyTouched` and `taxTouched` all went
   * when their three controls became derived read-only rows: with nothing able to
   * touch a field, there is no touch to record.)
   */
  function onContactChange(next: string) {
    contactTouched.current = true;
    setField('contactId', next);
  }

  /*
   * ── WHERE `onCurrencyChange` USED TO BE ──────────────────────────────────
   *
   * It did two things: it wrote `currencyTouched`, and it CLEARED a pinned
   * exchange rate whenever the code really moved. The currency picker was its
   * only caller and the currency is read-only now, so the function went.
   *
   * ⚠️ **The rate-clearing rule did NOT go with it** — a rate can still be
   * pinned on a create and the company underneath it can still move, so the
   * guard was carried into the company derivation above rather than deleted
   * with its old home. The production incident it comes from (a stale AUD rate
   * printing "GBP 6,398.90" against 12,100 INR — roughly SIXTY-NINE times over)
   * is written up there in full.
   *
   * ⚠️ **This is the shape this codebase records as its most repeated defect:
   * a guard written against one control, silently retired with that control,
   * while the hazard it guarded outlived it.** Removing a picker removes a way
   * to set a value; it does not remove the ways the value can move.
   */


  /*
   * The response carries resolved objects (`status`, `owner`, `deal`) where the
   * write takes ids, plus read-only audit columns, so a loaded invoice can never
   * be PATCHed back wholesale — every schema is `.strict()`, and an unknown key
   * is a 422 rather than a silent strip. Only changed keys go.
   *
   * The acting user is never sent: the server takes it from the bearer token,
   * and any actor key is now a 422.
   */
  /**
   * The PATCH body for a given set of changed keys.
   *
   * ⚠️ **Split out so the CONVERT follow-up cannot word a write differently from
   * an ordinary edit.** Both go to `PATCH /invoices/{id}` and both have to obey
   * the same three rules that are easy to get subtly wrong: `''` on an id key is
   * an explicit `null` that CLEARS, the two NOT NULL totals are omitted rather
   * than nulled, and the two dates travel together or not at all. A second
   * implementation of this for the convert path would agree on the day it was
   * written and drift on the first change to any of the three.
   *
   * The keys are passed in rather than read off `changedKeys` because the
   * convert path sends a NARROWER set — bounded by
   * `CONVERT_FOLLOW_UP_PATCH_KEYS` — and the narrowing is the caller's decision.
   */
  function buildUpdatePayload(keys: readonly (keyof FormValues)[]): InvoiceUpdateInput {
    const payload: InvoiceUpdateInput = {};
    for (const key of keys) {
      const next = values[key].trim();
      if (key === 'subject') {
        // Not nullable server-side: `subject: null` is a 422.
        payload.subject = next;
      } else if ((ID_KEYS as readonly string[]).includes(key)) {
        payload[key as (typeof ID_KEYS)[number]] = next === '' ? null : Number(next);
      } else if (key === 'currency') {
        // Uppercased on the way out as well as on the way in: a code pinned
        // back from an older record could still be stored in lower case.
        payload.currency = next === '' ? null : next.toUpperCase();
      } else if (key === 'currencyRate') {
        /*
         * ⚠️ An explicit `null` CLEARS the pin, and that is the intended
         * meaning of an emptied rate — the "Remove" control writes '' here,
         * and clearing hands the document back to Xero's own daily rate.
         * That is safe in a way the two NOT NULL totals are not:
         * `currency_rate` is genuinely nullable, so `null` is stored as null
         * rather than silently taking a column default. It is never `0` and
         * never `1`: both are real rates, and one of them converts every
         * figure on the document to nothing.
         *
         * It reaches this branch BY NAME rather than through `TEXT_KEYS`
         * because it is not text — it must not be length-checked, and it is
         * validated for decimal shape instead.
         *
         * Never sent when unchanged. The diff is what stops an invoice that
         * carries a pinned rate losing it because somebody edited its
         * subject.
         */
        payload.currencyRate = next === '' ? null : next;
      } else if ((TOTAL_KEYS as readonly string[]).includes(key)) {
        const totalKey = key as (typeof TOTAL_KEYS)[number];
        /*
         * ⚠️ Guard two of three: the header NEVER writes a total the LINES
         * own.
         *
         * Neither the inputs nor `changedKeys` can reach here while
         * `totalsLocked`, so nothing should — but this is the one place in
         * the form where a bug costs real money rather than a re-save. A
         * header PATCH writes literally what it is sent and never re-derives,
         * and the figures it replaces are kept nowhere. So the guard is
         * structural: no path through this function can put a totals key in
         * the same PATCH as anything else while the lines are in charge.
         */
        if (totalsLocked) continue;
        // An emptied NOT NULL total is caught by `validate` before we get
        // here; omitting it is the belt to that pair of braces, so a bug
        // upstream can never turn into a silent zero on a financial record.
        if (next === '' && UNCLEARABLE_TOTALS.includes(totalKey)) continue;
        payload[totalKey] = next === '' ? null : next;
      } else if (key === 'xeroLineAmountType') {
        /*
         * Narrowed rather than cast. The control can only produce the three
         * DOCUMENT members, but the baseline comes from the SERVER, and a
         * value outside the union must never be smuggled into a write
         * claiming to be one. Anything unrecognised — a company's `NONE`
         * included — becomes `null`, which says "fall back to the company's
         * Xero sales default", and never "no tax".
         */
        payload.xeroLineAmountType = toDocumentLineAmountType(next);
      } else if ((DATE_KEYS as readonly string[]).includes(key)) {
        // Handled once, just below: both dates travel together or not at all.
        continue;
      } else {
        payload[key as (typeof TEXT_KEYS)[number]] = next === '' ? null : next;
      }
    }

    // Either date changing sends both, so the server has the pair to compare.
    // See the note on DATE_KEYS: a one-sided PATCH skips the check entirely.
    if (DATE_KEYS.some((key) => keys.includes(key))) {
      payload.billDate = values.billDate.trim() === '' ? null : values.billDate.trim();
      payload.dueDate = values.dueDate.trim() === '' ? null : values.dueDate.trim();
    }

    return payload;
  }

  /*
   * The follow-up write a CONVERT needs, and nothing more.
   *
   * ⚠️ **Bounded by `CONVERT_FOLLOW_UP_PATCH_KEYS`, not merely by
   * `changedKeys`.** The bound is what stops this form ever naming a key that is
   * a 422 on an invoice — `quoteNumber`, `cityIds` — because a single such key
   * fails the WHOLE call, and the call in question is the one carrying the
   * user's edits onto a document that already exists.
   *
   * ⚠️ **`accountLeadId` was listed here as a third 422 and has been removed
   * from that list (2026-08-30): it is NOT refused.** Migration 082 put a real
   * `account_lead_id` override on `d_invoices` and the key is on the write
   * schema (`invoices.schema.ts:200`). It stays out of this bound anyway — this
   * form writes no lead, on the owner's instruction — but it is excluded by
   * decision, not because the server would reject it.
   *
   * Empty is the ordinary case: somebody who read the form and pressed Convert
   * changed nothing, and no second request is made at all.
   */
  function buildConvertFollowUpPatch(): InvoiceUpdateInput {
    const allowed = CONVERT_FOLLOW_UP_PATCH_KEYS as readonly string[];
    return buildUpdatePayload(changedKeys.filter((key) => allowed.includes(key)));
  }

  function buildPayload(): InvoiceCreateInput | InvoiceUpdateInput {
    if (invoice) return buildUpdatePayload(changedKeys);

    const payload: InvoiceCreateInput = { subject: values.subject.trim() };
    for (const key of ID_KEYS) {
      /*
       * ⚠️ `statusId` comes from `effectiveStatusId`, not from `values`. Draft is
       * DERIVED rather than written into state, so `values.statusId` is still ''
       * on a form nobody has touched the picker on — and sending nothing there
       * would create the invoice with no status while the control plainly reads
       * "Draft". The two have to agree, and this is the second of the derived
       * value's only two readers.
       */
      const next = key === 'statusId' ? effectiveStatusId : values[key];
      if (next !== '') payload[key] = Number(next);
    }
    for (const key of DATE_KEYS) {
      if (values[key] !== '') payload[key] = values[key];
    }
    if (values.currency.trim() !== '') payload.currency = values.currency.trim().toUpperCase();
    /*
     * Omitted when unset, never sent as an explicit `null`. On a create the two
     * read the same to the server — no pin either way — and omission is what
     * this app sends for every unset field. ⚠️ It is emphatically not defaulted
     * to `"1"`: a pin of 1 is a rate somebody negotiated, and inventing one on
     * every create would put a fabricated parity on documents whose currencies
     * are nowhere near it.
     */
    if (values.currencyRate.trim() !== '') payload.currencyRate = values.currencyRate.trim();
    // Same rule as the PATCH branch: the header only carries totals when the
    // lines are not the owner. A create can only be `totalsLocked` when the
    // caller could not tell, and an unknown line count fails safe.
    if (!totalsLocked) {
      for (const key of TOTAL_KEYS) {
        const next = values[key].trim();
        if (next !== '') payload[key] = next;
      }
    }
    for (const key of TEXT_KEYS) {
      const next = values[key].trim();
      if (next !== '') payload[key] = next;
    }
    /*
     * Omitted when unset, never sent as an explicit `null` — on a create the
     * two read the same to the server, and omission is what this app sends for
     * every unset field. Narrowed rather than cast, for the reason spelled out
     * in the PATCH branch above.
     */
    const lineAmountType = toDocumentLineAmountType(values.xeroLineAmountType.trim());
    if (lineAmountType) payload.xeroLineAmountType = lineAmountType;
    return payload;
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const subject = values.subject.trim();
    if (subject === '') found.subject = 'Subject is required.';
    else if (subject.length > MAX_LENGTHS.subject) {
      found.subject = 'Must be 255 characters or fewer.';
    }

    /*
     * ── A NEW INVOICE NEEDS A COMPANY ─ and that half really is ours ────────
     *
     * ⚠️ **APPROVAL IS NO LONGER CHECKED HERE — corrected 2026-08-31.** This
     * block used to refuse an unapproved company in sentences of our own, on the
     * stated premise that the server did not. It does: `assertCompanyApproved`
     * (migration 099) runs inside `createInvoice` and answers a **409** whose
     * message is better than either sentence we had. It is read by
     * `billingGateRefusalOf` and bannered by `reportFailure`.
     *
     * What remains is genuinely this form's own: `companyId` is nullable on the
     * wire and **the guard skips a document naming no company**, so without this
     * line a company-less invoice saves.
     *
     * ⚠️ **CREATE ONLY, and both exclusions are deliberate.**
     * - An EDIT of an invoice with no company has to keep working. Refusing
     *   there would strand a real document — one a customer may already have
     *   been sent — that nobody can correct.
     * - A CONVERT is excluded because the owner ruled explicitly that a deal
     *   with no company stays convertible — **and the backend agrees**, skipping
     *   the guard when the source names none — and because on both convert paths
     *   the company is read-only and copied server-side, so the refusal would
     *   name a control the reader cannot reach. **Do not let this rule reach
     *   either convert path.**
     */
    if (!isEdit && !isConverting) {
      const refusal = newInvoiceCompanyError(values.companyId);
      if (refusal !== null) found.companyId = refusal;
    }

    for (const key of LENGTH_LIMITED) {
      if (values[key].trim().length > MAX_LENGTHS[key]) {
        found[key] = `Must be ${MAX_LENGTHS[key].toLocaleString()} characters or fewer.`;
      }
    }

    const currency = values.currency.trim().toUpperCase();
    if (currency !== '' && !CURRENCY_PATTERN.test(currency)) {
      found.currency = 'Use a three-letter ISO code, e.g. USD.';
    }

    /*
     * ⚠️ Kept even though the dialog will not let an invalid rate out of
     * itself, and the reason is the second writer: the SERVER can still name
     * `currencyRate` on a 422 — a rate with seven decimal places from an older
     * record, or a schema that tightens later — and `KNOWN_WRITE_KEYS` claims
     * this form can mark it. That claim is only true because the rate note
     * under the Currency row renders `errors.currencyRate`. Marking a key
     * with nowhere on screen to show it is the "one field needs attention,
     * highlighted below" lie with nothing highlighted; the two halves have to
     * stay together.
     */
    const currencyRate = values.currencyRate.trim();
    if (currencyRate !== '' && !isValidCurrencyRate(currencyRate)) {
      /*
       * "Above zero" is in the sentence because zero is the refusal a reader
       * would not otherwise predict: the SERVER accepts `0` — its rule is
       * non-negative — and `isValidCurrencyRate` is deliberately stricter,
       * because a zero rate divides every base-currency total to `0.00` beside
       * a real document total. A message naming only the decimal places would
       * leave someone re-typing `0.000000` at a control that keeps refusing it.
       */
      found.currencyRate = `Use a rate above zero with at most ${CURRENCY_RATE_MAX_DP} decimal places, e.g. 1.89207.`;
    }

    // Both the shape and the existence of the day, exactly as the server checks.
    for (const key of DATE_KEYS) {
      const value = values[key].trim();
      if (value !== '' && !isRealDate(value)) {
        found[key] = 'Must be a real date in YYYY-MM-DD form.';
      }
    }

    /*
     * Compared as strings, never as Dates. ISO dates sort lexicographically so
     * the comparison is exact, whereas parsing 'YYYY-MM-DD' through `new Date()`
     * reintroduces the timezone off-by-one this format exists to avoid.
     * Reported at `dueDate`, which is where the server puts it.
     */
    const billDate = values.billDate.trim();
    const dueDate = values.dueDate.trim();
    if (
      found.billDate === undefined &&
      found.dueDate === undefined &&
      billDate !== '' &&
      dueDate !== '' &&
      billDate > dueDate
    ) {
      found.dueDate = 'The due date cannot be before the bill date.';
    }

    // Nothing to check once the lines own the totals: the inputs are not
    // rendered, and nothing money-shaped is in the payload either.
    if (!totalsLocked) {
      for (const key of TOTAL_KEYS) {
        const next = values[key].trim();
        if (next !== '' && !AMOUNT_PATTERN.test(next)) {
          found[key] = 'Use up to 12 digits and at most 2 decimal places, e.g. 1800.00.';
        } else if (
          next === '' &&
          isEdit &&
          UNCLEARABLE_TOTALS.includes(key) &&
          baseline[key].trim() !== ''
        ) {
          found[key] =
            `${TOTAL_LABELS[key]} cannot be emptied — enter a figure, or put the previous one back.`;
        }
      }
    }

    return found;
  }

  /**
   * Writes the draft lines against an invoice that now exists.
   *
   * ⚠️ **One request per line, run one at a time, and it does NOT stop at the
   * first failure.** There is no bulk endpoint and no transaction across them,
   * so a partial result is an ordinary outcome rather than an edge case.
   *
   * - **Sequential, not `Promise.all`.** The office shares a rate limit,
   *   `sortOrder` is what the customer's document prints in, and a
   *   `(product, project)` clash between two rows must be reported against the
   *   second one rather than raced.
   * - **A refused line does not abandon the rest.** The lines are independent;
   *   stopping would lose four good rows to one bad one.
   *
   * ⚠️ **Nothing is rolled back and nothing pretends to be.** The invoice
   * exists from the moment the create returned, and deleting it to "undo" a
   * refused line would destroy the part that worked.
   */
  async function writeDraftLines(
    invoiceId: number,
    lines: InvoiceProductCreateInput[],
  ): Promise<DraftLineOutcome> {
    const failures: DraftLineOutcome['failures'] = [];
    let saved = 0;
    /*
     * ⚠️ Overwritten on every write that lands, so what survives is the LAST
     * one — see the field's own note. A write that FAILS leaves it alone: its
     * report describes a request that did not happen.
     */
    let xeroPush: XeroPushReport | undefined;

    for (const [index, line] of lines.entries()) {
      try {
        const result = await api.createInvoiceProduct(invoiceId, line);
        xeroPush = result.xeroPush;
        saved += 1;
      } catch (caught) {
        failures.push({ position: index + 1, message: lineFailureMessage(caught) });
      }
    }

    return { attempted: lines.length, saved, failures, xeroPush };
  }

  /**
   * Write the note again, against the invoice that now exists.
   *
   * ⚠️ **Not a rollback and not a repair of the save** — the invoice was created
   * and stays created either way; it cannot be un-created at all. This is the
   * plain `POST /notes` a person would otherwise make from the invoice's own
   * Tasks & notes drawer, offered here only because the text they typed is
   * already on screen and retyping it is the one thing they should not have to
   * do.
   *
   * Reachable only after `created` is set, so `invoice` is a real record; the
   * guard is a ref rather than the `savingNote` state because a second click
   * arrives before a re-render and would otherwise write the note twice.
   */
  async function retryDraftNote() {
    if (noteLock.current) return;
    const target = invoice;
    if (!target) return;

    noteLock.current = true;
    setSavingNote(true);
    try {
      const result = await writeDraftNote({ kind: 'invoice', id: target.id }, noteDraft);
      setNoteOutcome(result);
      /*
       * Cleared only when it landed, exactly as the create path does: a refused
       * note exists nowhere but this box.
       */
      if (result?.kind === 'saved') setNoteDraft('');
    } finally {
      noteLock.current = false;
      setSavingNote(false);
    }
  }

  /**
   * Re-reads the header after a line write.
   *
   * ⚠️ Every line write rewrites `subTotal`, `discountTotal`, `taxTotal` and
   * `grandTotal` in the same transaction and returns none of them, so the
   * record this form holds is stale in exactly the four places that matter.
   *
   * Deliberately silent on failure: the grid has already reported the line
   * write truthfully, and a red block here would read as the line having
   * failed. The figures simply stay as they were until the invoice is reopened.
   */
  function refreshInvoice() {
    const target = invoice;
    if (!target) return;
    refreshRef.current?.abort();
    const controller = new AbortController();
    refreshRef.current = controller;
    api
      .getInvoice(target.id, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCreated(result.data);
      })
      .catch(() => {
        /* see above — a stale header beats a misleading error */
      });
  }

  /**
   * ── PHASE THREE: re-read the header after the server pushed ───────────────
   *
   * ⚠️ **CORRECTED 2026-08-31 — THIS PHASE NO LONGER PUSHES. THE SERVER DOES.**
   * `pushCreatedToXero`, `pushWithLock`, `runXeroPush` and `sendToXeroAgain`
   * stood here and are deleted with the `pushInFlight` lock, the cooldown
   * pre-check and the no-lines pre-check. Their header read:
   *
   * > *"send the created invoice to Xero … This runs AFTER the invoice AND its
   * > draft lines exist, which is the only order that works … an invoice pushed
   * > before its lines is an EMPTY invoice in SEED's accounting system."*
   *
   * The ordering argument is unchanged and is now the SERVER's to keep: it
   * pushes inside each write, so the last line write is the one that saw the
   * whole document — which is why `writeDraftLines` carries that report up and
   * not the create's.
   *
   * ⚠️ **Why the client copy went at all.** The server already pushed inside
   * the save, so this form pushed a second time on every create: two calls
   * against a tenant budget of 60 a minute and 5,000 a day, and two accounts of
   * one action. The owner's ruling is that saving-pushes is a property of the
   * SYSTEM rather than of one screen — it has to hold for an importer, a script
   * and any second client, none of which run this code.
   *
   * What remains is the re-read, and it is the half that was never about the
   * push: a successful push allocates `xeroInvoiceNumber`, fills the link's
   * GUID, and REPLACES `currencyRate` with the rate Xero applied. Without this
   * the form holds a stale `currencyRate` that the very next Save would PATCH
   * back over Xero's own figure.
   *
   * Silent on failure, like every other refresh here — a stale header beats an
   * error about a refresh. Fired rather than awaited, and guarded by `mounted`.
   */
  async function refreshAfterServerPush(target: Invoice) {
    try {
      const fresh = (await api.getInvoice(target.id)).data;
      if (!mounted.current) return;
      /*
       * ⚠️ The baseline follows the server, and so does every value the user has
       * NOT touched since the create — but only those. `setValues(toFormValues)`
       * wholesale would throw away anything typed while this was in flight, and
       * leaving `values` alone entirely would leave a replaced `currencyRate`
       * reading as the user's own edit, which the next Save would PATCH back
       * over Xero's figure.
       */
      const before = toFormValues(target);
      const after = toFormValues(fresh);
      setCreated(fresh);
      setValues((prev) => {
        let next: FormValues | null = null;
        for (const key of Object.keys(after) as (keyof FormValues)[]) {
          if (prev[key] !== before[key] || after[key] === prev[key]) continue;
          next ??= { ...prev };
          next[key] = after[key];
        }
        return next ?? prev;
      });
    } catch {
      /* see above — a stale header beats an error about a refresh */
    }
  }

  /**
   * Closes a form that created something.
   *
   * `onSaved` as well as `onClose`, because the callers treat `onSaved` as
   * "there is a new or changed record — reload" and `onClose` as "put the form
   * away". An invoice was created; the list has to hear about it even though
   * the last thing the user did was add a line, and the `?new` query parameter
   * still has to come out of the URL.
   *
   * ⚠️ **Refused outright while a save is in flight**, which Escape and the
   * close button can otherwise reach — the footer buttons are disabled, they
   * are not. A create with line items is several requests deep by then.
   */
  function finish() {
    if (submitting) return;
    /*
     * ⚠️ A NOTE retry is not covered by `submitting` — it runs from the report
     * band, outside any save — and unmounting under it would leave the person
     * never learning whether the second attempt worked, with the only copy of
     * what they typed going with the form.
     */
    if (savingNote) return;
    /*
     * ⚠️ A CONVERTED invoice counts as "something was saved" even when the
     * follow-up PATCH was refused, and this is the branch that stops somebody
     * walking away from a real financial record without being taken to it. It
     * exists, its lines are on it, and the callers treat `onSaved` as "there is
     * a new record" — on the convert screens that is what navigates to it.
     */
    const savedRecord = created ?? convertedInvoice;
    if (savedRecord) onSaved(savedRecord);
    onClose();
  }

  /**
   * What already stands against this quote, named rather than asserted.
   *
   * ⚠️ **The server's own sentence is the thing being worked around here, so it
   * is NOT rendered on this branch.** `QUOTE_ALREADY_CONVERTED` is raised by a
   * guard that counts every invoice carrying this `quote_id` whatever raised it,
   * and then reports "has already been converted to invoice 13" — which on a
   * quote billed in parts through `POST /quotes/{id}/invoices` names an act
   * nobody performed, over a quote that is mostly still unbilled. Repeating that
   * beside a correction would leave the wrong conclusion on screen next to the
   * right one, and it is the expensive wrong conclusion: *someone already
   * converted this, so my work is done.*
   *
   * So this says only what a second request can actually establish — **how many
   * documents exist**, which is exactly the fact the message cannot express, and
   * where to go and look at them. It says they were **raised from** the quote,
   * never converted from it, because which endpoint raised them is not knowable
   * from here; and it never says "the invoice", because `quote_id` has no UNIQUE
   * constraint and staged billing legitimately produces several.
   *
   * ⚠️ **A failed lookup returns the server's sentence unchanged.** A request
   * that did not happen is not evidence about what exists, and neither an
   * invented certainty nor a swallowed refusal is acceptable — the refusal is
   * real either way and the reader still has to see it. An empty list takes the
   * same path: the server refused on something, and a client that found nothing
   * has no business contradicting it.
   */
  async function alreadyBilledRefusal(caught: ApiError, quoteId: number): Promise<ReactNode> {
    let raised: Invoice[];
    let total: number;
    try {
      const response = await api.listInvoices({ quoteId, perPage: 100 });
      raised = response.data;
      total = response.meta.total;
    } catch {
      return caught.message;
    }
    if (raised.length === 0) return caught.message;

    return (
      <>
        {/*
          ⚠️ "RAISED FROM", never "converted from", and never "the invoice".
          Nothing here knows what produced these — the server's guard counts
          them all alike — and one of them may be a partial bill rather than a
          conversion. The plural is real: `quote_id` carries no UNIQUE
          constraint and staged billing legitimately produces several.
        */}
        <span className="block font-semibold">
          {pluralise(total, 'invoice')} {total === 1 ? 'has' : 'have'} already been raised from this
          quote, so it can’t be converted.
        </span>
        {/*
          ⚠️ The part-billed case is the whole reason this sentence exists, and
          it has to come before the list — somebody who reads a link and stops
          reading must already have been told that what they are about to open
          may cover only some of the quote.
        */}
        <span className="mt-1 block font-normal">
          Converting bills a whole quote in one invoice, and that is refused once anything has been
          raised from it — by a conversion, by the quote having been accepted, or by billing
          individual line items. What exists may cover only part of this quote.
        </span>
        <ul className="mt-1 list-none space-y-0.5 p-0 font-normal">
          {raised.map((invoice) => (
            <li key={invoice.id} className="[overflow-wrap:anywhere]">
              <Link
                to={`/invoices/${invoice.id}`}
                className="rounded-sm underline underline-offset-2 hover:no-underline"
              >
                {invoice.subject.trim() || `Invoice #${invoice.id}`}
              </Link>
              {invoice.xeroInvoiceNumber && (
                <span className="tabular"> · {invoice.xeroInvoiceNumber}</span>
              )}
            </li>
          ))}
        </ul>
      </>
    );
  }

  /**
   * Why a conversion was refused, said the way the two 409s must be said.
   *
   * ⚠️ **Both are matched on `error.code`, never on the message**, and
   * `QUOTE_IS_STAGED` is checked FIRST. The two mean opposite things: this one
   * says the quote is billed a line at a time and convert never applies to it,
   * and `QUOTE_ALREADY_CONVERTED` says something has already been raised from
   * it. Rendering them alike tells somebody their staged quote produced an
   * invoice it never produced.
   *
   * ⚠️ **Everything else still falls through to the SERVER's own sentence**, and
   * that degradation is designed, not laziness: an unrecognised 409 means "newer
   * than this branch", never "not a refusal". Do not give an unknown code a
   * default destination or a generic sentence — the same rule `quoteRefusalOf()`
   * states in `@/lib/xero`. `QUOTE_ALREADY_CONVERTED` arriving at all is what
   * that fall-through was holding the place for; the message it carries is
   * unchanged from before the code existed, which is why nothing broke in
   * between.
   *
   * Async only because of that second branch, which reads
   * `GET /invoices?quoteId=` to name what exists. Every other branch resolves
   * without a request.
   */
  async function convertRefusal(caught: unknown, quoteId: number): Promise<ReactNode> {
    if (caught instanceof ApiError && caught.code === QUOTE_IS_STAGED_CODE) {
      return (
        <>
          <span className="block font-semibold">Staged quotes aren’t converted.</span>
          <span className="mt-1 block font-normal">{STAGED_CONVERT_REASON}</span>
          {/*
            The server's own sentence, kept rather than swallowed — an
            unrecognised reword must never be lost — but placed second and
            quieter, because it names an endpoint path.
          */}
          <span className="mt-1 block text-fg-muted [overflow-wrap:anywhere]">
            {caught.message}
          </span>
        </>
      );
    }
    if (caught instanceof ApiError && caught.code === QUOTE_ALREADY_CONVERTED_CODE) {
      return alreadyBilledRefusal(caught, quoteId);
    }
    /*
     * ⚠️ **THE COMPANY-APPROVAL GATE RUNS ON THIS PATH TOO** —
     * `quote-convert.ts` calls `assertCompanyApproved` on the QUOTE's company —
     * and it is matched HERE, above the bare-409 branch, for a reason beyond
     * documentation: without it a gate refusal would be indistinguishable from
     * any other 409 in this chain, and the branch order is what stops it being
     * read as an already-billed one. The three sentences are the server's, and
     * they are rendered exactly as the fall-through would render them.
     */
    if (caught instanceof ApiError && billingGateRefusalOf(caught) !== null) {
      return caught.message;
    }
    if (caught instanceof ApiError && caught.status === 409) {
      /*
       * Verbatim. That message carries the reference somebody needs in order to
       * go and look, and paraphrasing it loses exactly that.
       *
       * ⚠️ Still reached, and still the right answer: it now covers a 409 whose
       * code this branch does not recognise, which is a code newer than this
       * file rather than an error nobody named.
       */
      return caught.message;
    }
    if (caught instanceof ApiError && caught.status === 404) {
      return 'This quote no longer exists — someone else deleted it.';
    }
    if (caught instanceof ApiError && caught.status === 403) {
      /*
       * Convert takes a WRITE role, so this is NOT "administrators only" — it is
       * a role that changed under a live token, or a viewer. A 403 carries no
       * `details`, so it can only be a banner.
       */
      return (
        denialReason(ability, 'convert', 'quotes') ??
        'Your access changed since this page loaded, so nothing was converted.'
      );
    }
    return caught instanceof Error
      ? caught.message
      : 'Could not convert this quote to an invoice.';
  }

  /**
   * ── SAVE, on the convert path: two writes, and only the first is a convert ──
   *
   * ⚠️ **`POST /quotes/{id}/convert` takes NO fields** — its body schema is
   * `z.object({}).strict()` — so the header edits on this form cannot ride along
   * with it. The conversion runs first, copying every line item with its
   * project, cities and campaign inclusions in one server-side transaction; the
   * edits follow as `PATCH /invoices/{newId}`, bounded by
   * `CONVERT_FOLLOW_UP_PATCH_KEYS`. Nothing changed means no second request at
   * all, which is the ordinary case.
   *
   * ⚠️ **THERE IS NO TRANSACTION ACROSS THE TWO, AND NO ROLLBACK.** If the
   * convert lands and the PATCH is refused, the invoice EXISTS — real, with the
   * copied lines on it, and the quote already moved to its accepted stage.
   * Reporting that as a failed save is the specific harm: the person closes the
   * modal believing nothing happened and converts again, and a second convert
   * either 409s or, on a quote billed some other way, puts a second demand for
   * money in front of a customer. So the failure names the invoice, links to it,
   * and offers the PATCH again — never the conversion.
   */
  async function runConvert(quoteId: number) {
    submitLock.current = true;
    setSubmitting(true);
    setConvertError(null);
    setQuoteLineErrors({});

    /*
     * ⚠️ Already-converted means RETRY THE PATCH, never convert again. This is
     * the state after a refused follow-up, and the one thing that must not
     * happen from here is a second `POST /convert`.
     */
    let target = convertedInvoice;
    /**
     * The conversion's OWN Xero report, or `undefined` where no conversion ran
     * this time — which is the retry-the-PATCH path, where `target` is already
     * set and nothing new was created to push.
     *
     * ⚠️ **It describes the INVOICE, never the quote in the URL.** The server
     * calls `autoPushAndRefresh('invoices', invoice.id, …)` on the document the
     * conversion made, so this is about `data` — which is what makes
     * `conversionPushReport('invoices', …)` below the right entity.
     */
    let convertPushed: XeroPushReport | undefined;
    if (target === null) {
      /*
       * ⚠️ **CAPTURED ONCE AND HELD**, for the reason `runConvertDeal` captures
       * its `rowKeys`: a 422 names `lines.2.quantity`, and `2` is a position in
       * the array that WAS SENT. Reading the current one in the catch would work
       * today and would mark the wrong row the first time a keystroke landed
       * between the request and its refusal.
       */
      const sentOverrides = quoteOverrides ?? [];
      /*
       * ⚠️ **`{}` when nothing was edited or the quote's lines have not been
       * read, and NEVER `lines: []`.** An absent key is byte-for-byte the
       * conversion this endpoint has always performed — the property the
       * backend's own first test asserts — and an empty array is a different
       * instruction. This is not the clearing `[]` of `cityIds`.
       */
      const body: QuoteConvertInput =
        sentOverrides.length === 0 ? {} : { lines: sentOverrides };
      try {
        const raised = await api.convertQuote(quoteId, body);
        target = raised.data;
        convertPushed = raised.xeroPush;
      } catch (caught) {
        /*
         * ⚠️ **A per-line 422 is MARKED ON THE BOX as well as bannered.** Left as
         * a banner alone it names a path — `lines.2.discountAmount` — that no
         * reader can map onto a row, and the form refuses to convert while
         * pointing at nothing.
         *
         * ⚠️ **Routed BEFORE the general refusal is worded**, so a line fault is
         * never reported as one of the 409s that mean something else entirely.
         */
        if (caught instanceof ApiError) {
          const routed = quoteConvertLineFieldErrors(caught.fieldErrors, sentOverrides);
          setQuoteLineErrors(routed.rows);
          if (routed.unrouted.length > 0) {
            /*
             * Carries a refusal on `quoteProductId` — ours to get right, with no
             * control anywhere — and anything else this branch has not been
             * taught about.
             */
            setConvertError(routed.unrouted.join(' '));
          } else if (Object.keys(routed.rows).length > 0) {
            /*
             * A sentence pointing AT THE TABLE, because a 422's `message` is only
             * "Validation failed" and the marks are inside a grid that may be
             * scrolled off.
             */
            setConvertError('Some line items were refused — they are marked below.');
          } else {
            /*
             * ⚠️ AWAITED, and the form stays submitting until it resolves. One
             * refusal branch makes a second request (`GET /invoices?quoteId=`, to
             * name what has already been raised), and releasing the lock first
             * would leave Convert live over a quote the server has just refused.
             */
            setConvertError(await convertRefusal(caught, quoteId));
          }
        } else {
          setConvertError(await convertRefusal(caught, quoteId));
        }
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
    }

    /*
     * ⚠️ **The two DATES are a prediction here, not a promise, and an untouched
     * one is deliberately NOT sent.** `quote-to-invoice.ts` seeds them from the
     * VIEWER's local today; the server dates the invoice from its own. East of
     * Greenwich those are different days for the first hours of the morning, so
     * the bill date shown can be one ahead of the one stored. Untouched dates
     * are absent from this diff, so the SERVER's answer stands — the right way
     * round, and the reader lands on the invoice showing the real date. A date
     * somebody EDITS is a genuine change and is patched, both keys together.
     * Forcing the pair into every conversion to close the gap would make every
     * convert two writes, and a second write is a second thing that can fail.
     */
    /*
     * ⚠️ **PHASE TWO — the lines somebody ADDED on top of the copied ones.**
     *
     * The conversion has already copied the quote's own lines, server-side, in
     * the transaction that made the invoice. These are the EXTRA rows typed into
     * the draft grid below the read-only preview, and they are written the only
     * way they can be: one `POST /invoices/{id}/products` each, after the
     * invoice exists. There is no bulk endpoint and no transaction across them.
     *
     * ⚠️ **Before the header PATCH, deliberately**, mirroring the create path.
     * Every line write rewrites all four header totals server-side, so writing
     * lines after a PATCH that set a total by hand would silently discard that
     * total. Lines first means the header edit is the last word on the header.
     *
     * ⚠️ **Cleared the moment they are attempted, not when they succeed.** A
     * refused line is reported and NOT retried by the next Save — re-sending it
     * would duplicate the ones that landed, because there is no way to send only
     * the failures through a path that writes the whole array.
     */
    const outcome = draftLines.length > 0 ? await writeDraftLines(target.id, draftLines) : null;
    if (outcome !== null) {
      setDraftLines([]);
      setBlockedDraftRows(0);
    }
    const linesFailed = outcome !== null && outcome.failures.length > 0;

    /*
     * ⚠️ **THE LAST PUSH OF THE SEQUENCE, not the conversion's own**, and for the
     * reason `writeDraftLines` states about its own field: every line write
     * pushes again on the server, so the conversion's report describes a document
     * that was still being assembled — an invoice that gained two rows afterwards
     * would be reported on in its line-less form. The conversion's own report is
     * the fallback for the ordinary case, where nothing was added after it.
     */
    const pushAfterLines = conversionPushReport(
      'invoices',
      target.subject,
      outcome?.xeroPush ?? convertPushed,
    );

    const patch = buildConvertFollowUpPatch();
    if (Object.keys(patch).length === 0) {
      /*
       * Nothing to correct on the header. `onSaved` is what takes the caller to
       * the invoice — the response is an INVOICE, never a quote, and the two
       * differ in exactly the fields a quote screen reads.
       *
       * ⚠️ **Unless a LINE was refused, OR XERO DID NOT TAKE IT.** Closing here
       * would take the person to an invoice that silently lacks a row they typed,
       * or that Xero has not got, with the only report of either destroyed by the
       * navigation. The invoice is real in both cases and the conversion is done —
       * so the modal STAYS OPEN to say so, and `convertedInvoice` makes sure the
       * next Save re-sends the header rather than converting a second time.
       *
       * ⚠️ **Both facts, never whichever happened last.** A refused line and a
       * refused push are two requests with two reasons and two different next
       * moves, and the bands below render independently for exactly that reason.
       */
      if (linesFailed || pushAfterLines !== null) {
        setConvertedInvoice(target);
        setLineOutcome(outcome);
        setConvertPush(pushAfterLines);
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      submitLock.current = false;
      onSaved(target);
      onClose();
      return;
    }

    try {
      const after = await api.updateInvoice(target.id, patch);
      // The same report the ordinary edit path carries — this is a PATCH like any other, and it pushed like any other.
      onXeroPush?.(after.xeroPush);
      /*
       * ⚠️ **The HEADER PATCH's report wins, because it is the last write.** It
       * pushed too, so it describes the invoice as it finally stands — where the
       * line reports and the conversion's own describe earlier versions of it.
       */
      const pushReport = conversionPushReport(
        'invoices',
        after.data.subject,
        after.xeroPush ?? outcome?.xeroPush ?? convertPushed,
      );
      /*
       * ⚠️ Same rule as above, one step later: the header landed, and a refused
       * line — or a refused push — still must not be reported by closing the
       * form. "Everything saved" and "everything except one line saved" must
       * never look alike, and neither must "everything saved" and "saved, and
       * Xero has not got it".
       */
      if (linesFailed || pushReport !== null) {
        setConvertedInvoice(after.data);
        setLineOutcome(outcome);
        setConvertPush(pushReport);
        setValues(toFormValues(after.data));
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      onSaved(after.data);
      onClose();
    } catch (caught) {
      /*
       * ⚠️ Recorded BEFORE anything is rendered, because it is what turns the
       * form into "the invoice exists, its edits did not land" rather than
       * leaving Convert live over a quote that has already been converted.
       */
      setConvertedInvoice(target);
      /*
       * ⚠️ **Both facts, never one.** The header was refused AND a line may have
       * been too; they are separate requests with separate reasons, and a reader
       * who fixes the marked field and saves again has to know the line is not
       * coming back with it. `convertHeaderRefused` is what keeps the two blocks
       * from claiming each other's cause.
       */
      setConvertHeaderRefused(true);
      if (outcome !== null) setLineOutcome(outcome);
      // The approval gate first: its 409 is a banner and never a field.
      if (!bannerBillingGate(caught)) {
        if (caught instanceof ApiError) {
          /*
           * Split against this form's own field list, exactly as the create and
           * edit paths do: `fields` may be marked and counted, and everything a
           * control here cannot carry is bannered rather than silently dropped.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        } else {
          setFormError('Could not reach the server. Check your connection and try again.');
        }
      }
      setSubmitting(false);
    } finally {
      submitLock.current = false;
    }
  }

  /**
   * The company-approval gate's refusal, bannered — `true` when it was one and
   * has been reported, so the caller returns without routing anything.
   *
   * ── WHY THIS EXISTS AS A FUNCTION ─────────────────────────────────────────
   *
   * `assertCompanyApproved` (migration 099) refuses `createInvoice`, both deal
   * conversions and the quote conversion against a company that is `pending`,
   * `rejected`, or carrying a status the server cannot read. Three codes, read
   * through `billingGateRefusalOf`.
   *
   * ⚠️ **The server's sentence, and nothing of ours in front of it.** It names
   * the company, says an approver has been emailed a link, and says everything
   * else on that company still works — all three of which this form would have
   * to invent, and two of which it cannot know. *SUPERSEDED 2026-08-31: two
   * sentences of our own, written on the stated premise that the server did not
   * enforce this.*
   *
   * ⚠️ **FORM LEVEL, never `serverErrors.companyId`.** The 409 carries no
   * `details` on any call site, deliberately: half of them reach the gate with a
   * company the caller never sent. Keying it to a field would make `fieldErrors`
   * non-empty and produce "one field needs attention, highlighted below" with
   * nothing highlighted.
   *
   * ⚠️ **Explicit rather than left to the fall-through.** A details-less 409
   * already lands on `setFormError(caught.message)` by way of an empty `fields`
   * and an empty `unroutable` — correct today, but correct by accident of the
   * envelope's shape. The day the server attaches a `path` to this refusal, the
   * fall-through would silently start routing it to a control.
   *
   * ⚠️ **Called from every ApiError handler here, including the two POST-CONVERT
   * PATCH paths where it cannot fire today.** `updateInvoice` is on the guard's
   * own RECOMMENDED-but-not-gated list — a re-parent onto a pending company is
   * the same act by another route — so the one line is what stops those two
   * handlers from being the pair nobody updated if that lands.
   *
   * An unrecognised code returns `false` and the caller falls through to the
   * server's own message anyway: newer than this branch, not "not a refusal".
   */
  function bannerBillingGate(caught: unknown): boolean {
    if (!(caught instanceof ApiError)) return false;
    const gate = billingGateRefusalOf(caught);
    if (gate === null) return false;
    setServerErrors({});
    setFormError(gate.message);
    return true;
  }

  /**
   * Why a DEAL conversion was refused, as a block rather than a sentence.
   *
   * ⚠️ **`COMPANY_HAS_NO_TAX_RATE` gets the server's TWO sentences and a LINK,
   * never a retry.** The identical request can only produce the identical
   * refusal until somebody edits the company, so a retry button here is the
   * affordance-with-one-outcome this codebase forbids. The link is built from
   * the DEAL's own company — the refusal also fires when the deal names none,
   * and the id is not in the prose, so parsing a company out of the sentence
   * would be matching on wording by another name.
   *
   * ⚠️ **Nothing was created when it fires.** The check runs before the
   * transaction opens, so the form keeps everything typed and stays open — the
   * opposite of what "your save failed" would lead somebody to do.
   *
   * ⚠️ **Ordered by SPECIFICITY, never by which shape arrived first**, and with
   * deliberately no default destination: an unrecognised code means "newer than
   * this branch", not "not a refusal", so the last branch is the server's own
   * message with nothing of ours in front of it.
   */
  function dealConvertFailure(caught: unknown): ReactNode {
    const taxRate = companyTaxRateRefusalOf(caught);
    if (taxRate) {
      return (
        <div className="grid gap-1">
          <p className="m-0">{taxRate.message}</p>
          {taxRate.action && <p className="m-0">{taxRate.action}</p>}
          {/* Underlined and inheriting the banner's colour rather than
              `accent-brand`, which would be a brand blue on a danger-soft
              ground. The underline is the affordance, so colour is not the only
              signal. */}
          {convertDeal?.company && (
            <p className="m-0">
              <Link
                to={`/companies/${convertDeal.company.id}`}
                className="rounded-sm font-semibold underline transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
              >
                Open {convertDeal.company.name}
              </Link>
            </p>
          )}
        </div>
      );
    }

    if (caught instanceof ApiError) {
      if (caught.status === 403) {
        /*
         * A write role, so this is NOT "administrators only" — it is a role
         * that changed under a live token, or a viewer. A 403 carries no
         * `details`, so it can only ever be a banner.
         */
        return (
          denialReason(ability, 'create', 'invoices') ??
          'Your access changed since this page loaded, so nothing was created.'
        );
      }
      /*
       * ⚠️ **THE COMPANY-APPROVAL GATE RUNS ON THIS PATH TOO.**
       * `deal-convert.ts` calls `assertCompanyApproved` on the DEAL's company
       * before it opens the transaction, so all three billing-gate codes arrive
       * here. Matched rather than left to the `formMessage ?? message` below —
       * which renders them identically today — so this cannot start swallowing
       * them the day the server attaches `details`, and so the reason it is a
       * banner is written down.
       *
       * ⚠️ **A deal naming NO company is skipped by the guard, not refused.**
       * The owner's ruling and the backend's behaviour agreeing; nothing here
       * blocks a company-less conversion.
       */
      const gate = billingGateRefusalOf(caught);
      if (gate !== null) return gate.message;
      /*
       * On a 422 `message` is only ever "Validation failed", so the detail
       * bound to a path is the useful half — `formMessage` reads it.
       */
      return caught.formMessage ?? caught.message;
    }
    return 'Could not reach the server, so nothing was created.';
  }

  /**
   * ── SAVE, converting a DEAL: two writes, and only the first is a convert ───
   *
   * ⚠️ **`POST /deals/{id}/invoices` takes NO HEADER fields**, so the header
   * edits on this form cannot ride along with it. The conversion runs FIRST,
   * copying every line with its project, cities, campaign inclusions and region
   * set, freezing the resolved account lead and seeding each line's tax rate
   * from the company, in one server-side transaction; the edits follow as
   * `PATCH /invoices/{newId}`, bounded by `CONVERT_FOLLOW_UP_PATCH_KEYS`.
   *
   * The one key that DOES ride along is **`lineSet` — the COMPLETE FINAL LIST of
   * lines**, in the order the grid displays them.
   *
   * ⚠️ **`lineSet` REPLACED `lines` HERE, and the two are OPPOSITE instructions
   * about the rows nobody named.** An entry absent from `lines` was still copied;
   * an entry absent from `lineSet` **does not appear on the invoice**. That is
   * what makes deleting a row on this screen work at all, and it is why nothing
   * may ever send both — `raiseDealInvoiceSchema` refuses the pair outright
   * (`refuseBothLineKeys`), so a body carrying both is a 422 naming `lineSet`.
   *
   * ⚠️ **OMITTED ENTIRELY when the grid reports itself PRISTINE.** An absent key
   * is the behaviour every conversion has had until now — provably today's — and
   * `lineSet: []` is an invoice with NO LINES, a real instruction and emphatically
   * not "no changes". `pristine` is read off the BUILT entries plus the deal's own
   * length, so a grid holding two untouched rows out of three is not pristine and
   * the deleted line stays deleted.
   *
   * ⚠️ **THERE IS NO TRANSACTION ACROSS THE TWO, AND NO ROLLBACK. THESE ARE TWO
   * REQUESTS.** If the convert lands and the PATCH is refused, the invoice
   * EXISTS — real, demanding money, with the deal's line items copied onto it —
   * carrying the DEAL'S ORIGINAL HEADER and none of the corrections made above.
   * Reporting that as a failed save is the specific harm: the person closes the
   * modal believing nothing happened and converts again, and
   * `POST /deals/{id}/invoices` answers **no 409**, so the second attempt
   * succeeds and the customer has two demands for the same work.
   *
   * So the failure is SURFACED rather than swallowed: it names the invoice,
   * links to it, and offers the PATCH again — never the conversion.
   */
  async function runConvertDeal(dealId: number) {
    submitLock.current = true;
    setSubmitting(true);
    setConvertError(null);
    setLineServerErrors({});

    /*
     * ⚠️ Already-converted means RETRY THE PATCH, never convert again. This is
     * the state after a refused follow-up, and the one thing that must not
     * happen from here is a second `POST /deals/{id}/invoices`.
     */
    let target = convertedInvoice;
    /**
     * The raise's OWN Xero report, or `undefined` on the retry-the-PATCH path
     * where nothing new was created.
     *
     * ⚠️ **It is about the INVOICE this created, not the deal in the URL.**
     * `deals.controller` calls `autoPushAndRefresh('invoices', invoice.id, …)`, so
     * the report describes `data`. (Its sibling `POST /deals/{id}/convert` makes a
     * QUOTE and reports on that — same rule, different entity, and it is not this
     * form's call.)
     */
    let convertPushed: XeroPushReport | undefined;
    if (target === null) {
      /*
       * ⚠️ **CAPTURED ONCE AND HELD, and `sentRowKeys` is the whole reason.** A
       * 422 names `lineSet.2.productId`, and `2` is a position in the array that
       * WAS SENT — so the `rowKeys` array the grid emitted beside those entries is
       * the only thing that can turn it back into a row. Reading the CURRENT one
       * in the catch would work today and would mark the wrong row the first time
       * a keystroke landed between the request and its refusal, with a
       * 200-shaped confidence and a red box on a figure nobody touched.
       */
      const sent = convertLineSet;
      const sentRowKeys = sent?.rowKeys ?? [];
      /*
       * ⚠️ **`{}` when the grid is pristine or has not read the deal's lines
       * yet**, and never `lineSet: []`. See this function's header: an empty array
       * is an invoice with no lines, which is the opposite of "nothing changed".
       */
      const body: DealConvertInput =
        sent === null || sent.pristine ? {} : { lineSet: sent.entries };
      try {
        const raised = await api.raiseInvoiceForDeal(dealId, body);
        target = raised.data;
        convertPushed = raised.xeroPush;
      } catch (caught) {
        /*
         * ⚠️ A per-line 422 is MARKED ON THE BOX as well as bannered. Left as a
         * banner alone it names a path — `lineSet.2.discountAmount` — that no
         * reader can map onto a row, and the form refuses to convert while
         * pointing at nothing.
         *
         * ⚠️ **The line refusal is routed BEFORE the tax one is worded**, which
         * matches the server: an entry naming a line that is not on the deal is
         * checked before the company tax rate, so the two can never arrive
         * together and the order here cannot hide either.
         */
        if (caught instanceof ApiError) {
          const routed = convertLineSetFieldErrors(caught.fieldErrors, sentRowKeys);
          setLineServerErrors(routed.rows);
          /*
           * `unrouted` first: it carries a refusal on `dealLineId` or `sortOrder`
           * — both ours to get right, with no control anywhere — and anything else
           * this branch has not been taught about. Then, when rows WERE marked, a
           * sentence pointing AT THE TABLE, because a 422's `message` is only
           * "Validation failed" and the marks are inside a table that may be
           * scrolled off. Otherwise the full refusal block.
           */
          if (routed.unrouted.length > 0) setConvertError(routed.unrouted.join(' '));
          else if (Object.keys(routed.rows).length > 0) {
            setConvertError('Some line items were refused — they are marked below.');
          } else setConvertError(dealConvertFailure(caught));
        } else {
          setConvertError(dealConvertFailure(caught));
        }
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
    }

    /*
     * ⚠️ **The two DATES are a prediction here, not a promise, and an untouched
     * one is deliberately NOT sent.** `invoiceBaselineFromDeal` builds them from
     * the VIEWER's local today; the server dates the invoice from its own. East
     * of Greenwich those are different days for the first hours of the morning,
     * so the bill date shown can be one ahead of the one stored. Untouched dates
     * are absent from this diff, so the SERVER's answer stands — the right way
     * round. A date somebody EDITS is a genuine change and is patched, both keys
     * together, because the `billDate > dueDate` check runs only when the server
     * sees both.
     */
    const patch = buildConvertFollowUpPatch();
    if (Object.keys(patch).length === 0) {
      /*
       * Nothing to correct. `onSaved` is what takes the caller to the invoice —
       * the response is an INVOICE, never a deal, and the two differ down to the
       * date fields.
       *
       * ⚠️ **Unless Xero would not take it.** The invoice exists and demands
       * money either way; navigating would destroy the only report anywhere that
       * Xero has not got it — the server does not re-read the record on a failed
       * push, so no link row is written and the invoice's own page cannot tell
       * that state from one nobody has pushed yet. `convertedInvoice` is set for
       * the reason it always is: from here the next Save must re-send the header,
       * never raise a second invoice, and `POST /deals/{id}/invoices` answers no
       * 409 to stop one.
       */
      const pushReport = conversionPushReport('invoices', target.subject, convertPushed);
      if (pushReport !== null) {
        setConvertedInvoice(target);
        setConvertPush(pushReport);
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      submitLock.current = false;
      onSaved(target);
      onClose();
      return;
    }

    try {
      const after = await api.updateInvoice(target.id, patch);
      // The same report the ordinary edit path carries — this is a PATCH like
      // any other, and it pushed like any other.
      onXeroPush?.(after.xeroPush);
      /*
       * ⚠️ The HEADER PATCH's report wins where there is one: it is the last
       * write, so it describes the invoice as it finally stands, where the raise's
       * own describes it before the corrections above were applied.
       */
      const pushReport = conversionPushReport(
        'invoices',
        after.data.subject,
        after.xeroPush ?? convertPushed,
      );
      if (pushReport !== null) {
        setConvertedInvoice(after.data);
        setConvertPush(pushReport);
        setValues(toFormValues(after.data));
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      onSaved(after.data);
      onClose();
    } catch (caught) {
      /*
       * ⚠️ Recorded BEFORE anything is rendered, because it is what turns the
       * form into "the invoice exists, its edits did not land" rather than
       * leaving Convert live over a deal that has already been invoiced.
       */
      setConvertedInvoice(target);
      /*
       * ⚠️ On the DEAL route this is the only way `convertedInvoice` is ever
       * set, so the flag is redundant here TODAY — and it is set anyway, because
       * the banner now reads the flag rather than the record. Leaving it unset
       * on one of the two routes would make that banner silently drop its reason
       * on deal conversions, which is the half nobody would run first.
       */
      setConvertHeaderRefused(true);
      // The approval gate first: its 409 is a banner and never a field.
      if (!bannerBillingGate(caught)) {
        if (caught instanceof ApiError) {
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        } else {
          setFormError('Could not reach the server. Check your connection and try again.');
        }
      }
      setSubmitting(false);
    } finally {
      submitLock.current = false;
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    /*
     * Contained, per the house rule on nested modals: React propagates by its
     * own tree rather than the DOM one, so a form rendered inside another
     * form's subtree submits both.
     */
    event.stopPropagation();

    // ⚠️ Synchronous, before any await. See `submitLock`.
    if (submitLock.current) return;

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    if (convertQuote !== undefined) {
      await runConvert(convertQuote.id);
      return;
    }

    /*
     * ⚠️ AFTER `validate`, so a date pair the person inverted is refused HERE,
     * on a form where nothing exists yet, rather than as a 422 against an
     * invoice the conversion has already created.
     */
    if (convertDeal !== undefined) {
      await runConvertDeal(convertDeal.id);
      return;
    }

    const payload = buildPayload();
    /*
     * ⚠️ **"Nothing has changed" IS A CLAIM ABOUT THE WHOLE FORM, so it may not
     * be made from the header payload alone.** An edit that touched only line
     * items builds an empty payload and is not an empty edit; saying otherwise
     * here is the same falsehood the disabled button used to tell, one layer
     * down, and it would survive every gate above it.
     */
    /*
     * ⚠️ **`undefined`, not `null`** — `invoice` here is `created ?? initialInvoice`
     * and `initialInvoice` is an OPTIONAL prop, so this union has no null member
     * at all. The quote twin's is nullable and reads `quote !== null`; copying
     * that spelling across would be true on a CREATE, where there is no grid to
     * save and `saveLinesRef` is null — the guard would pass and save nothing.
     */
    const linesToSave = invoice !== undefined && lineEdits.dirty > 0;
    if (Object.keys(payload).length === 0 && !linesToSave) {
      setFormError('Nothing has changed yet.');
      return;
    }

    submitLock.current = true;
    setSubmitting(true);
    /*
     * ⚠️ Hoisted OUT of the `try` on purpose: it is how the catch below can
     * tell "the create failed" from "the create succeeded and something after
     * it did not". Reporting the second as a failed create would leave the form
     * still offering "Create invoice" over an invoice that already exists, and
     * the next click would make a second one.
     */
    let createdInvoice: Invoice | null = null;
    try {
      if (invoice) {
        /*
         * ⚠️ **LINES FIRST, HEADER SECOND, and the order is arithmetic rather
         * than taste.** Every line write rewrites the invoice's four header
         * totals inside its own transaction; an invoice PATCH writes exactly
         * what it is given and never re-derives. Header-first would therefore
         * let a line save overwrite figures the person had just typed, with a
         * 200 and nothing on screen to say so.
         *
         * ⚠️ **The grid still reports its own run, row by row, and this must
         * not restate it.** What is added here is only the ONE thing the grid
         * cannot know: whether the form may close.
         */
        const linesLanded = linesToSave ? ((await saveLinesRef.current?.()) ?? false) : true;
        if (Object.keys(payload).length === 0) {
          /*
           * Line items only. `onSaved` is the callers' "reload and close"
           * signal and they re-read the record, so handing up the header as it
           * stands is right — the totals it carries were rewritten by the
           * writes that just ran, and this form is not the place they are read
           * back from.
           */
          if (!linesLanded) {
            /*
             * ⚠️ **`setSubmitting(false)` BEFORE EVERY RETURN THAT DOES NOT
             * CLOSE.** It is not reset in a `finally` here — only the `catch`
             * and the create branch clear it — because until now every
             * non-throwing path ended in `onClose()`, where a stuck flag is
             * invisible. These two returns are the first that report and stay,
             * and a stuck `submitting` disables Cancel/Done (`finish()` refuses
             * outright) and puts Save into `loading`, which `Button` renders as
             * disabled. That is the whole form frozen behind a message telling
             * somebody to go and fix a row — the exact dead-control defect this
             * change exists to remove, reintroduced one screen further in.
             */
            setSubmitting(false);
            setFormError(LINES_NOT_SAVED);
            return;
          }
          onSaved(invoice);
          onClose();
          return;
        }
        const result = await api.updateInvoice(invoice.id, payload);
        /*
         * ⚠️ The PATCH has already been to Xero — the server pushes inside the
         * save — so this reports a second act that is already finished, and it
         * cannot fail, throw or unmake the save. Nothing about it may be worded
         * as the invoice not having been stored: this line has already proved
         * it was.
         */
        onXeroPush?.(result.xeroPush);
        /*
         * ⚠️ **A REFUSED LINE MUST NOT CLOSE A FORM THAT SAVED THE HEADER — and
         * `onSaved` IS a close.** The callers read it as "reload and close", so
         * calling it here would unmount this form and take the grid's per-row
         * report with it: the person would be back at the list, told nothing,
         * with the header saved and their line edits gone from the screen.
         *
         * `setCreated` instead. It is what `refreshInvoice` and
         * `refreshAfterServerPush` already do with a re-read of an existing
         * invoice, so it is the established way to hold a fresher copy — and
         * `finish()` hands `created` to `onSaved` when the person does close,
         * so the list still reloads. Nothing is lost; the signal is deferred to
         * the moment they are actually finished.
         */
        if (!linesLanded) {
          setCreated(result.data);
          // See the note on the lines-only return above — this one stays open too.
          setSubmitting(false);
          setFormError(HEADER_SAVED_LINES_NOT);
          return;
        }
        onSaved(result.data);
        onClose();
      } else {
        /*
         * ⚠️ A CREATE DOES NOT CLOSE THE FORM, and does not call `onSaved`.
         *
         * A line item is `POST /invoices/{id}/products` — nested, no bulk
         * endpoint — so no line can exist until the invoice does. What happens
         * instead: the form becomes an EDIT of the record that just came back.
         * `created` feeds `invoice`, so the baseline, the diff and the PATCH all
         * follow it without a second code path — and the grid below flips from
         * draft mode to saved mode against a real id.
         */
        const result = await api.createInvoice(payload as InvoiceCreateInput);
        createdInvoice = result.data;

        /*
         * ⚠️ PHASE TWO, and it runs BEFORE `setCreated`.
         *
         * The order is deliberate. `setCreated` flips the grid below into saved
         * mode, and saved mode's first act is to list the invoice's lines —
         * which, at this instant, is an empty list. Writing the lines first
         * means the one read it does is the read that already has them, instead
         * of an empty grid that fills in afterwards and looks for a moment as
         * though the drafts were thrown away.
         */
        const outcome =
          draftLines.length > 0 ? await writeDraftLines(result.data.id, draftLines) : null;

        /*
         * ⚠️ PHASE TWO ALSO — a third request, and a third separate fact.
         *
         * A note is `POST /notes` with an `invoiceId`, so like a line it cannot
         * exist before the invoice does. It is written here, after the lines and
         * before the re-read, so everything the person pressed Save for has been
         * attempted by the time the form reports on it.
         *
         * ⚠️ It cannot fail this create. `writeDraftNote` catches everything and
         * returns the reason — letting a rejection escape would land in the
         * catch below, which reports a FAILED create over an invoice that
         * plainly exists and **cannot be un-created**, and the next click would
         * raise a second one. An empty box returns `null` having sent nothing:
         * `body: ''` is a 422, and "no note was typed" must not be reported as
         * "a note was refused".
         */
        const noteResult = await writeDraftNote({ kind: 'invoice', id: result.data.id }, noteDraft);

        /*
         * ⚠️ Re-read when anything landed, and this is what makes the record
         * handed back to `onSaved` the SERVER's figures rather than a local sum.
         * Every line write rewrites all four header totals in the same
         * transaction and returns none of them, so `result.data` is stale in
         * exactly the four places that reach a Xero document. Silent on failure,
         * like `refreshInvoice`.
         */
        let saved = result.data;
        if (outcome !== null && outcome.saved > 0) {
          try {
            saved = (await api.getInvoice(saved.id)).data;
          } catch {
            /* a stale header beats an error about a refresh */
          }
        }

        setCreated(saved);
        setJustCreated(true);
        setLineOutcome(outcome);
        setNoteOutcome(noteResult);
        /*
         * ⚠️ Cleared ONLY when it actually landed. A refused note keeps its text
         * on screen, because that text is the only copy of it anywhere — the
         * request failed, so nothing was stored — and clearing the box to tidy
         * up would destroy what the person wrote while telling them to try
         * again.
         */
        if (noteResult?.kind === 'saved') setNoteDraft('');
        /*
         * The drafts are no longer drafts. Whatever landed is now the grid's
         * own saved state, and whatever did not is named by position in the
         * report — holding on to the array would write it twice on the next
         * save.
         */
        setDraftLines([]);
        setBlockedDraftRows(0);
        /*
         * Re-seeded from the RECORD, not left as typed: the server normalises,
         * and a baseline that disagreed with the values would light up Save
         * over changes nobody made.
         */
        setValues(toFormValues(saved));
        setSubmitting(false);

        /*
         * ⚠️ PHASE THREE, and it is no longer a request.
         *
         * ⚠️ **CORRECTED 2026-08-31.** It read: *"it is fired rather than
         * awaited … a push that takes a second must not hold back the news that
         * the invoice exists. The push reports itself, separately and
         * alongside them, into `xeroPush`."* There is no push here now — the
         * server made it inside the writes above and its answer is already in
         * hand, so the report is set synchronously and there is nothing to
         * outrun.
         *
         * ⚠️ **The LAST line write's report, falling back to the create's.**
         * The sentence that justified the old ordering — *"`writeDraftLines`
         * has finished, so Xero is sent an invoice WITH its lines rather than
         * an empty one"* — is still exactly why: the earlier reports describe a
         * document still being assembled. The create's own report is the
         * fallback for an invoice created with no lines, where it is the only
         * one there is (and answers `skipped`, which is ordinary).
         */
        const pushReport = serverPushReport(
          saved.subject,
          saved.company?.name ?? null,
          outcome?.xeroPush ?? result.xeroPush,
        );
        if (pushReport !== null) setXeroPush({ kind: 'settled', report: pushReport });
        /*
         * Fired, not awaited, for the reason the old push was: the created
         * banner and the line report must paint on this render. This only
         * re-reads a header the server has already changed.
         */
        void refreshAfterServerPush(saved);
      }
    } catch (caught) {
      /*
       * ── THE COMPANY-APPROVAL GATE, FIRST ───────────────────────────────────
       *
       * This is the CREATE path, so it is where `assertCompanyApproved` actually
       * fires on an ordinary save. Its 409 is a banner and never a field; see
       * `bannerBillingGate` for why that is explicit rather than left to the
       * routing below, which would render it identically today.
       */
      if (!bannerBillingGate(caught)) {
        if (caught instanceof ApiError) {
          /*
           * ⚠️ There is no `xeroInvoiceNumber` collision handler here — the
           * number is Xero's, this form never sends it, and no collision on it
           * can be attributed to anything a person typed. A 409 naming it falls
           * through to the general path below and speaks in the server's own
           * words. A handler for a refusal that cannot arrive would rot
           * unnoticed and eventually fire on some unrelated 409, marking a field
           * nobody touched.
           *
           * Split against this form's OWN field list, never against the shape of
           * the path. `fields` is safe to mark AND to count; `unroutable` is
           * everything no input here can carry — the EMPTY-path `.strict()` and
           * "at least one field" refusals, plus any path naming a key with no
           * control on this form. Rendering it at form level is the only way it
           * is seen at all, and it is never counted as highlighted.
           *
           * ⚠️ A 422 naming `xeroInvoiceNumber` or `xeroInvoiceId` now lands in
           * `unroutable` and is BANNERED rather than silently dropped — which is
           * how a regression that started sending either key again would
           * announce itself instead of highlighting nothing.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        } else {
          setFormError('Could not reach the server. Check your connection and try again.');
        }
      }
      /*
       * ⚠️ The invoice may exist even though we are in the failure path — see
       * the note on `createdInvoice`. Switching to edit mode is what stops the
       * next click creating a second one, and it is the truth: a record was
       * made, and this form is now editing it. The error above still stands.
       */
      if (createdInvoice !== null && created === null) {
        setCreated(createdInvoice);
        setJustCreated(true);
        setValues(toFormValues(createdInvoice));
        setDraftLines([]);
        setBlockedDraftRows(0);
      }
      setSubmitting(false);
    } finally {
      /*
       * Released on every path, including the successful create. By then
       * `created` is set, so the form is an EDIT of a real record and the next
       * submit can only PATCH it — the one thing the lock exists to prevent, a
       * second `POST /invoices`, is unreachable from here on.
       */
      submitLock.current = false;
    }
  }

  const statusOptions = useMemo(
    () => selectableStatuses(statuses, invoice?.status ?? null),
    [statuses, invoice],
  );

  /*
   * ⚠️ **`currencyChoices` WENT WITH THE CURRENCY PICKER — 2026-08-31.** Its
   * whole job was pinning a code the `/currencies` master no longer offers back
   * into the dropdown, from four sources: the record, a converted quote, a
   * converted deal and a prefill.
   *
   * ⚠️ **The failure it existed to prevent CANNOT ARISE any more, and this is
   * the one place where deleting a guard is safe rather than merely tidy.** It
   * read: *"a quote priced in a code the master no longer offers would render
   * blank, reading as 'no currency' over a form that is carrying it — and the
   * reader would then set one, changing the currency of a document the customer
   * already agreed to."* Both halves are now unreachable. A read-only row prints
   * `values.currency` verbatim and never looks it up, so a retired code renders
   * as itself rather than as blank; and there is nothing for a reader to set.
   *
   * ⚠️ **This is exactly the reasoning that must be repeated, not inherited, if a
   * picker ever comes back here.** A picker resolving a code against a fetched
   * active list reintroduces both halves at once — and 161 of the 164 currency
   * rows are retired as of 2026-08-31, so it is not a rare edge any more.
   */

  /*
   * ── The base currency, and what may be converted into it ─────────────────
   *
   * Xero states a rate the way an accountant reads one: `1 GBP = 1.89207 AUD`,
   * base first. So the two codes are not interchangeable and the pair has to be
   * carried together — which is what `ExchangeRate` is for, and why nothing
   * here passes a bare rate string around.
   *
   * ⚠️ **The base currency comes from the TENANT and is never assumed.** It is
   * `GBP` on this organisation today; hardcoding that would print a GBP figure
   * against a Xero organisation that files in something else, which is a wrong
   * number on a financial screen rather than a wrong label. A disconnected Xero
   * therefore means the base currency is genuinely UNKNOWN — a real state with
   * its own sentence below, not a reason to fall back to a guess.
   */
  const documentCurrency = values.currency.trim().toUpperCase() || null;
  const baseCurrency = xeroConnected ? xero.connection.baseCurrency : null;

  /**
   * A usable rate, or null — and null covers five quite different situations.
   *
   * Every one of them means the same thing to a RENDERER (there is no converted
   * figure to show), which is why they collapse here; they mean five different
   * things to a READER, which is why `rateState` below keeps them apart. Do not
   * merge the two.
   */
  const exchangeRate = useMemo<ExchangeRate | null>(
    () =>
      /*
       * ⚠️ `exchangeRateOf` and not a hand-rolled check here. Which
       * combinations count as "a real conversion" — same code both sides, an
       * unparseable rate, a rate of zero — is one rule, and this form is one
       * more consumer of it. A second copy would agree until one of them was
       * edited, and the half that drifts is always the one nobody is looking
       * at.
       */
      exchangeRateOf({
        baseCurrency,
        documentCurrency,
        currencyRate: values.currencyRate,
      }),
    [documentCurrency, baseCurrency, values.currencyRate],
  );

  /**
   * Which sentence the reader gets under the Currency row.
   *
   * ⚠️ **These are not variants of one message.** "Xero is not connected" and
   * "no rate is recorded" send someone to two different screens, and "this
   * invoice is already in the base currency" is not a gap at all. Collapsing
   * any two of them into "no conversion available" would be the one thing that
   * reads as a fault in the case where nothing is wrong.
   */
  const rateState: 'no-currency' | 'checking' | 'base-unknown' | 'same' | 'unset' | 'set' =
    documentCurrency === null
      ? 'no-currency'
      : xero.loading
        ? 'checking'
        : baseCurrency === null
          ? 'base-unknown'
          : documentCurrency === baseCurrency
            ? 'same'
            : values.currencyRate.trim() === ''
              ? 'unset'
              : 'set';

  /*
   * The project this invoice is ALREADY stored against, read straight off the
   * record. Not a selection — there is nothing to select — and deliberately not
   * mirrored into `values`, which is what keeps it out of every payload.
   *
   * ⚠️ `null` here does not prove the invoice has no project: the lookup the
   * revenue modules run swallows its own failures. Nothing below asserts it has
   * none.
   *
   * ⚠️ Only the NAME survives — `headerProjectId` went with the derived-tracking
   * readout that resolved it against the fetched project list.
   */
  /**
   * Hoisted rather than read inline in the JSX, so the narrowing survives the
   * render callback the `Field` primitive takes — same shape as
   * `QuoteFormModal`'s, for the same reason.
   */
  const headerProjectName = invoice?.project?.name ?? null;

  /*
   * 🔴 **THE DERIVED-TRACKING READOUT IS GONE — 2026-08-31.** It resolved the
   * header project to a full record and turned its region and intake into the
   * placeholders under the Location / Tour Type override boxes. With those boxes
   * removed there is nothing left to describe, so `headerProject`,
   * `headerProjectId`, `derivedLocation`, `derivedTourType` and the whole
   * `useProjectOptions` fetch behind them went with it.
   *
   * ⚠️ **The DERIVATION ITSELF is untouched and still runs.** It is server-side
   * and per line — `resolveLineTracking` prefers the LINE's project and falls
   * back to the document's — and a stored `null` on either column still means
   * *derive*, exactly as it did. What was removed is this form's readout of it
   * and its ability to override it, not the behaviour.
   *
   * ⚠️ **`headerProjectName` above survives and is NOT part of this.** It comes
   * straight off `invoice.project.name` and feeds the read-only Project box; it
   * never needed the fetched list.
   */

  /*
   * The stored theme is injected when the live list no longer carries it — an
   * accountant can rename or delete a branding theme long after an invoice was
   * filed against it, and a control that renders blank reads as "use the
   * default" and is one save away from silently reprinting a customer's
   * document on the wrong letterhead. We cache no name for it, so the id is
   * the label; `brandingThemeOptions` says so.
   */
  /*
   * ⚠️ A SEEDED id needs exactly the same injection as a stored one. On a
   * create there is no record, so `invoice?.xeroBrandingThemeId` is null — and
   * a company default the live list does not carry would leave the control
   * rendering blank, which reads as "use the organisation default" and is one
   * save away from the silent letterhead change this injection exists to stop.
   * It is offered only while it is still what the field holds; once someone
   * picks something else it is an id nothing refers to.
   */
  const storedThemeId =
    invoice?.xeroBrandingThemeId ??
    /*
     * ⚠️ A CONVERTED quote's letterhead is a stored value too — it is on the
     * quote and `convert` copies it — so it is pinned back exactly as a stored
     * one is. Without this, a theme Xero no longer lists renders the picker
     * blank while `values` carries the GUID, which reads as "the organisation
     * default" over a document whose letterhead is in fact already chosen.
     */
    convertQuote?.xeroBrandingThemeId ??
    (seededThemeId !== null && seededThemeId === values.xeroBrandingThemeId ? seededThemeId : null);

  const themeChoices = useMemo(
    () => brandingThemeOptions(brandingThemes.themes, storedThemeId, null),
    [brandingThemes.themes, storedThemeId],
  );

  /*
   * ── THE THREE DERIVED, READ-ONLY ROWS ────────────────────────────────────
   *
   * Currency, Tax and Branding theme. Owner's decision, 2026-08-31: all three
   * come from the company master and none of them is editable here.
   *
   * ⚠️ **THERE ARE TWO SOURCES, NOT ONE, AND THE ROW SAYS WHICH.** *SUPERSEDED:
   * "all three come from the company master" as an exhaustive statement.* Where
   * a company records none, its BILLING REGION's default is used — the server
   * resolves the region and serves it on the company read as
   * `billingRegionDefaults`, deliberately BESIDE the company's own values and
   * never merged with them, so this form can fall back and still name the
   * record. Measured across 149 live companies: only 4 carry any of the four
   * fields, so a company-only chain would have left these rows empty on 145 of
   * them, and a read-only EMPTY row is worse than an editable one.
   *
   * `derivedSources` is what carries the answer to the render, and only the
   * billing-region case prints a visible line — see `DerivedField`'s `source`.
   *
   * ⚠️ **Read-only means TEXT, not a disabled control**, and the difference is
   * what the reader concludes. A disabled picker says "you have not earned the
   * right to use this" and sends somebody hunting for the permission; text says
   * "this is a fact about the company", which is what it is. Same pattern, same
   * classes, as the Company, Account lead and Project rows above — do not
   * invent a second one.
   *
   * ⚠️ **Reused rather than re-derived.** The theme's label comes from
   * `themeChoices`, which already carries the stored-or-derived id injected back
   * when the live Xero list no longer names it; the currency comes from
   * `documentCurrency`, which the exchange-rate note beside it already reads;
   * the tax label comes from `documentLineAmountLabel`, the shared vocabulary
   * `QuoteFormModal` prints the same enum through. A second derivation for a
   * read-only row would agree on the day it was written and drift on the first
   * correction — and what would drift is a statement about what a customer is
   * billed.
   */

  /**
   * The chosen branding theme as a NAME, or `null` when the field is empty.
   *
   * ⚠️ **Falls back to the raw id rather than to nothing.** A theme an
   * accountant has since renamed or deleted, or a tenant that cannot be read at
   * all, leaves us with an id and no name — and a blank row there would read as
   * "use the organisation default", which is a different letterhead on a
   * customer's document. An ugly GUID is a worse label and a true one.
   */
  const chosenThemeId = values.xeroBrandingThemeId.trim();
  const derivedThemeLabel =
    chosenThemeId === ''
      ? null
      : (themeChoices.find((option) => option.value.toLowerCase() === chosenThemeId.toLowerCase())
          ?.label ?? chosenThemeId);
  /**
   * True when the row is printing the id because nothing could name it — which
   * is the only remaining state where the Xero connection's health is worth a
   * sentence on this form. It is not "you cannot choose a theme" any more;
   * there is nothing to choose.
   */
  const themeNameUnresolved =
    !companyDefaultsLoading && chosenThemeId !== '' && derivedThemeLabel === chosenThemeId;

  /**
   * What a derived row prints INSTEAD of an empty box.
   *
   * ⚠️ **An empty read-only row is the failure mode this exists to prevent.** A
   * blank box beside a label asserts nothing and explains nothing: the reader
   * cannot tell a company that records no currency from a form that has not
   * finished reading one, and there is no control to poke to find out. So the
   * three states are kept apart and each says where the value is set.
   *
   * ⚠️ **`reading` is create-only** — on an edit the value is the record's own
   * and there is no company read to wait for, so claiming to be reading one
   * would be a sentence about a request that is not happening.
   */
  function derivedAbsence(sentences: {
    reading: string;
    noCompany: string;
    unset: string;
  }): string {
    if (isEdit) return sentences.unset;
    if (companyDefaultsLoading) return sentences.reading;
    if (values.companyId.trim() === '') return sentences.noCompany;
    return sentences.unset;
  }

  /**
   * A derived value, WITHHELD while the company behind it is still being read.
   *
   * ⚠️ **This is about the company CHANGING, not about the first paint.** On the
   * first paint the fields are empty anyway, so the absence sentence renders on
   * its own. The window this closes is the second read: somebody moves a create
   * from company A to company B, and until B's response lands the three rows go
   * on printing A's currency, A's tax treatment and A's letterhead — under
   * labels whose entire claim is that the value came from THIS company.
   *
   * ⚠️ **A brief "reading…" is the honest render and a stale value is not**, and
   * this is deliberately the opposite call from `rateState`'s `checking` a few
   * hundred lines down, which refuses to say anything during its own moment. The
   * difference is what is on screen underneath: there, nothing is claimed while
   * the connection settles; here, a WRONG figure is claimed — the other
   * company's — and it is a figure about what a customer is billed.
   *
   * Withheld rather than cleared in `onCompanyChange`, which is where this form
   * clears the contact. A second writer of these three would have to reproduce
   * the prefill rule (`currencyPrefilled`) to avoid blanking a caller's currency
   * for good, and the derivation would be one edit away from disagreeing with
   * it. The derivation stays the ONLY writer; this is a render decision.
   */
  function derivedValue(value: string | null): string | null {
    return companyDefaultsLoading ? null : value;
  }

  /*
   * ⚠️ **`userOptions` and `staffFootnote` went with the Owner picker.** Both
   * existed to make a searched staff Combobox legible — pinning an owner who
   * sat outside the fetched page, and saying when the directory was unread or
   * truncated. The account lead needs neither: it arrives resolved on the
   * record, so there is no page it can fall off and no list whose failure
   * could misrepresent it.
   */

  /**
   * The account lead this invoice carries, or `null` where it carries none —
   * which on a CREATE is always, because there is no record yet.
   *
   * ⚠️ **Read straight off the record, never derived from the chosen company.**
   * The server resolves it through `company_id -> d_companies.account_lead_id`
   * on every read; re-deriving it here from `values.companyId` would be a
   * second implementation of somebody else's join, and it would answer for a
   * company the invoice has not been saved against yet.
   */
  const accountLead = invoice?.accountLead ?? null;
  /**
   * What the read-only row prints. Never `''`: a blank box over a lead that
   * exists reads as "nobody", which is the one thing this row must not say.
   */
  const accountLeadLabel =
    accountLead === null
      ? ''
      : accountLead.name.trim() !== ''
        ? accountLead.name
        : accountLead.email.trim() !== ''
          ? accountLead.email
          : `Staff #${accountLead.id}`;

  /*
   * 🔴 **`convertLinesNote` IS GONE — the grid says all four things itself.**
   * *SUPERSEDED: a four-branch string — "These could not be read just now — all of
   * them still come across." / "Reading the products on this deal…" / "This deal
   * has no products." / "Copied to a new Draft invoice — edit these before
   * converting." — under a comment arguing that "the three states are kept apart
   * because two of them are opposite instructions".*
   *
   * ⚠️ **The RULE survives and is now enforced by the prop shape.** The grid takes
   * `dealLines`, `dealLinesLoading` and `dealLinesError` as three separate props
   * precisely so a failed read cannot be rendered as an empty basket: it prints
   * its own error block saying every line still crosses, its own skeletons, and
   * its own empty state. Reinstating a note here would be a second sentence about
   * the same state, worded from a different place — and the last branch was
   * already false the moment rows became addable and removable.
   */

  /**
   * The COMPANY sales tax as the GRID is able to know it — the same tag test the
   * seeded contact uses, and for the same reason.
   *
   * ⚠️ **A tag mismatch is `known: false`, never `rate: null`.** Not read, read
   * and failed, and read for a DIFFERENT company are one fact: *we cannot say*.
   * Reporting any of them as "this company charges no tax" would put a claim
   * about a customer's tax treatment on screen that no response supports — and on
   * this route that claim also predicts a refusal, since
   * `POST /deals/{id}/invoices` answers `COMPANY_HAS_NO_TAX_RATE` when a deal with
   * lines meets a company with no rate.
   *
   * ⚠️ **Memoised, and that is not a micro-optimisation.** The grid depends on
   * this object's identity to decide whether to rebuild every row's money
   * preview; a fresh literal per render would recompute the whole map on every
   * keystroke in this form's header fields. `companyTaxRate` only changes when a
   * company read lands, so this changes then and not otherwise.
   */
  const companySalesTax = useMemo<CompanySalesTax>(
    () =>
      companyTaxRate !== null && companyTaxRate.companyId === companyLookupId
        ? { known: true, rate: companyTaxRate.rate, taxType: companyTaxRate.taxType }
        : { known: false },
    [companyTaxRate, companyLookupId],
  );

  /*
   * 🔴 **`convertLineProblems` AND `blockedConvertRows` ARE GONE, AND THE GRID
   * ANSWERS BOTH NOW.** *SUPERSEDED: two `useMemo`s over `dealLines.lines` and
   * `lineEdits`, one merging `validateConvertLineEdit` with the server's refusals
   * keyed on `dealLineId`, the other counting the rows that would be refused.*
   * They could only judge the five figures `lines` carried. `lineSet` carries the
   * product, the project, the cities, the regions and the inclusions as well, so
   * the judgement is `validateRow`'s inside the grid — where the controls are —
   * and the count arrives through `onConvertBlockedChange`.
   *
   * The rule those two encoded SURVIVES, in the grid: an untouched carry-over row
   * is never validated, because it emits `{dealLineId, sortOrder}` and nothing
   * else, so not one of its stored figures reaches the request and none of them
   * can be refused. Checking them anyway would let a legacy deal line disable the
   * conversion over a figure nobody chose.
   */

  /**
   * Whether the converted rows are still live.
   *
   * 🔴 **THEY ARE NOT ONCE `convertedInvoice` IS SET, AND THIS IS THE ONE PLACE
   * AN EDITABLE TABLE COULD HAVE LIED.** That state means the conversion LANDED
   * and only the follow-up header PATCH was refused — so the invoice exists, its
   * lines are already written from whatever overrides were sent, and
   * `runConvertDeal` deliberately never calls `POST /deals/{id}/invoices` a
   * second time. A box left live there is a control somebody types a new price
   * into, presses Save, and watches succeed with the old price **on a document
   * that demands money**: no error, nothing in a log, and a customer invoice
   * that disagrees with what was on screen. Editing a line from that point on is
   * the invoice's own line grid, which the alert above the table links to.
   */
  const convertLinesLocked = convertedInvoice !== null || submitting;

  /*
   * 🔴 **`patchLineEdit` IS GONE — the grid owns every box now.** *SUPERSEDED: it
   * merged a `Partial<ConvertLineEdit>` into `lineEdits[lineId]` and dropped that
   * row's server errors, "because a 422 describes the request that was sent; the
   * moment somebody edits the row it describes nothing on screen".*
   *
   * ⚠️ **That second half is a real obligation and it did NOT come across
   * automatically.** The grid clears its OWN per-row state on a keystroke, but
   * `lineServerErrors` lives here and is passed DOWN, so nothing inside the grid
   * can clear it. It is cleared at the start of every attempt instead — see
   * `runConvertDeal` — which is the same guarantee one request later: a refusal is
   * never shown against a request other than the one that produced it.
   */

  const fieldErrorCount = Object.keys(errors).length;
  /*
   * ⚠️ A blocked draft row disables Save on a create.
   *
   * The grid withholds any row that would be refused and reports the count
   * rather than sending it. Letting Save through anyway would create the
   * invoice and drop that row on the floor — and there is no un-creating an
   * invoice. `draftLines.length` alone cannot see this, which is why the count
   * exists.
   *
   * ⚠️ **The deal-convert path uses the SAME count under a different name**, and
   * the cost of letting one through is worse there: a converted row is not
   * dropped, it is REFUSED — and the conversion is what creates the invoice, so
   * a 422 on `lines.2.quantity` means no invoice at all rather than an invoice
   * missing a line. The two counts stay separate because the two row sets are.
   *
   * ⚠️ *SUPERSEDED 2026-08-31: "the draft grid is not rendered on the convert
   * path and `blockedDraftRows` is permanently 0 there."* **It renders on the
   * QUOTE-convert path now**, holding the rows somebody adds on top of the
   * copied ones — so `blockedDraftRows` is live there and the `isConvertingDeal`
   * ternary below already routes to it. Nothing needed changing here, which is
   * exactly why the sentence would have gone on reading as true.
   *
   * ⚠️ **The DEAL-convert path still has no DRAFT grid**, so the claim survives
   * for that half — it has the CONVERT grid instead, which is a different mode
   * issuing no request of its own. Two convert routes, two different grids: do
   * not read either half onto the other.
   *
   * ⚠️ **Zero once the conversion has landed, and that is not leniency.** With
   * `convertedInvoice` set the boxes are locked (see `convertLinesLocked`) and
   * Save re-sends the HEADER PATCH alone — no `lineSet` is built and none could
   * be. Counting a blocked row there would disable the only control that can
   * finish the job, over a box the person is no longer allowed to correct.
   */
  const blockedRows = isConvertingDeal
    ? convertedInvoice === null
      ? convertBlockedRows
      : 0
    : blockedDraftRows;
  /*
   * ⚠️ **`isDirty` IS THE HEADER, AND SAVE IS THE WHOLE FORM.** The two were
   * the same thing until line items became editable inside this modal; treating
   * them as the same afterwards is what produced a Save that refused to save.
   * An edit is now offerable when EITHER half has changed, and the line half is
   * counted through `lineEdits`, which the grid reports.
   *
   * ⚠️ **A blocked row still disables it**, exactly as on a create, and for the
   * same reason: the grid withholds a row it knows would be refused, so letting
   * Save through would close this form over work that was never sent. The
   * sentence in the footer names the count — a bare disabled control here would
   * be the defect this change exists to remove, in a new place.
   */
  const saveDisabled = isEdit
    ? (!isDirty && lineEdits.dirty === 0) || lineEdits.blocked > 0
    : values.subject.trim() === '' || blockedRows > 0;

  // A failed save must not leave its error hidden behind a collapsed section.
  const totalsErrorCount = TOTAL_KEYS.filter((key) => Boolean(errors[key])).length;
  const totalsExpanded = totalsOpen || totalsErrorCount > 0;

  /*
   * ⚠️ `documentErrorCount` / `documentExpanded` went with the "Xero tracking
   * and references" disclosure on 2026-08-31, along with `DOCUMENT_KEYS` and
   * `documentOpen`. They forced that section open when a save failed behind it;
   * there is no section left to force.
   *
   * ⚠️ **The reason the Xero fields are kept OUT of the totals band survives and
   * is not about layout**: the four totals are stored columns with two competing
   * writers, and putting a totals key in the same PATCH as a letterhead change
   * is how a real figure gets rewritten by an edit nobody thought was about
   * money. `buildPayload` enforces that; the banding is where it is visible.
   */

  /*
   * ⚠️ **`themesUnavailable` and `themePickerFrozen` went with the letterhead
   * PICKER — 2026-08-31.** *SUPERSEDED: "The letterhead picker is inert only
   * when there is nothing to choose AND nothing to clear. Freezing it whenever
   * Xero is unreachable would trap an invoice on a letterhead it can no longer
   * be released from … So an already-set value stays clearable."*
   *
   * ⚠️ **That reasoning is spent, not overruled, and the distinction matters if
   * a picker ever comes back.** It argued against DISABLING a control that could
   * still usefully be cleared. The branding theme is not a control on this form
   * any more — the owner's decision is that it comes from the company and is
   * uneditable — so there is nothing to freeze and nothing to trap. It is
   * emphatically NOT a finding that freezing a picker on an unreachable tenant
   * would have been fine.
   *
   * What survives of it is `themeNameUnresolved` above: the connection's health
   * still decides whether this form can put a NAME on the theme the invoice
   * carries, and that is the one thing left worth telling a reader.
   */

  /**
   * What this form has to say once it has created something — rendered above
   * the grid, as the saved grid's `contextNote`.
   *
   * ⚠️ **The whole point is that "the invoice was created" and "the lines were
   * created" are separate facts.** They are separate requests with no
   * transaction across them, so a report collapsing them into one "Saved" would
   * be wrong in the case that matters: the invoice exists, a line does not, and
   * the person is about to close the form believing nothing happened and press
   * New invoice again.
   *
   * Nothing here offers to undo anything, because nothing can. Deleting the
   * invoice to "roll back" a refused line would destroy the part that worked,
   * and calling it a rollback when it is a second destructive write is worse
   * than saying plainly what stands.
   *
   * ⚠️ **Identified by id, never by a number.** An invoice carries no number of
   * its own — `xeroInvoiceNumber` is XERO's, written back best-effort on a
   * push, and is therefore `null` on every invoice this form has just created.
   * Interpolating it would print "Invoice null was created".
   */
  const createdNote: ReactNode = !justCreated ? undefined : (
    <div className="grid gap-2">
      <Notice
        tone="success"
        role="status"
        className="break-words"
        title={`Invoice #${invoice?.id} was created and is saved.`}
      >
        {lineOutcome === null
          ? 'No line items were entered — add them below.'
          : lineOutcome.failures.length === 0
            ? savedLinesSentence(lineOutcome.attempted)
            : `${lineOutcome.saved} of ${lineOutcome.attempted} line items were saved with it.`}
      </Notice>

      {lineOutcome !== null && lineOutcome.failures.length > 0 && (
        <Notice
          tone="danger"
          role="alert"
          className="break-words"
          title={
            lineOutcome.failures.length === 1
              ? 'One line was refused, and the invoice was still created.'
              : `${lineOutcome.failures.length} lines were refused, and the invoice was still created.`
          }
        >
          <span className="block">
            The refused {lineOutcome.failures.length === 1 ? 'line was' : 'lines were'} not
            kept — add {lineOutcome.failures.length === 1 ? 'it' : 'them'} again.
          </span>
          {/*
            Numbered by the position each line held in the grid as it was
            SUBMITTED — which is not its position now, because the rows that
            landed have closed up around it. Said plainly above rather than
            silently letting someone count down the grid to row 3.
          */}
          <ul className="mt-2 grid gap-1 pl-6">
            {lineOutcome.failures.map((failure) => (
              <li key={failure.position} className="list-disc">
                <strong className="font-semibold">
                  Line {failure.position} of {lineOutcome.attempted}
                </strong>{' '}
                — {failure.message}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {/*
        ⚠️ **CORRECTED 2026-08-31 — TWO THINGS WENT FROM THIS BAND, AND WHAT
        THEY REPORTED DID NOT.**

        A `sending` block stood above this — *"Sending to Xero… The invoice is
        saved either way."* — and a "Send to Xero again" button stood below it,
        under the sentence *"Saved either way — this only sends them to Xero."*
        Both existed because this form made its own Xero call. It does not: the
        server pushes inside every write and answers `xeroPush` beside `data`,
        so there is no in-flight window to narrate and nothing here to repeat.

        The re-send is NOT lost, it moved: "Send to Xero now" on the invoice's
        own page is the app's manual write for an invoice, it can only ever
        UPDATE, and it is there whenever the reader comes back — which this
        modal, being a modal, is not.

        ⚠️ **The REPORT is deliberately kept.** A push that fails silently is
        the whole failure `xeroPush` exists to prevent, and deleting the band
        because the client call went would have reintroduced it from the other
        side.

        ⚠️ Rendered by `XeroPushReportBanner`, the component the list rows
        already use. The classification and almost all of the copy are the
        shared vocabulary's or the SERVER's own, because one refusal worded
        three ways across a list row, the detail page and this modal is
        precisely the drift `@/lib/xero` was consolidated to prevent. Only the
        HEADINGS are this form's, and every one of them leads with the invoice
        having been saved — see `serverPushReport`.
      */}
      {xeroPush.kind === 'settled' && (
        <div className="overflow-hidden rounded-md border border-border">
          <XeroPushReportBanner
            report={xeroPush.report}
            onDismiss={() => setXeroPush({ kind: 'idle' })}
          />
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open
      /*
       * `finish`, not `onClose`. Escape and the close button have to reload the
       * list when this form created an invoice — otherwise a record that plainly
       * exists is missing from the list behind it.
       */
      onClose={finish}
      /*
       * The widest step the primitive offers. Below `sm` every size is the same
       * full-bleed sheet, so this costs a phone nothing; from `lg` up it is what
       * seats four columns of header fields at a readable width instead of one
       * stacked column scrolled past.
       */
      size="xl"
      dismissOnBackdrop={false}
      initialFocusRef={subjectRef}
      title={
        invoice
          ? justCreated
            ? `Invoice created — ${invoice.subject}`
            : `Edit ${invoice.subject}`
          : convertQuote
            ? /*
               * Named as a CONVERSION rather than as a new invoice, because the
               * two behave differently in the one way that matters: the COPIED
               * line items below are the quote's and are written by the server,
               * not entered here.
               *
               * ⚠️ Still a conversion even though extra lines can now be added
               * beneath them — what makes it one is that the invoice comes from
               * `POST /quotes/{id}/convert` and the quote's own rows come with
               * it. The added rows are an ordinary second phase, exactly as on a
               * create.
               */
              `Convert ${convertQuote.subject} to an invoice`
            : convertDeal
              ? /*
                 * ⚠️ The DEAL'S OWN NAME, not the seeded subject. The subject box
                 * is editable and starts from `dealName`, so once somebody types
                 * in it the two diverge — and a title that followed the box would
                 * stop naming the record being converted, which is the one thing
                 * the title is here to say.
                 */
                `Raise an invoice from ${convertDeal.dealName}`
              : 'New invoice'
      }
      /*
       * 🔴 **THE HEADER, NOT THE FOOTER — owner's decision, 2026-09-14.** Cancel
       * and Save sit beside the title on all three document forms: this one is a
       * screen tall and its line grid carries a Save of its own partway down.
       * **The pair moved together** — splitting them across the two ends of a
       * scrolling dialog leaves a reader hunting the other half.
       *
       * ⚠️ **The sentence below travels WITH Save and that is the point of it.**
       * Its whole job is to say why the button beside it is off, so leaving it
       * in a footer while Save moved would strand an explanation a screen away
       * from the thing it explains. `sm:mr-auto` still pushes the buttons to the
       * end of the row — the header's action slot is `justify-end` from `sm`,
       * exactly as the footer was.
       */
      headerActions={
        <>
          {/*
            Why Save is off, said next to Save — and ONLY for the reason nobody
            can see. A disabled Save on an unedited form explains itself; a
            disabled Save because of a row further down the form does not.
          */}
          {/*
            ⚠️ **ONE count, TWO sentences, and the second half is why.** The
            draft grid's rows can be REMOVED and a converted row cannot — there
            is no delete control on that table and the line belongs to the deal
            — so telling somebody to "fix or remove it" on a conversion is
            advice they cannot follow, which is the class of defect that already
            cost this repo a message naming a control that had stopped existing.
            The count is shared because it is the same fact.
          */}
          {/*
            ⚠️ **The edit's own version of the same sentence, and it is the only
            reason Save may be disabled here that a reader cannot see.** An
            unedited form explains its own greyed-out Save; a row further down
            that the grid is withholding does not, and this is exactly the state
            that used to leave somebody pressing a dead button. It names the
            grid's own control rather than a count alone, because that control
            is what the row is waiting for.
          */}
          {isEdit && lineEdits.blocked > 0 && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {lineEdits.blocked === 1
                ? 'One line item below cannot be saved yet — fix or remove it.'
                : `${lineEdits.blocked} line items below cannot be saved yet — fix or remove them.`}
            </p>
          )}
          {!isEdit && blockedRows > 0 && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {isConvertingDeal
                ? blockedRows === 1
                  ? 'One line item below cannot be converted yet — correct it.'
                  : `${blockedRows} line items below cannot be converted yet — correct them.`
                : blockedRows === 1
                  ? 'One line item below cannot be saved yet — fix or remove it.'
                  : `${blockedRows} line items below cannot be saved yet — fix or remove them.`}
            </p>
          )}
          {/*
            🔴 "No changes yet." went in the owner's stricter pass, 2026-09-05 —
            a why-Save-is-disabled sentence, removed by name.
          */}
          {/* Renders nothing unless the company has spendable credit in this currency. */}
          {isEdit && invoice && (
            <ApplyCreditButton
              invoice={invoice}
              onApplied={() => setCreditSummaryKey((key) => key + 1)}
            />
          )}
          <Button variant="secondary" onClick={finish} disabled={submitting}>
            {/*
              Something was saved, so this is not a "Cancel" any more — and on
              the convert path a refused follow-up leaves a REAL invoice behind,
              which is exactly the moment somebody must not read this control as
              "undo".
            */}
            {created || convertedInvoice ? 'Done' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit
              ? 'Save changes'
              : convertedInvoice
                ? /*
                   * ⚠️ The conversion is DONE. Only the header edits are being
                   * re-sent, and the label has to say so — "Convert to invoice"
                   * over an invoice that exists reads as an offer to make a
                   * second one.
                   */
                  'Save these changes again'
                : isConvertingDeal
                  ? /*
                     * ⚠️ "Raise", not "Convert", and it is not a synonym here.
                     * A deal is not consumed by this: `POST /deals/{id}/invoices`
                     * answers no 409 and the same deal may be invoiced again, so
                     * "Convert" — which reads as a one-way change of state, and
                     * is exactly what it means on the quote route — would
                     * describe the wrong thing. It also matches the title above.
                     */
                    'Raise invoice'
                  : isConverting
                    ? 'Convert to invoice'
                    : 'Create invoice'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {/*
          ⚠️ THE ONE SENTENCE THIS SCREEN CANNOT DO WITHOUT.
          Nothing exists until Convert is pressed — that is the whole change the
          owner asked for — and the line items below are the SERVER's copy rather
          than rows anybody is authoring here. Both facts are invisible from the
          controls: the form looks exactly like the one that creates an invoice
          from scratch, where the grid is editable and Save writes what is in it.
        */}
        {/*
          ⚠️ **TWO SENTENCES, because the second clause is only true of the
          QUOTE route.** On a deal conversion the rows below are boxes — the
          `lines` override is real — so "come across with it" would be right
          about where they come from and wrong about whether anybody is
          authoring them, which is exactly the half-true sentence that reads as
          authoritative to everybody who skims it. Both say the one fact this
          screen cannot do without: nothing exists until the button is pressed.
        */}
        {/*
          ⚠️ **The QUOTE sentence was re-worded on 2026-08-31 and had to be.**
          *SUPERSEDED: "The line items below come across with it."* There are two
          grids below now and only the FIRST comes across: the copied rows are
          written by the server inside the conversion, the added rows are written
          one at a time after it. The old sentence was still true of the grid it
          was written about and false about the screen — which is this codebase's
          "an artefact is falsified by a change somewhere else", arriving on the
          copy rather than on the code.
        */}
        {isConverting && convertedInvoice === null && (
          <Notice tone="info" className="break-words">
            {isConvertingDeal
              ? 'Nothing is created until you press Convert.'
              : 'Nothing is created until you press Convert. The quote’s line items come across with it; anything you add below is saved after it, one at a time.'}
          </Notice>
        )}

        {convertError !== null && (
          <Notice tone="danger" role="alert" className="break-words">
            {convertError}
          </Notice>
        )}

        {/*
          ⚠️ **THE INVOICE EXISTS.** The conversion landed and something after it
          did not, and there is no rollback and no compensating endpoint safe to
          fire on somebody's behalf — so the only truthful report is the one that
          says what stands, names it, and links to it. "Nothing was saved" here
          is the sentence that produces a second invoice: the person closes the
          modal and converts again.

          ⚠️ *SUPERSEDED 2026-08-31: "Convert landed and the follow-up PATCH did
          not."* That is now only ONE of the two ways to get here — a refused
          ADDED LINE lands here too, with the header perfectly fine. The block
          below reads `convertHeaderRefused` rather than inferring the cause from
          its own presence; see the note on that state.

          ⚠️ The heading leads with what was CREATED, not with what failed. Same
          ordering, for the same reason, as the Xero cooldown report above.
        */}
        {convertedInvoice !== null && (
          <Notice tone="warning" role="alert" className="break-words">
            {/*
              ⚠️ **AND ON THE DEAL ROUTE IT ALSO SAYS THE LINE BOXES ARE
              SPENT**, because from this point they are locked
              (`convertLinesLocked`) and a disabled control with no sentence
              beside it is indistinguishable from a broken one. The lines were
              written by the conversion that landed; Save from here re-sends the
              HEADER only, so a price typed into the table would do nothing and
              report success. "Why a control is disabled" is one of the four
              kinds of sentence the no-helper-text rule keeps, and this is one.
            */}
            <p className="m-0">
              <strong className="font-semibold">
                Invoice #{convertedInvoice.id} was created from this{' '}
                {isConvertingDeal ? 'deal' : 'quote'}, with its line items.
              </strong>{' '}
              {/*
                ⚠️ **Read off `convertHeaderRefused`, NOT off the presence of
                `convertedInvoice`.** Since the quote route gained a draft grid,
                this banner also renders when the header was perfectly fine and
                an ADDED line was refused — and telling that reader their header
                changes were refused sends them to correct a field that is not
                marked and was never wrong.
              */}
              {convertHeaderRefused ? 'Your header changes were refused. ' : ''}
              Converting again would raise a second invoice
              {isConvertingDeal
                ? ', and the line items below can only be edited on the invoice now.'
                : '.'}
            </p>
            <p className="m-0 mt-2">
              {/*
                The instruction has to follow the cause too: with nothing marked,
                "correct what is marked" is an instruction that cannot be
                carried out.
              */}
              {convertHeaderRefused ? 'Correct what is marked and save again, or ' : 'You can '}
              {/*
                `finish` rather than a bare route push: the caller is what
                navigates, and it also has a list to reload. A raw <Link> here
                would leave a modal open behind the new page.
              */}
              <button
                type="button"
                className="underline underline-offset-2 hover:no-underline"
                onClick={finish}
              >
                open invoice #{convertedInvoice.id}
              </button>{' '}
              and edit it there.
            </p>
          </Notice>
        )}

        {/*
          ⚠️ **XERO DID NOT TAKE THE INVOICE THIS CONVERSION CREATED.**

          Its own block, beside the banner above and never instead of it, for the
          reason the refused-line block below is: the conversion landing, the
          header landing, a line being refused and the push being refused are four
          separate facts from separate requests, and each changes what the reader
          does next.

          ⚠️ **This is the ONLY artefact anywhere that carries it.** The server
          swallows a failed push so the conversion still answers `201`, and it does
          not re-read the record on that branch because nothing was written — so
          the invoice arrives with no Xero link, and its own page cannot tell that
          state from one nobody has pushed yet. Drop this and the person is told
          the conversion worked, which it did, and never learns the customer's
          document is not in the accounts.

          ⚠️ **`pushed` and `skipped` never reach here, deliberately.**
          `conversionPushReport` answers `null` for both — see it for why a warning
          on the commonest outcome in the system would be worse than none.

          ⚠️ Every string is `rowPushReport`'s or the SERVER's own, rendered by the
          component the list rows use. Only the heading is replaced, and it leads
          with the invoice having been created, because reading a refused push as a
          failed conversion is what produces a second invoice.
        */}
        {convertPush !== null && (
          <div className="overflow-hidden rounded-md border border-border">
            <XeroPushReportBanner report={convertPush} onDismiss={() => setConvertPush(null)} />
          </div>
        )}

        {/*
          ⚠️ **A LINE ADDED ON THE CONVERT PATH THAT DID NOT SAVE.**

          Its own block, beside the banner above and never instead of it: the
          conversion landing, the header landing and an added line being refused
          are three separate facts from three separate requests, and each changes
          what the reader does next. The create path renders the same report
          inside `createdNote`; this is the convert path's copy of it, because
          that path keeps `created` unset on purpose — the lines are the server's
          copy and the form must not turn into an edit of them.

          ⚠️ **Nothing here offers a retry and nothing claims a rollback.** The
          invoice exists with its copied lines; a refused extra row was never
          written, and re-sending the draft array would duplicate the rows that
          did land. Saying plainly what stands, and where to add it again, is the
          only truthful report available.
        */}
        {convertedInvoice !== null && lineOutcome !== null && lineOutcome.failures.length > 0 && (
          <Notice
            tone="danger"
            role="alert"
            className="break-words"
            title={
              lineOutcome.failures.length === 1
                ? 'One line item you added was refused.'
                : `${lineOutcome.failures.length} line items you added were refused.`
            }
          >
            <span className="block">
              The invoice and the lines copied from the quote are saved. The refused{' '}
              {lineOutcome.failures.length === 1 ? 'line was' : 'lines were'} not kept — add{' '}
              {lineOutcome.failures.length === 1 ? 'it' : 'them'} on the invoice.
            </span>
            {/*
              Numbered by the position each row held in the ADDED grid as it was
              submitted — not its position on the finished invoice, where the
              copied lines sit above it. Said plainly rather than letting
              somebody count down the document to row 3.
            */}
            <ul className="mt-2 grid gap-1 pl-6">
              {lineOutcome.failures.map((failure) => (
                <li key={failure.position} className="list-disc">
                  <strong className="font-semibold">
                    Added line {failure.position} of {lineOutcome.attempted}
                  </strong>{' '}
                  — {failure.message}
                </li>
              ))}
            </ul>
          </Notice>
        )}

        {/*
          * Band 1 — the invoice itself, laid out the way Xero lays out a
          * document header: a multi-column grid rather than one stacked column,
          * so the whole record is one view instead of a scroll.
          *
          * The column count steps 1 → 2 → 4, and it STEPS rather than shrinks:
          * at 320px every one of these is a full-width control, because a
          * quarter-width date input on a phone is not a denser form, it is an
          * unusable one. `[&>*]:min-w-0` is on every grid here — a grid item
          * defaults to `min-width: auto`, so one wide child (a combobox
          * footnote, a long city chip) would otherwise widen its whole track
          * and push the dialog sideways.
          *
          * Row order is chosen so it reads correctly at ALL THREE counts, which
          * is why the groups are four wide: parties, then the project and what
          * it implies, then the dates and the two picked values, then the three
          * things that decide what the customer's PDF says — their PO number,
          * the letterhead and the tax treatment. Collapsed to one column that is
          * still a sensible top-to-bottom order.
          *
          * ⚠️ That last group arrived here from two separate bands, "References"
          * and "Xero document", when the form took `QuoteFormModal`'s shape.
          * They are the EVERYDAY fields — an ordinary invoice sets a letterhead
          * and a tax treatment — and only the exceptions went behind the
          * disclosure below. Do not push any of the three back behind it to
          * shorten this grid.
          */}
        <fieldset className="min-w-0">
          <legend className="sr-only">Invoice details</legend>

          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4 [&>*]:min-w-0">
          {/*
            * Two columns inside the grid, not a full-width row above it. It
            * was its own `mb-3` block, which made one field as wide as the
            * four beneath it put together and read as a different KIND of
            * control. The quote spans two here; matching it is the whole point.
            */}
          <div
            className="sm:col-span-2"
            ref={(node) => {
              subjectRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Subject" required error={errors.subject}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.subject}
                  maxLength={MAX_LENGTHS.subject}
                  placeholder="Riverdale Academy — Summer 2027 recruitment"
                  invalid={Boolean(errors.subject)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    /*
                     * The ONE writer of `subjectTouched` — see the project seed
                     * above. Typing here, clearing the box included, ends the
                     * seeding for the life of this form: `''` is a choice, and
                     * a seed that read it as "untouched" would put the project
                     * description back over a deliberately emptied field.
                     */
                    subjectTouched.current = true;
                    setField('subject', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

            {lockedCompany ? (
              /*
               * Read-only rather than a disabled Combobox: there is nothing to
               * open, and a disabled picker invites the reader to go looking for
               * the one option it has. The server can still reject the id — an
               * unknown company is a 422 at `companyId` — so the error is
               * rendered here as well.
               */
              /*
               * The hint "Locked to the company you opened this form from."
               * went in the owner's stricter pass, 2026-09-05 — a lock nothing
               * refuses is not one of the five sentences kept. The box is still
               * `readOnly` and still shows the company's name.
               */
              <Field label="Company" error={errors.companyId}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={lockedCompany.name}
                    readOnly
                    aria-readonly="true"
                    invalid={Boolean(errors.companyId)}
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            ) : (
              <Field label="Company" error={errors.companyId}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={values.companyId}
                    // Not `setField` — a company change clears the contact with
                    // it, and the two have to be one state update. See
                    // `onCompanyChange`.
                    onChange={onCompanyChange}
                    options={companies.options}
                    onSearch={companies.setTerm}
                    loading={companies.loading}
                    emptyOptionLabel="Unassigned"
                    searchPlaceholder="Search companies…"
                    footnote={companies.footnote}
                    invalid={Boolean(errors.companyId)}
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            <ContactPickerField
              value={values.contactId}
              onChange={onContactChange}
              options={contactOptions}
              onSearch={contacts.setTerm}
              loading={contacts.loading}
              footnote={contacts.footnote}
              error={errors.contactId}
              scope={contactScope}
              company={contactCompany}
            />

            <Field label="Deal" error={errors.dealId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.dealId}
                  onChange={(next) => setField('dealId', next)}
                  options={deals.options}
                  onSearch={deals.setTerm}
                  loading={deals.loading}
                  emptyOptionLabel="Unassigned"
                  searchPlaceholder="Search deals…"
                  footnote={deals.footnote}
                  invalid={Boolean(errors.dealId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>

            {/*
              ── WHERE THE OWNER PICKER USED TO BE ─────────────────────────────

              The owner's instruction: this row shows the ACCOUNT LEAD, taken
              from the company. The server already does the taking — every read
              of an invoice resolves `company_id -> d_companies.account_lead_id`
              — so there is nothing to seed here and nothing to send.

              ⚠️ **This is NOT the owner field renamed, and no copy here may
              suggest it is.** They are two different people answering two
              different questions: the owner holds this invoice, the account
              lead holds the relationship with the company. `types.ts` says so
              in those words, and a live example exists (quote 9: owner Paras
              Fatnani, account lead Daryna Kodinets). Anyone reading a document
              before and after this change sees a DIFFERENT NAME in this
              position, which is why it is labelled "Account lead" and given its
              own sentence rather than inheriting the old label.

              ⚠️ **Read-only, and structurally unwritable from here.** There is
              no key for it in `FormValues`, no entry in `ID_KEYS`, and
              `KNOWN_WRITE_KEYS` (derived from `toFormValues()`) cannot name it.
              A control here would be a second answer to a question the company
              already answers, which is why the owner retired lead-assignment on
              this document.

              ⚠️ **CORRECTION (2026-08-30): this justified itself with
              "`accountLeadId` in a write body is a 422 on all four revenue
              documents — it is written on the COMPANY and nowhere else". FALSE
              on three of the four.** Deals gained an `account_lead_id` override
              in migration 079, quotes and invoices in 082, and all three accept
              the key on write (`invoices.schema.ts:200`, `optionalUserId()`,
              verified from backend source); `null` clears the override rather
              than blanking the field. Only a DELIVERABLE really refuses it. The
              structural guard stays — it is good practice whatever the server
              takes — but the read-only rendering rests on the owner's decision,
              so do not report an override as a backend gap.

              **Nothing is rendered on a CREATE**, and that is deliberate rather
              than a missing empty state: the value is derived from a record
              that does not exist yet, so a labelled row reading "None" would
              assert that this invoice has no account lead when what is true is
              that it has no invoice. It appears the moment the create returns,
              because `invoice` becomes the created record.

              `invoice.owner` is untouched by all of this — still stored, still
              read, and no longer writable from this form. See `FormValues`.
            */}
            {accountLead !== null && (
              <Field label="Account lead">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    /*
                     * The email is the disambiguator on a staff directory where
                     * two people can share a name, and it is already resolved on
                     * the record — no second request to show it. `name` is typed
                     * `string` but is `''` on real rows (a `KanbanOwner`'s
                     * falls back to it), so the email carries the row rather
                     * than the box rendering blank over a lead that exists.
                     */
                    value={accountLeadLabel}
                    readOnly
                    aria-readonly="true"
                    title="Set on the company record, not here."
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            {/*
              ── WHERE THE HEADER PROJECT PICKER USED TO BE ────────────────────

              The owner's decision: an invoice is not connected to a project as a
              whole — individual LINE ITEMS are, exactly as on a quote. A project
              names one product and a document sells many, so the choice belongs
              on the row that names the product. The same change was made to
              `QuoteFormModal` and `DealFormModal` first; this is their solution,
              not a new one.

              **The header key still exists on the API.** `projectId` is still
              accepted on `POST`/`PATCH /invoices`, still stored, still READ —
              `invoice.project` is rendered read-only just below, and the two
              Xero tracking hints derive their Location and Tour Type from it. It
              is now written by **NOTHING IN THIS APP AT ALL** — only by whatever
              set it before.

              ⚠️ *SUPERSEDED, 2026-08-31: "written by nothing in this app except
              `DeliverableFormModal` (which keeps its own header project)".* That
              was true until `DeliverableFormModal` was **deleted** with the old
              deliverables entity, which took the last header-`projectId` writer
              in the application with it. **The count went one → zero**, and the
              same stale sentence existed in `use-project-options.ts`.

              ⚠️ **A documented invariant changed because a DIFFERENT file was
              deleted, and nothing failed.** No build, lint or type error can see
              a comment naming a module that no longer exists; the deletion was
              deliberate and correct, and this sentence was simply somewhere
              nobody was looking. It is the exact shape this codebase records as
              its most repeated defect — an artefact accurate about what it
              describes, falsified by a change somewhere else.

              **Why it matters rather than being tidy-up:** a reader deciding
              whether it is safe to touch the header key needs to know whether
              anything still writes it. "Nothing except X" invites them to go and
              check X; "nothing at all" is a different fact and a different
              decision.

              ⚠️ **This form deliberately CANNOT express that write, and the
              guard is the absence of the key rather than an `if`.** `projectId`
              is not a member of `FormValues`, so: `toFormValues` has no
              baseline for it, `changedKeys` walks `Object.keys(baseline)` and
              cannot produce it, `buildPayload` indexes `values[key]` and would
              not compile with it, `ID_KEYS` — the only thing that turns an id
              into `null`-on-empty — does not list it, and `KNOWN_WRITE_KEYS` is
              derived from the same object so a 422 naming it goes to the
              form-level banner instead of marking a control that is not there.

              A `delete payload.projectId` or a `if (key !== 'projectId')` would
              be one careless edit from failing, and the failure is SILENT and
              destructive: an empty box emits `''`, `ID_KEYS` turns that into an
              explicit `null`, and an explicit `null` CLEARS the column — so an
              invoice that carried a project would lose it, and the `cities` it
              derives THROUGH that project, the first time anybody edited its
              subject. A 200, no error, nothing recorded to restore from. A key
              that does not exist cannot be forgotten.

              Gone with the picker: the city panel that sat beside it. It listed
              the cities the chosen project could sell in — never the set a line
              is validated against, and beside a field nobody can change it is
              context for a decision that is not made on this screen. Each line
              names its own cities, from its own project, inside the grid below.
            */}
            {headerProjectName !== null && (
              <Field label="Project">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={headerProjectName}
                    readOnly
                    aria-readonly="true"
                    title="Chosen on each line item now."
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            <Field label="Bill date" error={errors.billDate}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  // A native date input emits YYYY-MM-DD from local parts, which
                  // is the wire format — no Date, no timezone drift.
                  type="date"
                  value={values.billDate}
                  invalid={Boolean(errors.billDate)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    const next = event.target.value;
                    setField('billDate', next);
                    /*
                     * ⚠️ **The due date FOLLOWS this one until somebody edits
                     * it**, and both halves of that are deliberate. Moving the
                     * bill date on a form whose due date is still the default
                     * and leaving the due date behind is how an invoice ends up
                     * due before it is billed, or thirty days out from a date
                     * nobody chose. Editing the due date ends the following for
                     * good — `dueDateTouched`, written by that input alone.
                     *
                     * Never on an EDIT: a stored due date is what the customer
                     * was told, and re-deriving it because somebody corrected
                     * the issue date would silently change a payment deadline.
                     */
                    if (isEdit || dueDateTouched) return;
                    if (next.trim() === '') {
                      // Following includes following to nothing. The due date
                      // here is derived and nothing but derived, so there is no
                      // typed value to destroy.
                      setField('dueDate', '');
                      return;
                    }
                    // `addDaysIso` reads the three parts out of the string, so a
                    // half-typed or impossible day would produce a nonsense date
                    // rather than throwing. A native picker cannot emit one;
                    // keyboard entry in some browsers can.
                    if (!isRealDate(next)) return;
                    setField('dueDate', addDaysIso(next, DEFAULT_PAYMENT_TERM_DAYS));
                  }}
                />
              )}
            </Field>

            <Field label="Due date" error={errors.dueDate}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  value={values.dueDate}
                  invalid={Boolean(errors.dueDate)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    /*
                     * The ONE writer of `dueDateTouched`. Clearing the box counts
                     * — an emptied due date is a choice, and a bill-date change
                     * putting a derived one back into it would undo that.
                     */
                    setDueDateTouched(true);
                    setField('dueDate', event.target.value);
                  }}
                />
              )}
            </Field>

            {/*
              The load-failure hint ("The status list could not be loaded — try
              signing in again.") went in the owner's stricter pass, 2026-09-05:
              a failed fetch is not a refusal. The picker can still be empty for
              that reason and now says nothing about why.
            */}
            <Field label="Status" error={errors.statusId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  // Derived, so a NEW invoice reads "Draft" the moment the master
                  // lands — see `effectiveStatusId`, and note `buildPayload` reads
                  // the same value so the payload cannot disagree with the label.
                  value={effectiveStatusId}
                  onChange={(next) => {
                    /*
                     * The ONE writer of `statusTouched`. Choosing here, including
                     * choosing "No status", ends the defaulting for good: '' is a
                     * deliberate choice, and a Draft that came back over it would
                     * undo exactly what was just done.
                     */
                    setStatusTouched(true);
                    setField('statusId', next);
                  }}
                  // Filtered locally: the whole master arrives in one request.
                  // There is deliberately no "add new" here — invoice statuses are
                  // an admin-managed master, so this picker only ever reads them.
                  options={statusOptions}
                  loading={statusesLoading}
                  emptyOptionLabel="No status"
                  searchPlaceholder="Search statuses…"
                  invalid={Boolean(errors.statusId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>

            {/*
              The currency and the rate it is pinned at are ONE cell, exactly as
              they are on Xero's own document header — the rate line sits
              directly under the currency it qualifies. They are two fields on
              the wire and one fact to a reader, and separating them would leave
              the rate floating with no currency beside it to make sense of.

              ⚠️ **The pairing OUTLIVED the picker and matters more without it.**
              The currency is derived and read-only now while the RATE is still
              typed, so this cell is the one place a reader can see the pair a
              rate is only meaningful against. *SUPERSEDED: "the rate line sits
              directly under the picker it qualifies."*
            */}
            <div className="space-y-2">
              {/*
                ── WHERE THE CURRENCY PICKER USED TO BE ──────────────────────
                Owner's decision, 2026-08-31: an invoice is billed in the
                currency the COMPANY is billed in, and this form does not offer
                a second answer to that.

                The value is the three-letter CODE, not a currency id — an
                invoice stores `currency` as a CHAR(3) with no foreign key to
                /currencies. `documentCurrency` is the same reading the exchange
                rate note below already takes, so the two cannot disagree about
                what this document is priced in.

                ⚠️ `errors.currency` is still rendered. The server can name the
                key on a 422 — a code the master no longer offers, a lower-case
                one from an older writer — and `KNOWN_WRITE_KEYS` claims this
                form can mark it. Marking a key with nowhere on screen to show
                it is the "one field needs attention, highlighted below" lie
                with nothing highlighted.
              */}
              <DerivedField
                label="Currency"
                value={derivedValue(documentCurrency)}
                absence={derivedAbsence({
                  reading: 'Reading the currency from the company…',
                  noCompany:
                    'Choose a company — an invoice takes its currency from the company record.',
                  /*
                   * ⚠️ **Names BOTH records now that both are consulted.** The
                   * old sentence said "the company record" alone, which was
                   * true when the company was the only rung and became an
                   * understatement the moment the region was added — it sends
                   * somebody to fix one of the two places a value can come
                   * from, and says nothing about the other having nothing in
                   * it either. The absence is the state where BOTH are empty.
                   */
                  unset:
                    'No currency is set on the company or on its billing region — it comes from one of those.',
                })}
                title={derivedSourceTitle(derivedSources.currency)}
                source={derivedSources.currency}
                error={errors.currency}
              />

              {/*
                `documentCurrency` is no longer passed: its only reader was the
                `same` branch's sentence, removed 2026-09-05.
              */}
              <ExchangeRateNote
                state={rateState}
                baseCurrency={baseCurrency}
                rate={values.currencyRate.trim()}
                exchangeRate={exchangeRate}
                error={errors.currencyRate}
                onSet={() => setRateDialogOpen(true)}
                /*
                 * ⚠️ Clearing writes '' and nothing else. It must NOT also
                 * clear the currency, and it must not write '1': both would be
                 * this form deciding something on the user's behalf that
                 * changes what a customer is billed. '' becomes an explicit
                 * `null` on the PATCH, which is the API's own way of saying
                 * "no pin — use Xero's daily rate".
                 */
                onRemove={() => setField('currencyRate', '')}
              />
            </div>
            {/*
              THREE references live on this invoice and conflating any two loses
              a real one: the record id is EMERGE's own (shown on the invoice
              page, not editable, and so not a control anywhere),
              `xeroInvoiceNumber` is the finance system's, and this one is the
              CUSTOMER's. It is what their finance team matches the payment
              against, so it is labelled by whose number it is rather than as a
              bare "Reference".

              ⚠️ It is also the ONLY one of the three anybody may type. The
              other two are written by their own systems and are displayed
              read-only in the disclosure below — so a control here is not
              evidence that its neighbours take one.

              ⚠️ The LABEL is the real safeguard — the same one this field
              carries on `/invoices/:id`, because a field that reads one way on
              the form and another on the record is exactly the mix-up the
              naming exists to prevent. Never "Reference" alone.
            */}
            <Field label="Customer PO number" error={errors.reference}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.reference}
                  maxLength={MAX_LENGTHS.reference}
                  placeholder="PUR-00019670"
                  invalid={Boolean(errors.reference)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('reference', event.target.value)}
                />
              )}
            </Field>

            {/*
              Branding theme and tax treatment sit in the header band, exactly as
              they do on Xero's own screen and on `QuoteFormModal`: they are two
              of the three things that decide what the customer's PDF looks like,
              and a branding theme decides which bank account the customer is
              told to pay into — which is not a detail to have to go looking for.

              The cell owns its own stack because the unreachable-tenant notices
              belong to this field and to nothing else.
            */}
            <div className="space-y-2">
              {/*
                ── WHERE THE BRANDING THEME PICKER USED TO BE ────────────────
                Owner's decision, 2026-08-31: the letterhead a customer's
                document prints on comes from the COMPANY, and this form does
                not offer a second answer to it.

                ⚠️ **"Branding theme", not "Letterhead"** — the owner's wording,
                to match Xero's own screen, which is the screen this is
                reconciled against. **The LABEL moved and NOTHING ELSE did**: the
                key is still `xeroBrandingThemeId`, the payload is unchanged, and
                most comments in this file still say "letterhead" because they
                describe the concept rather than quote the UI. Do not rename the
                key to follow the label, and do not go looking for this row by
                grepping the old word.

                ⚠️ **Every hint the picker carried is gone with it, and only one
                of them was helper text.** The rest reported on a list nobody
                picks from any more — "so none can be chosen right now" describes
                a choice that does not exist. What survives is the one fact a
                reader still cannot see: whether this form could put a NAME on
                the theme the invoice carries. That is `themeNameUnresolved`, and
                the alert below is gated on it.
              */}
              <DerivedField
                label="Branding theme"
                value={derivedValue(derivedThemeLabel)}
                absence={derivedAbsence({
                  reading: 'Reading the branding theme from the company…',
                  noCompany:
                    'Choose a company — an invoice takes its branding theme from the company record.',
                  unset:
                    'No branding theme is set on the company or on its billing region, so Xero uses the organisation’s default.',
                })}
                /*
                 * ⚠️ **The two sentences are JOINED, not chosen between.** The
                 * unresolved-name note and the source are different facts — why
                 * a GUID is on screen, and which record put it there — and a
                 * reader meeting the id needs both: one tells them the label is
                 * missing, the other tells them where to go. Coalescing would
                 * drop whichever came second.
                 */
                title={
                  themeNameUnresolved
                    ? `Xero does not list this branding theme, so its id is shown. ${derivedSourceTitle(
                        derivedSources.xeroBrandingThemeId,
                      )}`
                    : derivedSourceTitle(derivedSources.xeroBrandingThemeId)
                }
                source={derivedSources.xeroBrandingThemeId}
                error={errors.xeroBrandingThemeId}
              />

              {/*
                ⚠️ **Gated on the NAME being unresolved, not merely on Xero
                being unreachable.** These notices used to explain why a picker
                could not be filled; there is no picker. The only thing left that
                a reader is owed is why the row above prints a GUID instead of a
                theme name — so when the name resolves, or there is no theme at
                all, the connection's health is not this form's business and
                saying anything about it would be noise about a list nothing here
                reads from.
              */}
              {themeNameUnresolved &&
                !xero.loading &&
                !xero.connection.connected &&
                !xero.notInstalled && (
                  <p className="text-xs">
                    <TextLink to="/integrations" className="font-medium">
                      Set up the Xero connection
                    </TextLink>
                  </p>
                )}

              {themeNameUnresolved && xeroConnected && brandingThemes.error && (
                <Notice
                  role="alert"
                  className="break-words"
                  /*
                   * A cooldown is a calm state of a HEALTHY connection, so it
                   * takes the warning band rather than the danger one. Red
                   * would say something is wrong, and nothing is.
                   */
                  tone={
                    brandingThemes.error.kind === 'retry' ||
                    brandingThemes.error.kind === 'missing' ||
                    brandingThemes.error.kind === 'cooldown'
                      ? 'warning'
                      : 'danger'
                  }
                  {...(brandingThemes.error.kind !== 'missing'
                    ? {
                        action: (
                          <Button variant="secondary" size="sm" onClick={brandingThemes.reload}>
                            Try again
                          </Button>
                        ),
                      }
                    : {})}
                >
                  {brandingThemes.error.message}{' '}
                  {brandingThemes.error.kind === 'reconnect' && (
                    <TextLink to="/integrations" className="font-medium">
                      Re-authorise Xero
                    </TextLink>
                  )}
                </Notice>
              )}
            </div>

            {/*
              ⚠️ **The label is "Tax", the same word `QuoteFormModal` puts over
              the same enum** — the two halves of one conversion naming one
              field two ways is the drift `DOCUMENT_LINE_AMOUNT_OPTIONS` was
              extracted to stop.

              ⚠️ Blank means "use the company's Xero default", never "no tax".
              "No tax" is a value you CHOOSE — NOTAX — and letting blank read as
              it would assert a VAT treatment nobody picked, on a document a
              customer is invoiced against. The blank option's own label,
              `USE_COMPANY_DEFAULT`, says so.
            */}
            {/*
              ── WHERE THE TAX SELECT USED TO BE ───────────────────────────────
              Owner's decision, 2026-08-31: how tax is shown on this document
              comes from the company's Xero sales defaults, and this form does
              not offer a second answer to it.

              ⚠️ **The label is "Tax", the same word `QuoteFormModal` puts over
              the same enum** — the two halves of one conversion naming one field
              two ways is the drift `DOCUMENT_LINE_AMOUNT_OPTIONS` was extracted
              to stop, and `documentLineAmountLabel` is that same vocabulary read
              from the other end.

              ⚠️ **Blank means "use the company's Xero default", never "no
              tax".** "No tax" is a value somebody CHOOSES — NOTAX — and letting
              blank read as it would assert a VAT treatment nobody picked, on a
              document a customer is invoiced against. That distinction used to
              be carried by the empty option's `USE_COMPANY_DEFAULT` label; with
              no option list left it is carried by the absence sentence, which
              names the company's Xero sales defaults outright rather than
              printing a value.
            */}
            <DerivedField
              label="Tax"
              value={derivedValue(documentLineAmountLabel(values.xeroLineAmountType.trim()) ?? null)}
              absence={derivedAbsence({
                reading: 'Reading the tax treatment from the company…',
                noCompany:
                  'Choose a company — an invoice takes its tax treatment from the company’s Xero sales defaults.',
                /*
                 * ⚠️ **This sentence covers ONE MORE STATE than the other two,
                 * and does not name it — deliberately.** A company whose sales
                 * default is `NONE` reaches this branch too: the value is real
                 * and simply cannot be represented on a document from here, so
                 * the form leaves the key off and the SERVER translates it to
                 * `NOTAX` on save. Saying "no tax treatment is set" of such a
                 * company is imprecise; saying "it is set to NONE and we are
                 * sending nothing" would be a sentence about this form's
                 * internals, on a row whose job is to say where the value
                 * lives. Both answers point at the same record, which is what
                 * the reader needs.
                 */
                unset:
                  'No tax treatment is set on the company or on its billing region — it comes from the company’s Xero sales defaults.',
              })}
              title={derivedSourceTitle(derivedSources.xeroLineAmountType)}
              source={derivedSources.xeroLineAmountType}
              error={errors.xeroLineAmountType}
            />

            {/*
              ⚠️ **The company's Xero sales defaults are not shown here, and
              must not come back as a CONTROL either.** `xeroSalesTaxType` and
              `xeroSalesLineAmountType` are the COMPANY's fields, collected at
              push time by `XeroSalesDefaultsDialog` with the person pushing in
              front of it; a copy on this document would be a second answer to
              one question, and no key for either exists in `FormValues`. A
              company recording none is refused loudly by `XERO_NO_TAX_TYPE` at
              push time — that refusal is the guarantee.
            */}
          </div>
        </fieldset>

        {/*
          THE LINE ITEMS BAND — the document's middle, between its header and
          its foot, exactly where Xero's own invoice screen puts it, and ABOVE
          the Totals panel that is derived from it.

          🔴 **RENDERED ON AN EDIT TOO, since 2026-08-31 — owner's instruction:
          *"in the invoice edit there should exist the line items grid, same
          editable as quotes and the convert-to-invoice modal, so it can be
          editable here also."***

          ⚠️ *SUPERSEDED, and it is the reason the grid was missing rather than
          merely below the fold: "**Rendered on a CREATE, and on the record a
          create just made — not on an ordinary edit.** `InvoiceDetailPage`
          already carries the grid under the record it is editing, and a second
          copy inside the modal over it would be two live grids on one basket.
          What this band exists for is the gap the detail page cannot cover: the
          lines somebody wants to enter *before* there is an invoice to hang them
          on. That is the one place this differs from `QuoteFormModal`, which
          renders it always."*

          The guard was `{(!isEdit || justCreated) && …}`, so on an edit the
          whole band — INCLUDING the `) : invoice ? (` branch below, which mounts
          the ordinary saved-mode grid with a real `invoiceId` — never rendered
          at all. Somebody opening an invoice to edit it saw a Totals panel with
          no products above it and reasonably concluded the lines were missing.

          ⚠️ **The old reasoning is not wrong about the detail page, and the
          owner overrode it knowingly.** The sentence it ends on is the point:
          `QuoteFormModal` has always rendered this band unconditionally, so the
          divergence — not the alignment — was the odd one out, and "same
          editable as quotes" is exactly what closes it. Two grids over one
          basket is a real cost: the modal's grid and the detail page's are the
          same saved mode issuing the same `/invoices/{id}/products` requests, so
          the one behind the modal can be stale after the modal writes a line.
          It refetches when the modal closes, which is what makes this
          survivable rather than free.

          ⚠️ **The Enter guard is load-bearing, not tidiness.** A line control is
          a text input inside this `<form>`, and a form with a submit button
          submits on Enter from any of them — so typing a quantity and pressing
          Enter would create the invoice. The guard cancels only the implicit
          submission, and only from a bare `<input>`: a Combobox that has already
          handled its own Enter arrives here `defaultPrevented`, and buttons and
          textareas are left alone.
        */}
        <div
          className="border-t border-border pt-4"
          onKeyDown={(event) => {
            if (event.defaultPrevented) return;
            if (event.key !== 'Enter') return;
            if (event.target instanceof HTMLInputElement) event.preventDefault();
          }}
        >
            {isConvertingDeal ? (
              /*
               * ⚠️ CONVERTING A DEAL — THE FIGURES ARE EDITABLE, and this is a
               * different branch from the quote preview below rather than a
               * variant of it.
               *
               * `POST /deals/{id}/invoices` copies every line, with its project,
               * its city set, its campaign inclusions and its region set, inside
               * the transaction that creates the invoice — and it takes a
               * `lines` override keyed by `dealLineId`, so the boxes below change
               * what that ONE server-side copy writes. This branch still posts
               * nothing itself.
               *
               * ⚠️ **The DRAFT grid must never render here.** Its rows are
               * written as `POST /invoices/{id}/products` the moment a record
               * exists, so on this path they would be appended to lines the
               * server has ALREADY copied — an invoice demanding money for the
               * same work twice, from a control that looks exactly like the one
               * on a blank form. The boxes below are NOT that.
               *
               * ⚠️ **Do not replace this with a client-side copy either.** One
               * request per line is the path most likely to drop the campaign
               * inclusions — whose read and write shapes differ under the same
               * key — and it would also lose the region set, the frozen account
               * lead and the company-seeded tax rate, none of which a create can
               * express.
               *
               * 🔴 **ITEM, PROJECT, CITIES, REGIONS AND INCLUSIONS ARE FIELDS
               * NOW, AND ROWS CAN BE ADDED AND REMOVED.** *SUPERSEDED, and it was
               * the load-bearing sentence of this whole branch: "**ITEM, PROJECT,
               * CITIES AND INCLUSIONS STAY FACTS.** The override carries none of
               * them: they are structural, and a product or project changed here
               * would need the whole line-project guard, which is the saved grid's
               * job on the invoice that results."*
               *
               * Every clause of that was a true statement about `lines`.
               * `raiseDealInvoiceSchema` takes **`lineSet`** — the complete final
               * list — which carries `productId`, `projectId`, `cityIds`,
               * `regionIds` and `inclusions`, admits an entry with no `dealLineId`
               * as a NEW line, and drops any line left out of the array. So all
               * five pickers have somewhere to put their answer, and the
               * line-project guard the old note pointed at is exactly what the
               * grid already runs.
               *
               * ⚠️ **THERE IS STILL NO TAX CONTROL HERE AND THERE MUST NOT BE.**
               * The body is `.strict()` and carries no `taxRate` and no
               * `xeroTaxType` on either key — sending one is a 422 — and
               * `f_deal_products` has no tax column to override anyway. A
               * converted line's rate comes from the company, which is what
               * {@link CONVERTED_INVOICE_LINE_TAX_RATE} says below the table and
               * what the grid's own Tax rate readout says on every row.
               *
               * ⚠️ **NO TOTALS ROW**, and the grid suppresses its own footer for
               * this mode. The four figures are already on screen in the Totals
               * band, derived one way; a second sum here could disagree with it.
               *
               * ⚠️ `[&>*]:min-w-0` is load-bearing, not decoration. A grid item
               * defaults to `min-width: auto`, so the wide table inside would
               * widen its own track instead of scrolling inside its `TableWrap`,
               * and the whole modal would overflow sideways at 320px. That exact
               * bug has shipped in this repo twice.
               */
              <div className="grid gap-2 [&>*]:min-w-0">
                <InvoiceLineItemsTable
                  mode="convert"
                  /*
                   * ⚠️ **Held in state and handed down WHOLE.** The grid re-seeds
                   * every row whenever this array's identity changes, so mapping
                   * or filtering it inline here would wipe whatever somebody is
                   * typing, with no error and nothing on screen to explain it.
                   */
                  dealLines={dealLines.lines}
                  dealLinesLoading={dealLines.loading}
                  dealLinesError={dealLines.error}
                  convertLocked={convertLinesLocked}
                  /*
                   * ⚠️ **Keyed by the grid's own ROW KEY**, which is why they are
                   * routed through `convertLineSetFieldErrors` with the `rowKeys`
                   * array that was SENT. A row added on this screen has no
                   * `dealLineId` to be addressed by.
                   */
                  convertLineErrors={lineServerErrors}
                  onConvertLinesChange={setConvertLineSet}
                  onConvertBlockedChange={setConvertBlockedRows}
                  /*
                   * ⚠️ **THE FIGURE, not a promise of one.** The Tax rate column is
                   * a readout on this path — `f_deal_products` has no tax column
                   * and neither request key carries a `taxRate` — so this is the
                   * only place the number can come from. It rides the company read
                   * this form already makes; nothing extra is fetched, and the rate
                   * is never sent back.
                   */
                  companySalesTax={companySalesTax}
                  /*
                   * ⚠️ **Empty, and structurally so rather than by omission.** This
                   * prop is the HEADER project's city set, and an invoice raised by
                   * conversion has no header project this form can express —
                   * `ID_KEYS` cannot carry one. Each row draws its own cities from
                   * its own project, inside the grid.
                   */
                  cities={NO_HEADER_CITIES}
                  /*
                   * ⚠️ **THE DEAL'S OWN CURRENCY, not `values.currency`.** The
                   * figures on these rows are the deal's; an unsaved change in the
                   * header box would label every one of them with a code they are
                   * not in.
                   */
                  currency={convertDeal.currency}
                  canEdit={ability.canCreate('invoices')}
                  /*
                   * ⚠️ **`true`, and NOT `ability.canDelete('invoices')`** — the
                   * same ruling the draft grid carries, for the same reason.
                   * Nothing here has been sent anywhere: removing a row issues no
                   * request and destroys no record on the deal OR on any invoice,
                   * so gating it on the admin-tier delete ability would trap a
                   * `basic_user` with a line they do not want and no way to take it
                   * off the form.
                   */
                  canDelete
                />
                {/*
                  ⚠️ **THE THREE STATES STAY APART, and the GRID keeps them apart
                  now.** `loading` gets skeletons, a failed read (`lines === null`,
                  not loading) gets the error and NO table — every line still
                  crosses either way — and an empty array is the deal genuinely
                  having none. That is why all three are passed down as separate
                  props rather than collapsed into one: `dealLinesError` is not
                  `dealLines.lines === null`, and reading one as the other shows
                  somebody an empty basket for a deal about to bring five products
                  across.
                */}
                {/*
                  ⚠️ Only where there is something to carry the rate. On a deal
                  with no products the sentence would describe lines that do not
                  exist — and that deal converts perfectly well, because the tax
                  check early-returns when there are no lines.
                */}
                {dealLines.lines !== null && dealLines.lines.length > 0 && (
                  <p className="m-0 text-xs break-words text-fg-muted">
                    {CONVERTED_INVOICE_LINE_TAX_RATE}
                  </p>
                )}
                {/*
                  ⚠️ Stated because the stage move happens server-side after the
                  invoice is raised (owner 2026-10-18): the deal moves to the won
                  stage unless it is already won or lost, which the reader cannot
                  see from this form.
                */}
                <p className="m-0 text-xs break-words text-fg-muted">
                  {INVOICE_CONVERSION_MOVES_THE_DEAL}
                </p>
              </div>
            ) : isConvertingQuote ? (
              /*
               * ⚠️ CONVERTING A QUOTE — ONE GRID, holding TWO POPULATIONS.
               *
               * 🔴 **SUPERSEDED IN FULL, AND EVERY WORD OF IT WAS TRUE WHEN
               * WRITTEN.** This branch used to open *"CONVERT MODE — TWO GRIDS,
               * and the split is the whole design"* and argued, at length:
               * *"**The COPIED lines are READ-ONLY, and that has not changed.**
               * … It accepts no line overrides — its body schema is
               * `z.object({}).strict()` — so an editable grid over them could
               * only discard what was typed … **This branch keeps
               * `ConvertedLinesPreview` because `convertQuoteSchema` is still
               * `z.object({}).strict()` and takes nothing at all.** … **So this
               * is NOT an unfinished half of that change, and the fix is not to
               * point this branch at the same grid.** … ⚠️ **The two must stay
               * visibly separate.** One grid holding locked rows and editable
               * rows together is how somebody types a price into a copied line
               * and believes it was sent. Two headings, two grids, two
               * behaviours."*
               *
               * **The premise died on 2026-08-31: `convertQuoteSchema` is now
               * `z.object({ lines: quoteLineOverridesArray.optional() })
               * .strict()`.** A price typed into a copied row IS sent, so the
               * reason for the split went with the fact it rested on. It is
               * quoted rather than deleted because it is the load-bearing
               * paragraph of this branch and a reader meeting only the new code
               * would think the separation had never been decided.
               *
               * ⚠️ **What the old note got RIGHT and still holds:** a grid must
               * never collect an edit the request cannot carry. That rule is why
               * `mode="quote-convert"` locks a copied row's item, project,
               * cities, regions and inclusions and gives it no trash can —
               * `lines` carries five FIGURE keys and nothing else, and an
               * omission from it means *unchanged*, not *removed*.
               *
               * ⚠️ **It is NOT `mode="convert"`.** That mode emits `lineSet`,
               * whose omissions DELETE and whose entries carry the structural
               * keys this endpoint has no place to put. Mirroring one onto the
               * other typechecks every time — see the table on
               * `InvoiceLineItemsMode`.
               *
               * ⚠️ **What the owner was actually reporting**, and why the split
               * had to go rather than be relabelled: beneath the read-only table
               * sat a second grid headed "Additional line items" whose empty
               * state read *"No line items yet"* — directly under a table full of
               * line items.
               *
               * ⚠️ **Do not replace the copy with a client-side one.** One
               * `POST /invoices/{id}/products` per copied line is the path most
               * likely to drop the inclusion set — whose read and write shapes
               * differ under the same key — and the result is a document that
               * looks complete and bills for less than the customer agreed to.
               * The grid's own `draftLines` therefore SKIPS every copied row.
               *
               * ⚠️ `[&>*]:min-w-0` below is load-bearing, not decoration. A grid
               * item defaults to `min-width: auto`, so the wide table inside
               * would widen its own track instead of scrolling inside its
               * `TableWrap`, and the whole modal would overflow sideways at
               * 320px. That exact bug has shipped in this repo twice.
               */
              <div className="grid gap-2 [&>*]:min-w-0">
                <InvoiceLineItemsTable
                  mode="quote-convert"
                  /*
                   * ⚠️ **Held in state and handed down WHOLE.** The grid re-seeds
                   * its copied rows whenever this array's identity changes, so
                   * mapping or filtering it inline here would wipe whatever
                   * somebody is typing, with no error and nothing on screen to
                   * explain it.
                   */
                  quoteLines={quoteLines.lines}
                  quoteLinesLoading={quoteLines.loading}
                  quoteLinesError={quoteLines.error}
                  /*
                   * ⚠️ EMPTY, exactly as on the ordinary create path. An invoice
                   * raised here has no HEADER project — there is no control for
                   * one and `ID_KEYS` cannot express the write — so there is no
                   * set to pass and `[]` is the true answer. It was never a
                   * line's option list in any case: each row draws its own
                   * cities from its own project.
                   */
                  cities={NO_HEADER_CITIES}
                  /*
                   * ⚠️ **The QUOTE's currency, NOT `values.currency`.** These
                   * figures are the quote's own stored columns and are copied
                   * unchanged; the header box is only a prediction until the
                   * follow-up PATCH runs. Printing them against a code somebody
                   * has just typed would re-denominate money that is not going
                   * to be re-denominated.
                   */
                  currency={convertQuote?.currency ?? null}
                  /*
                   * ⚠️ **For the ADDED rows only, and the grid enforces that.** A
                   * COPIED quote line keeps the quote's own rate — `lines` has no
                   * tax key at all — and its cell is `QuoteConvertedTaxRateCell`,
                   * which reads that rate rather than this one. A row added here
                   * is an ordinary POST after the invoice exists, so its blank
                   * percentage does take the company's rate.
                   *
                   * ⚠️ DISPLAY ONLY. Nothing here reaches `lines` or a POST body.
                   */
                  companySalesTax={companySalesTax}
                  canEdit={ability.canCreate('invoices')}
                  /*
                   * ⚠️ `true`, not `ability.canDelete('invoices')`. The only rows
                   * this can reach are ones ADDED on this screen — a copied row
                   * has no trash can at all — and such a row has never been sent
                   * anywhere, so discarding it issues no request and destroys no
                   * record. Gating it on the admin-tier delete ability would trap
                   * a `basic_user` with a row they added by mistake.
                   */
                  canDelete
                  /*
                   * ⚠️ Once the conversion has landed the invoice exists, the
                   * copied lines are written from whatever `lines` was sent, and
                   * a retried Save re-sends the HEADER alone. Every box must go
                   * dead or somebody types a price nothing will ever send.
                   */
                  convertLocked={convertedInvoice !== null || submitting}
                  quoteLineErrors={quoteLineErrors}
                  onQuoteOverridesChange={setQuoteOverrides}
                  onDraftLinesChange={setDraftLines}
                  onDraftBlockedChange={setBlockedDraftRows}
                />
              </div>
            ) : invoice ? (
              <InvoiceLineItemsTable
                invoiceId={invoice.id}
                /*
                 * The INVOICE's own cities — the HEADER project's set, already
                 * on the record the create returned, so no extra request.
                 * Deliberately not a line's option list: a line names its OWN
                 * project and `cityIds` is validated against THAT project's
                 * cities.
                 */
                cities={invoice.cities}
                /*
                 * The SAVED currency, not `values.currency`. An unsaved change
                 * in the header box would otherwise print every line figure in
                 * a code the stored lines are not in.
                 */
                currency={invoice.currency}
                /*
                 * ⚠️ **DISPLAY ONLY, and it changes nothing about what is SENT.**
                 * A row added to a saved invoice is a POST, and the server gives
                 * a blank percentage the company's rate — so the Tax rate control
                 * says so and the Amount cell previews it. Existing rows are
                 * untouched: a blank there means *leave the stored rate alone*,
                 * which the grid tests for.
                 */
                companySalesTax={companySalesTax}
                canEdit={ability.canUpdate('invoices')}
                canDelete={ability.canDelete('invoices')}
                deleteDenialReason={denialReason(ability, 'delete', 'invoices') ?? undefined}
                /*
                 * ⚠️ Said where the lines are, not only in the title bar.
                 * Someone who closes this form after a partial line failure
                 * believing nothing was created will make a second invoice.
                 */
                contextNote={createdNote}
                /*
                 * Every line write rewrites the four header totals server-side
                 * and returns none of them, so the record this form is now
                 * editing has to be re-read.
                 */
                onLinesChanged={refreshInvoice}
                /*
                 * ⚠️ The two halves of one fix: the grid tells this form it has
                 * unsaved rows, and hands it the run that saves them. Without
                 * the first, the footer Save asserts "nothing has changed" over
                 * a grid full of edits; without the second, it can say so and
                 * still do nothing about it. See `lineEdits`.
                 */
                onUnsavedChange={setLineEdits}
                registerSave={registerLineSave}
                /*
                 * ⚠️ **THE ONLY MOUNT THAT CAN FIRE THIS, and the only one the
                 * type permits it on.** A run started from the GRID's own
                 * "Save N rows" reports nothing up this form — `created` stays
                 * null on an edit, so `finish()` falls through to `onClose` and
                 * the list behind keeps the value it fetched before the modal
                 * opened. Passed straight through: this form re-reads the
                 * invoice through `onLinesChanged` and has nothing to add.
                 *
                 * The other three mounts declare it `never`, because draft,
                 * convert and quote-convert issue no line request at all —
                 * there is no write from them for anything to go stale against.
                 */
                onRowsSaved={onLinesSaved}
              />
            ) : (
              /*
               * ⚠️ CREATE MODE — the grid holds the rows and issues NO request.
               *
               * `POST /invoices/{id}/products` needs an id and there is no bulk
               * endpoint, so nothing here can be written until the invoice
               * exists. Draft mode closes that gap without lying about it: the
               * rows are edited exactly as they are on a saved invoice, handed
               * up as the very payloads the POST takes, and written by this form
               * the moment the create returns. One Save, two phases, and the
               * second phase is reported line by line rather than as one
               * "Saved".
               */
              <InvoiceLineItemsTable
                mode="draft"
                /*
                 * ⚠️ **THE PLANNED BASKET, SEEDED ONCE AT MOUNT — and CREATE
                 * ONLY by construction rather than by a flag.** This branch
                 * renders only while `invoice` is null, so an edit and a saved
                 * create both mount the SAVED grid instead and never reach this
                 * prop. That is the same guarantee `withPrefill` gets from being
                 * called in a `useState` initialiser, obtained here from where
                 * the JSX sits.
                 *
                 * ⚠️ **The grid reads it in its own `useState` initialiser and
                 * never again**, so this may be a fresh array on every render —
                 * which it is: the page maps `plannedProductLines(row)` inline.
                 * Nothing re-seeds, so a row deleted here stays deleted.
                 *
                 * ⚠️ **These become ordinary draft rows and are written by the
                 * EXISTING two-phase create** — invoice first, then each line
                 * through `writeDraftLines`, then `lineOutcome` reports any that
                 * were refused. There is no second write path and no way for a
                 * seeded row to be sent twice: it reaches `draftLines` through
                 * `onDraftLinesChange` like every other row.
                 */
                initialDraftRows={prefill?.lines}
                /*
                 * ⚠️ EMPTY, permanently — and this now matches `QuoteFormModal`
                 * exactly. An invoice raised from this form has no header
                 * project at all (there is no control for one, and `ID_KEYS`
                 * cannot express the write), so there is no set to pass and `[]`
                 * is the true answer rather than a placeholder. It was
                 * `projectCities`, the chosen header project's set, back when
                 * this form chose one.
                 *
                 * It was never a line's option list in any case: each draft row
                 * draws its own cities from its own project, inside the grid.
                 */
                cities={NO_HEADER_CITIES}
                /*
                 * The TYPED currency, not a saved one — there is no saved one
                 * yet. Uppercased, because that is what will be stored and a
                 * lowercase code is a 422 rather than a value to preview.
                 */
                currency={
                  values.currency.trim() === '' ? null : values.currency.trim().toUpperCase()
                }
                /*
                 * ⚠️ **THE MOUNT THE OWNER'S REPORT WAS ABOUT.** Every row here
                 * is a POST after the invoice is created, so a blank percentage
                 * is given the company's rate server-side — and until this prop
                 * existed the Tax rate control read "Xero default" and the Amount
                 * cell previewed Tax 0.00 over it.
                 *
                 * ⚠️ **DISPLAY ONLY. It reaches no payload**: `createPayload`
                 * still omits `taxRate` when the box is blank, so the SERVER
                 * decides at Save with the rate then in force rather than the one
                 * that happened to be read when this modal opened.
                 */
                companySalesTax={companySalesTax}
                canEdit={ability.canCreate('invoices')}
                /*
                 * ⚠️ `true`, and NOT `ability.canDelete('invoices')`.
                 *
                 * On a saved invoice, removing a line is
                 * `DELETE /invoices/{id}/products/{lineId}` and is admin-tier. A
                 * draft row has never been sent anywhere: removing it issues no
                 * request and destroys no record. Gating it on the delete
                 * ability would trap a `basic_user` with a row they added by
                 * mistake and cannot take off the form.
                 */
                canDelete
                onDraftLinesChange={setDraftLines}
                onDraftBlockedChange={setBlockedDraftRows}
                /*
                 * 🔴 **`contextNote` IS NO LONGER PASSED on the draft mount —
                 * owner's stricter pass, 2026-09-05.** It rendered "Saved with
                 * the invoice — invoice first, then each line. Nothing is
                 * undone if one fails."
                 *
                 * ⚠️ **It was the one place the create's NON-ATOMICITY was
                 * stated BEFORE it could bite**: the invoice is written first
                 * and each line after it, so an invoice can exist while a line
                 * was refused. Under the narrowed bar a warning about a write
                 * that has not happened yet is not a refusal.
                 *
                 * ⚠️ **The after-the-fact report is untouched** — the saved
                 * mount still passes `createdNote`, and the partial-save
                 * sentences are explicitly kept. Never reword either into a
                 * promise that nothing is saved unless everything is.
                 */
              />
            )}
        </div>

        {/*
         * ⚠️ **THERE IS NO TERMS BOX ON AN INVOICE — removed 2026-08-31, owner's
         * instruction: "on the invoice, remove this Terms section from the create
         * and edit modal both, not required here".**
         *
         * *SUPERSEDED, the comment this block used to carry: "The closing row,
         * and the one place this form deliberately puts two unrelated things side
         * by side: the wording the customer reads, and the figures they are asked
         * to pay. Xero's own screen ends the same way, and it is the right
         * pairing to see together at the moment of writing either one. ⚠️ Side by
         * side is a LAYOUT fact and nothing more … including anything typed into
         * Terms on the left."* The pairing it defended has one half left, so the
         * row is a single column now.
         *
         * ⚠️ **THE REASON IS STRONGER THAN TIDINESS, AND IT IS THE HALF WORTH
         * KEEPING: A XERO INVOICE HAS NO `Terms` FIELD AT ALL.** Measured twice
         * on the backend — absent from Xero's `Invoice` schema while present on
         * `Quote`, and 0 of 500 live invoices carry one — so `xero-invoice.payload
         * .ts` declares `Terms?: never` and the push DROPS the value silently.
         * The box asked a person to write wording that could never leave EMERGE.
         * ⚠️ **`QuoteFormModal` KEEPS ITS Terms box and must**: terms are real on
         * a quote, which is the document that gets signed. Do not "harmonise" the
         * two forms.
         *
         * ⚠️ **`terms` IS STILL A FORM VALUE, A `TEXT_KEY` AND A PAYLOAD KEY, AND
         * REMOVING IT WOULD BE A DIFFERENT AND WORSE CHANGE.** `d_invoices.terms`
         * still exists and `POST /quotes/{id}/convert` still copies a quote's
         * terms onto the invoice it raises, column for column. Because the value
         * is seeded from the record and nothing can now change it, it never
         * appears in `changedKeys`, so no PATCH from this form ever names it and
         * no create sends it (the create omits an empty text key). **Deleting the
         * key instead would make this form the thing that blanks copied terms.**
         *
         * ⚠️ **THE CONSEQUENCE, STATED SO THE NEXT READER DOES NOT FILE IT AS AN
         * OVERSIGHT: an invoice converted from a quote already carries that
         * quote's terms, and that text is now invisible and uneditable here.** It
         * is not lost and it never reached a customer through this system — the
         * push drops it — so nothing is owed. It is simply stored and unread.
         */}
        <div className="grid gap-4 [&>*]:min-w-0">
          {/*
            * The four totals are the one genuinely dangerous part of this form.
            *
            * They are STORED columns with two competing writers: a line-item
            * create, update or delete rewrites all four from the lines in the
            * same transaction, while a POST or PATCH on the invoice writes
            * literally what it is sent and never re-derives anything. Nothing
            * reconciles the two, and the overwritten figures are not kept
            * anywhere. So the header may only own the totals on an invoice that
            * has no lines, and the form has to say so rather than sitting four
            * innocent money boxes next to the dates.
            *
            * Hence: collapsed by default so it cannot be stumbled into, always
            * mounted so collapsing it does not throw typing away, and replaced
            * by a read-only note the moment `hasLineItems` says the lines are in
            * charge.
            *
            * ⚠️ This is the ONE section of the form the wide layout did not
            * un-collapse, and that is on purpose: every other band folded open
            * because being visible is harmless, while these four boxes are
            * exactly the thing that should take a deliberate act to reach.
            */}
          {totalsLocked ? (
            <FormSection
              title="Totals"
              summary={
                draftLinesPending
                  ? 'The line items below will own these figures'
                  : countUnknown
                    ? 'Line items may own these figures'
                    : 'The line items own these figures'
              }
              expanded
              onToggle={undefined}
              errorCount={0}
            >
              <p className="text-xs text-fg-muted">
                {/*
                  ⚠️ The create-with-draft-lines wording is a THIRD case, not a
                  variant of the other two, and it has to be present tense about
                  a future write: the boxes disappear the moment a line is added,
                  before either the invoice or the line exists. Without this
                  sentence a typed figure appears to have been thrown away.

                  The other two were shortened in the copy pass; this one was
                  not, because "why the control is missing" is the named
                  carve-out rather than an explanation of how the form works.
                */}
                {draftLinesPending
                  ? 'Worked out from the line items — remove them all to enter figures by hand.'
                  : countUnknown
                    ? 'Where an invoice has line items, the server rewrites these from them.'
                    : 'Rewritten from the line items on every line change.'}
              </p>
              <dl className="mt-3 grid gap-2 sm:grid-cols-2 [&>*]:min-w-0">
                {TOTAL_KEYS.map((key) => (
                  <div key={key} className="flex items-baseline justify-between gap-3">
                    <dt className="text-xs text-fg-secondary">{TOTAL_LABELS[key]}</dt>
                    <dd className="text-sm text-fg">
                      <InvoiceMoney
                        /*
                         * ⚠️ On a QUOTE convert the figure is the QUOTE's, taken
                         * from the seeded value — that route copies all four
                         * verbatim, so this is what the invoice will hold. There
                         * is no `invoice` yet, and rendering a dash over four
                         * real totals would read as "this converts to nothing" on
                         * the one screen where the money is the decision.
                         *
                         * ⚠️ **On a DEAL convert all four seeds are `''` and a
                         * dash is the CORRECT answer, not a gap.**
                         * `invoiceBaselineFromDeal` leaves them empty because
                         * `POST /deals/{id}/invoices` **recomputes** them from
                         * the lines it copies, in the same transaction — the
                         * deal's own `amount` is deliberately not carried, since
                         * a header figure no line justifies is how a bill for the
                         * wrong amount reaches Xero. So the `.trim() !== ''`
                         * guard is doing real work here rather than guarding an
                         * impossible case: it is what stops a confident `0.00`
                         * being printed over four figures nobody has computed
                         * yet. **Do not "fix" this by seeding the deal's amount.**
                         */
                        value={
                          invoice
                            ? invoice[key]
                            : isConverting && values[key].trim() !== ''
                              ? values[key]
                              : null
                        }
                        currency={values.currency === '' ? null : values.currency}
                      />
                    </dd>
                  </div>
                ))}
              </dl>
              {isEdit && invoice && (
                <InvoiceCreditSummary invoiceId={invoice.id} reloadKey={creditSummaryKey} />
              )}
            </FormSection>
          ) : (
            <FormSection
              title="Totals"
              summary="Only for an invoice with no line items"
              expanded={totalsExpanded}
              onToggle={() => setTotalsOpen((prev) => !prev)}
              errorCount={totalsErrorCount}
            >
              <p className="text-xs text-fg-muted">
                Adding a line item replaces these figures.
              </p>
              <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 [&>*]:min-w-0">
                {TOTAL_KEYS.map((key) => (
                  /*
                    The null-versus-zero hint ("Blank means not recorded, not
                    zero.") went in the owner's stricter pass, 2026-09-05, and
                    so did the quote form's `title` carrying the same fact —
                    which settles the two having drifted apart.

                    ⚠️ The COLUMNS still differ and nothing on screen says so:
                    `discountTotal` and `taxTotal` are NULLABLE, `subTotal` and
                    `grandTotal` are NOT NULL DEFAULT 0.00.
                  */
                  <Field key={key} label={TOTAL_LABELS[key]} error={errors[key]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        // Text with a numeric mode, not type="number": the value
                        // is an exact decimal string end to end, and a number
                        // input hands back a rounded or locale-formatted one.
                        inputMode="decimal"
                        value={values[key]}
                        placeholder="1800.00"
                        invalid={Boolean(errors[key])}
                        aria-describedby={describedBy}
                        onChange={(event) => setField(key, event.target.value)}
                      />
                    )}
                  </Field>
                ))}
              </div>
              {isEdit && invoice && (
                <InvoiceCreditSummary invoiceId={invoice.id} reloadKey={creditSummaryKey} />
              )}
            </FormSection>
          )}
        </div>

        {/*
          THE NOTE — last thing on the form, on the owner's instruction, and the
          same block in the same place as `QuoteFormModal`.

          ⚠️ **CREATE ONLY**, and the condition below is what says so: on an
          invoice that already existed when this form opened, notes belong in the
          record's own Tasks & notes drawer, which lists them, edits them and
          deletes them. The `justCreated` half keeps the section alive across the
          moment this form turns into an edit of the invoice it just made —
          without it the outcome of the note write would be unmounted in the same
          render that produced it, and a refusal would never be seen.

          🔴 **`!isConverting` is load-bearing, not tidiness.** A conversion runs
          through `POST /quotes/{id}/convert` and then `onSaved` + `onClose` — it
          never sets `justCreated`, and it unmounts this form on the way to the
          invoice it produced. A box rendered on that path would take what
          somebody typed, write it nowhere, and close over it: no request, no
          error, no report — the "it looks like it worked" failure in its purest
          form. So it is not offered. Attaching a note to a conversion would mean
          writing it after the navigation and reporting it somewhere else
          entirely, which is a different feature and needs asking for.

          ⚠️ It is NOT a field on the invoice. It is a `d_notes` row written by a
          separate request, so it never enters `buildPayload`, never appears in
          `KNOWN_WRITE_KEYS`, and its refusal is never routed onto a header
          control by `routeFieldErrors`.
        */}
        {((!isEdit && !isConverting) || justCreated) && (
          <DraftNoteSection
            documentNoun="invoice"
            value={noteDraft}
            onChange={setNoteDraft}
            disabledReason={noteDenial}
            outcome={noteOutcome}
            created={justCreated}
            onRetry={() => void retryDraftNote()}
            retrying={savingNote}
          />
        )}
      </form>

      {/*
        The exchange-rate dialog.

        A SIBLING of the `<form>`: React propagates by its own tree rather than
        the DOM's, so a control inside the form's subtree can reach the form's
        `onSubmit` even when it is portalled elsewhere in the document — which
        is how a nested picker once created a half-filled record in this repo.
        A sibling has no such path at all, which is a stronger guarantee than
        remembering to stop the event. The dialog contains no `<form>` either.
      */}
      {rateDialogOpen && baseCurrency !== null && documentCurrency !== null && (
        <ExchangeRateDialog
          baseCurrency={baseCurrency}
          documentCurrency={documentCurrency}
          initialRate={values.currencyRate.trim()}
          onCancel={() => setRateDialogOpen(false)}
          onSave={(next) => {
            setField('currencyRate', next);
            setRateDialogOpen(false);
          }}
        />
      )}
    </Modal>
  );
}

/**
 * One READ-ONLY, DERIVED row: a label, and either the value or a sentence
 * saying why there is none and where it is set.
 *
 * ⚠️ **The value branch is the pattern the Company, Account lead and Project
 * rows above already use, character for character** — a plain `Input` with
 * `readOnly`, `aria-readonly` and `bg-surface-sunken text-fg-secondary`. It is
 * NOT a disabled control, and the difference is what the reader concludes: a
 * disabled picker says "you have not earned the right to use this" and sends
 * somebody hunting for a permission, where text says "this is a fact", which is
 * what a value taken from the company master is.
 *
 * ⚠️ **The absence branch is the part that is new, and it exists because an
 * empty read-only box is the failure mode of this whole idea.** Account lead and
 * Project simply do not render when they have nothing — they can, because
 * neither is a value anybody is waiting on. These three are: a person who opened
 * this form to raise an invoice needs to know that the currency is blank because
 * the COMPANY records none, and that the company record is where it is fixed. A
 * blank box says neither, and there is no control left to poke to find out.
 *
 * ⚠️ **It is a refusal/explanation, not helper text, and the no-helper-text rule
 * exempts exactly this.** The test the rule states is *what wrong conclusion does
 * this prevent?* — here, "this invoice will be billed in nothing" and "somebody
 * has broken the form". Do not delete these sentences in a copy pass.
 *
 * The absence is a `<p>` rather than an empty `Input` on purpose. `Field`'s
 * `<label htmlFor>` then points at a non-labelable element and is inert, which
 * is correct: there is no control to focus, and a label that focused a sentence
 * would be claiming there is.
 */
function DerivedField({
  label,
  value,
  absence,
  title,
  source,
  error,
}: {
  label: string;
  /** The derived value, or `null` when nothing derives. */
  value: string | null;
  /** What to say instead, when `value` is `null`. Never blank. */
  absence: string;
  /** Where the value is really set — on the value branch only. */
  title: string;
  /**
   * Which record the value came from, or `null` when nothing here derived it.
   *
   * ⚠️ **Only `'billing-region'` renders visible text, and that asymmetry is
   * the decision.** A value taken from the company is what every label on these
   * three rows already implies, so naming it adds a line and prevents no wrong
   * conclusion — it is helper text and the standing prohibition applies. A value
   * taken from the BILLING REGION comes from a record the reader may not know
   * exists: without the line they open the company record to change it, find the
   * field empty, and conclude the form is lying about where the value lives.
   * That is a wrong conclusion prevented, which is the exception the
   * no-helper-text rule states in as many words.
   *
   * ⚠️ **`title` alone would NOT do here** — it is hover-only, so it is invisible
   * on touch and to anyone not hunting for it, and this form is required to work
   * at 320px. The tooltip stays as the fuller sentence; this is the fact.
   */
  source?: DerivedSource | null;
  error?: ReactNode;
}) {
  return (
    <Field label={label} error={error}>
      {({ id, 'aria-describedby': describedBy }) =>
        value === null ? (
          /*
           * Sized by its content rather than pinned to `h-9`: these sentences
           * wrap to two and three lines at 320px, which is the width this form
           * is required to collapse to and the width a fixed height would clip
           * them at.
           */
          <p
            id={id}
            aria-describedby={describedBy}
            className="rounded-md border border-border bg-surface-sunken px-2.5 py-2 text-sm text-fg-secondary"
          >
            {absence}
          </p>
        ) : (
          <>
            <Input
              id={id}
              value={value}
              readOnly
              aria-readonly="true"
              title={title}
              invalid={Boolean(error)}
              className="bg-surface-sunken text-fg-secondary"
              aria-describedby={describedBy}
            />
            {source === 'billing-region' && (
              /*
               * ⚠️ **It names the region as the source and the company as the
               * override, because those are the two things a reader does next**
               * — either go and change the regional default, or give this one
               * account its own value. A line saying only "from the billing
               * region" answers the first question and leaves the second, which
               * is the one somebody raising an invoice actually has.
               *
               * `fg-subtle` and `text-xs`: it is a provenance note under a row
               * that is already read-only, not a second label competing with
               * the value.
               */
              <p className="mt-1 text-xs text-fg-subtle">
                From the billing region — the company records none.
              </p>
            )}
          </>
        )
      }
    </Field>
  );
}

/**
 * What is known about converting this invoice into the organisation's base
 * currency, said under the Currency row — the position Xero puts it in.
 *
 * ⚠️ **Six states, and each says a different thing to the reader.** The
 * temptation is one sentence covering "there is no converted figure", because
 * that is what they have in common to a renderer. It is the wrong sentence in
 * four of the six:
 *
 * - **`base-unknown`** is not a missing rate, it is a missing CONNECTION, and
 *   the fix is on `/integrations`. Wording it as a missing rate would send
 *   somebody to a dialog that cannot help them — the dialog needs a base
 *   currency to state a rate against, which is why it is not offered here.
 * - **`same`** is not a gap at all. An invoice already in the base currency has
 *   nothing to convert, and copy implying something is missing would have
 *   people pinning rates of 1 to make a warning go away — the fabricated parity
 *   every comment about this field warns about.
 * - **`checking`** is a moment, not a state of the data. Saying "not connected"
 *   during it would be a claim we cannot yet support, and it flickers.
 * - **`no-currency`** is answered by the row sitting directly above, which says
 *   in words that no currency is set and that it comes from the company record.
 *   A second sentence saying so is noise. ⚠️ *SUPERSEDED 2026-08-31: "answered
 *   by the picker sitting directly above, reading 'Not set'."* The picker is
 *   gone — the currency is derived and read-only now — and the sentence that
 *   replaced its "Not set" is strictly more useful, so the conclusion is
 *   unchanged and only its subject moved.
 *
 * ⚠️ **It renders `error` in every state, including the ones with no body.**
 * `currencyRate` is in this form's `KNOWN_WRITE_KEYS`, so a 422 naming it is
 * marked rather than bannered — and a marked key with nowhere on screen to
 * appear is the "one field needs attention, highlighted below" failure with
 * nothing highlighted. This is that somewhere.
 *
 * ⚠️ **It is NO LONGER a duplicate of `QuoteFormModal`'s note, and the
 * divergence is DELIBERATE.** The two were word for word the same, with a note
 * here saying that rewording either one meant extracting both into
 * `@/components/documents/` rather than letting a quote and the invoice it
 * converts into describe one field two ways. That still holds for VOCABULARY.
 * This is not vocabulary — **the two documents behave differently**:
 *
 *  - an **invoice** push reads `CurrencyRate` back off the write response and
 *    stores it (`xero-invoice.service.ts` → `recordXeroCurrencyRate`), on an
 *    update as well as a create, so a typed rate is REPLACED by Xero's;
 *  - a **quote** push writes back only the quote number. There is no quote
 *    equivalent of that call, so a typed rate SURVIVES.
 *
 * So the copy here states the replacement and the quote's deliberately does
 * not. **Do not "bring the two into line".** One wording across both would put
 * a claim on one of the forms that is false there — the failure this repo
 * catalogues under "copy is the one part of a contract that nothing
 * typechecks". If the pair is ever extracted into a shared component, the
 * replacement sentence has to arrive as a PROP, never as shared text.
 */
// `documentCurrency` was a prop here and is gone: its only reader was the
// `same` branch's sentence, removed 2026-09-05.
function ExchangeRateNote({
  state,
  baseCurrency,
  rate,
  exchangeRate,
  error,
  onSet,
  onRemove,
}: {
  state: 'no-currency' | 'checking' | 'base-unknown' | 'same' | 'unset' | 'set';
  baseCurrency: string | null;
  rate: string;
  exchangeRate: ExchangeRate | null;
  error: string | undefined;
  onSet: () => void;
  onRemove: () => void;
}) {
  const body = (() => {
    switch (state) {
      case 'no-currency':
        return null;

      /*
       * Both silent since the owner's stricter pass, 2026-09-05. They rendered
       * "Checking Xero for the base currency…" and "Xero is not connected, so
       * the base currency is not known." — a loading state and an explanation
       * of an absent figure, neither a refusal nor a money warning.
       *
       * ⚠️ The reason there is no "Set exchange rate" button on `base-unknown`
       * is UNCHANGED and is not a copy decision: a rate is a pair of currencies
       * and one of them is missing, so the dialog could not even state the
       * question — `1 ??? = x AUD`. Do not add one now that the sentence
       * explaining the emptiness has gone.
       */
      case 'checking':
      case 'base-unknown':
        return null;

      case 'same':
        /*
         * The ordinary case is silence: this invoice is in the base currency
         * and there is nothing to convert.
         *
         * 🔴 The sentence for a rate that is still STORED went in the stricter
         * pass: "A rate of {rate} is stored, but this invoice is already in
         * {currency}." The CONTROL stays — a control is not copy, and this is
         * the only way to remove such a rate.
         *
         * ⚠️ The rate is still not cleared on the currency's behalf — silently
         * discarding a recorded figure is data loss dressed as tidying — so the
         * button now appears with nothing saying why.
         */
        if (rate === '') return null;
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            <Button variant="ghost" size="sm" onClick={onRemove}>
              Remove rate
            </Button>
          </div>
        );

      case 'unset':
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            <span className="min-w-0">
              No rate set &mdash; Xero uses its daily rate on push, and records it here.
            </span>
            <Button variant="secondary" size="sm" onClick={onSet}>
              Set exchange rate
            </Button>
          </div>
        );

      case 'set':
        /*
         * `exchangeRate` is null here only when the stored string will not
         * parse — an older record, or a rate with more than six decimal places.
         * It is reported rather than rendered as absent: an invoice that
         * silently showed no conversion, with a rate sitting in the column,
         * would look like the feature was off.
         */
        if (exchangeRate === null) {
          return (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-warning">
              <span className="min-w-0">
                The stored rate <span className="tabular-nums">{rate}</span> cannot be read, so
                no {baseCurrency} figure is shown.
              </span>
              <Button variant="secondary" size="sm" onClick={onSet}>
                Replace it
              </Button>
            </div>
          );
        }
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            {/*
              ⚠️ This parenthetical said "(entered by hand)" and that is NO
              LONGER TRUE of every rate. An invoice push stores the
              `CurrencyRate` Xero applied, so the figure sitting here after one
              is XERO's rather than the typed one — and **nothing on the record
              says which**, because the second column that would have recorded
              it was offered to the owner and declined.

              So the parenthetical states what HAPPENS to the rate instead of
              where it came from. Provenance is the one claim this field can no
              longer support, and the original reason not to assert a false one
              is unchanged: it sits on a figure a customer is billed against,
              and it is the part of this line nothing would ever check.

              Xero prints `1 GBP = 1.89207 AUD (Source: XE, 27 Aug 2026)` here.
              Do not borrow that wording either — same reason, other direction.
            */}
            <span className="min-w-0 tabular-nums text-fg-secondary">
              {formatRateLine(exchangeRate)}
            </span>
            <span
              className="min-w-0"
              title="A push replaces this with the rate Xero applied."
            >
              (replaced by Xero&rsquo;s rate on push)
            </span>
            <Button variant="ghost" size="sm" onClick={onSet}>
              Change
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemove}>
              Remove
            </Button>
          </div>
        );
    }
  })();

  if (body === null && error === undefined) return null;

  return (
    <div className="space-y-1">
      {body}
      {error !== undefined && (
        <p role="alert" className="text-xs break-words text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Enter a custom exchange rate, pinned to this document.
 *
 * ⚠️ **Custom only, and the automatic rate is NOT offered as a disabled
 * control.** Xero's own dialog gives two choices — its provider's mid-market
 * rate, or a rate you type. Ours gives one, because the provider half needs an
 * API this app cannot reach: the currency service the owner supplied speaks
 * plain HTTP on a non-TLS port and sends no CORS header at all, so a browser on
 * an HTTPS origin is blocked twice over, and the owner ruled it out by name in
 * favour of this field. A backend proxy has been asked for and does not exist.
 * Until it does, a "Use today's rate" button could only ever fail, and this
 * repo's standing rule is that such an affordance is absent rather than inert.
 * **The seam is here**: when the proxy lands, this dialog gains a second choice
 * and the rate starts having a source other than a person. ⚠️ That seam is
 * already half-open, from the other end: an invoice push stores the rate Xero
 * applied, so `ExchangeRateNote` has stopped naming a source at all rather than
 * naming one it cannot know. Nothing in this file fetches a rate today.
 *
 * ⚠️ **The draft lives here and nowhere else.** It reaches the form's values
 * on Save only, so cancelling cannot leave a half-typed rate in a payload — and
 * because the form's own diff drives the PATCH, an unchanged rate is not sent
 * at all.
 *
 * There is no `<form>` element inside: the dialog is portalled but React
 * propagates by its own tree, so a submit here could otherwise reach the invoice
 * form and PATCH the header. A button and an onClick cannot.
 */
function ExchangeRateDialog({
  baseCurrency,
  documentCurrency,
  initialRate,
  onCancel,
  onSave,
}: {
  baseCurrency: string;
  documentCurrency: string;
  initialRate: string;
  onCancel: () => void;
  onSave: (rate: string) => void;
}) {
  const [draft, setDraft] = useState(initialRate);
  const trimmed = draft.trim();
  const valid = isValidCurrencyRate(trimmed);
  /*
   * An empty box is not an error, it is an unfinished one — the box opens empty
   * on an invoice with no rate, and reddening it before a keystroke would be
   * scolding someone for not having typed yet. Save is simply unavailable.
   * Removing a rate is `Remove` on the note, not an emptied box here, so there
   * is no clearing intent this refusal could be blocking.
   */
  const error = trimmed !== '' && !valid;

  return (
    <Modal
      open
      onClose={onCancel}
      title="Set exchange rate"
      /*
       * The DIRECTION is the whole content of this sentence, and getting it
       * backwards is a silent factor-squared error rather than a visible one:
       * `1 GBP = 1.89 AUD` and `1 AUD = 0.53 GBP` are both plausible-looking
       * numbers. Xero states it base-first, so this does too, and the field
       * label repeats it rather than relying on the reader holding it.
       */
      description={`How many ${documentCurrency} one ${baseCurrency} buys — the direction Xero states a rate in.`}
      size="sm"
      // Losing a typed rate to a clipped backdrop is losing real work.
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            onClick={() => {
              if (!valid) return;
              onSave(trimmed);
            }}
          >
            Save rate
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={`Exchange rate — 1 ${baseCurrency} in ${documentCurrency}`}
          error={
            error
              ? `Use a rate above zero with at most ${CURRENCY_RATE_MAX_DP} decimal places.`
              : undefined
          }
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-sm text-fg-secondary">1 {baseCurrency} =</span>
              <Input
                id={id}
                /*
                 * Text with a numeric mode, never `type="number"`. The value is
                 * an exact decimal string end to end — a number input hands
                 * back a rounded or locale-formatted one, and a rate is the
                 * value in this form where six decimal places actually matter.
                 */
                inputMode="decimal"
                value={draft}
                placeholder="1.89207"
                invalid={error}
                aria-describedby={describedBy}
                onChange={(event) => setDraft(event.target.value)}
              />
              <span className="shrink-0 text-sm text-fg-secondary">{documentCurrency}</span>
            </div>
          )}
        </Field>

        {/*
          ⚠️ Kept under the copy rule that a sentence stays when it prevents a
          specific wrong conclusion. Anyone who has used Xero's own dialog will
          look for the provider rate and, not finding it, conclude the control
          is broken or that they have missed a setting. This says which of the
          two options exists here and why the other does not.
        */}
        {/*
          🔴 "Xero's automatic mid-market rate is not available here yet." went
          in the owner's stricter pass, 2026-09-05 — it explained a missing
          affordance rather than warning about money or reporting a refusal.

          ⚠️ The wrong conclusion it prevented is back: anyone who has used
          Xero's own dialog will look for the provider rate and, not finding it,
          decide the control is broken. Why it is genuinely absent is on this
          component's doc comment.
        */}

        {/*
          What a pinned rate DOES, said once. Without it the choice reads as a
          display preference, which is the wrong conclusion in the direction
          that costs money: it is written to the document and travels to Xero.

          ⚠️ The THIRD sentence is the one not to drop. A push stores the rate
          Xero applied back onto the invoice, so what is typed here does not
          survive it — and the only place a person could learn that is the box
          they are typing into. Without it the reasonable conclusion is that a
          rate entered here is the rate that stays on the document, which is
          wrong about a figure printed beside a customer's total.

          It is deliberately the FACT and its consequence, not the mechanism:
          the write-response readback is the backend's business, and a sentence
          describing it would date the moment they change how they read it.
        */}
        <p className="text-xs text-fg-muted">
          Pushing to Xero replaces this with the rate Xero applied.
        </p>
      </div>
    </Modal>
  );
}

/**
 * Server-side search for the company, contact and deal pickers.
 *
 * All three directories are larger than one page, so filtering a fetched page
 * locally would only ever search the first 100 records. The Combobox debounces
 * the term itself, so there is no second debounce here.
 *
 * The record's existing link is pinned into the list: a search that does not
 * happen to return it must not make the field look unassigned, because an
 * unassigned-looking field saves as a `null` that quietly breaks the link.
 *
 * The deal branch differs twice over — its label comes from `dealName` rather
 * than `name`, and `/deals` takes no sort parameter at all.
 *
 * ⚠️ **The contacts branch is SCOPED to one company** (`scopeCompanyId`), which
 * is a different thing from the `enabled` flag below it and behaves differently
 * in the one case that matters: a disabled picker keeps whatever it last read,
 * while an unscoped one is EMPTY on purpose. See `src/lib/contact-scope.ts` for
 * the whole rule, the wording, and the measurement that the API does not check
 * the pair itself.
 */
function useLookupSearch(
  resource: 'companies' | 'contacts' | 'deals',
  pinned: { id: number; label: string } | null,
  /** `false` when the picker is not rendered, so no request is made at all. */
  enabled = true,
  /**
   * The company the contact list is scoped to. `undefined` on the two unscoped
   * pickers; `null` means "this IS a scoped picker and no company is chosen",
   * which fetches nothing and offers nothing — see `CONTACT_NEEDS_COMPANY`.
   */
  scopeCompanyId?: number | null,
  /**
   * COMPANIES ONLY — withhold every company that is not `approved`, and report
   * how many were withheld.
   *
   * ⚠️ **The optimisation, never the guarantee.** A company's approval can move
   * between this list being read and Save being pressed, so `validate()` refuses
   * again from the company's own record. Narrowing here only keeps a choice
   * whose one outcome is that refusal off the list.
   *
   * ⚠️ **A withheld company must not vanish silently.** One that is plainly on
   * `/companies` and absent here reads as a broken fetch, so the count goes into
   * the Combobox FOOTNOTE — the same exception to the no-helper-text rule as
   * "Showing the first 100 matches", and for the same reason: it is a fact about
   * the list, not advice about how to use it.
   *
   * ⚠️ **The PIN is never withheld**, in any mode. An invoice already on a
   * company since rejected must still render its name, or the field reads
   * "Unassigned" over a record that holds one and the next save writes that
   * emptiness back. That is why the withholding happens on the fetched rows.
   */
  withholdUnapproved = false,
) {
  const [term, setTerm] = useState('');
  /*
   * Rows and the scope they ANSWER FOR, in one state, so "which company is this
   * list for" and "which company is selected" can never disagree for a frame —
   * the frame in which somebody could otherwise pick the previous company's
   * contact. Derived rather than cleared in a second effect, exactly as
   * `ProjectFormModal` tags its intakes with their cycle.
   *
   * ⚠️ That frame is worth closing here even though the server would accept the
   * write: it accepts it SILENTLY, so there is no 422 to fall back on.
   */
  const [result, setResult] = useState<{
    scope: number | null | 'unscoped';
    rows: { id: number; label: string }[];
    truncated: boolean;
    failed: boolean;
    /**
     * How many rows this page dropped for not being approved.
     *
     * ⚠️ **0 and "we did not look" are different facts and must not merge.** It
     * is 0 on every unfiltered list — contacts, deals, and companies on an edit
     * or a convert — which is true of those lists rather than a claim that
     * nothing was withheld. Only ever read beside `withholdUnapproved`.
     */
    withheld: number;
  } | null>(null);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  const scope: number | null | 'unscoped' =
    resource === 'contacts' ? scopeCompanyId ?? null : 'unscoped';

  useEffect(() => {
    if (!enabled) return;
    // No company, nothing to fetch. The empty answer is DERIVED below rather
    // than written here: a `setState` in this branch would be a render's worth
    // of cascade to reach a value already known during render.
    if (scope === null) return;

    const controller = new AbortController();
    const named = { search: term, perPage: 100, sort: 'name', order: 'asc' } as const;

    const request: Promise<{
      rows: { id: number; label: string }[];
      totalPages: number;
      withheld: number;
    }> =
      resource === 'companies'
        ? api.listCompanies(named, controller.signal).then((result) => {
            /*
             * ⚠️ **Filtered on the ENUM, never on a name.** `approvalStatus` is a
             * literal union — `'pending' | 'approved' | 'rejected'` — and
             * `types.ts` says why it is safe to match where a stage or status
             * NAME is not: those three values ARE the flow, so an admin adding a
             * fourth would be changing the workflow rather than adding a row.
             *
             * ⚠️ **`=== 'approved'`, written out, rather than excluding the two
             * negatives.** A fourth member added upstream would then be offered
             * by default; this way it is withheld and counted, which is the
             * visible wrong answer rather than the silent one.
             */
            const keep = withholdUnapproved
              ? result.data.filter((row) => row.approvalStatus === 'approved')
              : result.data;
            return {
              rows: keep.map((row) => ({ id: row.id, label: row.name })),
              totalPages: result.meta.totalPages,
              withheld: result.data.length - keep.length,
            };
          })
        : resource === 'contacts'
          ? // ⚠️ A SINGLE id — `?companyId=15,16` is a 422 on this endpoint.
            api
              .listContacts({ ...named, companyId: scope as number }, controller.signal)
              .then((result) => ({
                rows: result.data.map((row) => ({ id: row.id, label: row.name })),
                totalPages: result.meta.totalPages,
                withheld: 0,
              }))
          : api.listDeals({ search: term, perPage: 100 }, controller.signal).then((result) => ({
              rows: result.data.map((row) => ({ id: row.id, label: row.dealName })),
              totalPages: result.meta.totalPages,
              withheld: 0,
            }));

    request
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({
          scope,
          rows: response.rows,
          truncated: response.totalPages > 1,
          failed: false,
          withheld: response.withheld,
        });
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody a company has no contacts when the request simply did not
         * come back sends them to create a person who already exists.
         *
         * ⚠️ **`withheld: 0` here is "we withheld nothing", NOT "nothing was
         * unapproved"** — the request never came back, so there is nothing to
         * report and the footnote must stay silent rather than claim a count of
         * zero. The SAVE reads the company's own record, not this, and a failed
         * read there lets the save through.
         */
        if (!controller.signal.aborted) {
          setResult({ scope, rows: [], truncated: false, failed: true, withheld: 0 });
        }
      })
      .finally(() => {
        // Records which term the list now answers, so `loading` is derived
        // during render rather than toggled from inside the effect.
        if (!controller.signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
    /*
     * ⚠️ `withholdUnapproved` is in here because the effect READS it, not
     * because it moves: it is `!isEdit && !isConverting`, which is fixed for the
     * life of a mounted form, so this re-fetches nothing today. Omitting it
     * would still be wrong — a list filtered under one rule and re-read under
     * another is the stale-closure bug that shows as a picker quietly offering
     * what it just withheld.
     */
  }, [resource, term, enabled, scope, withholdUnapproved]);

  /**
   * The result, but only while it describes the scope currently selected — the
   * previous company's rows are not a stale version of this company's list, they
   * are the wrong list, so they are dropped rather than shown dimmed.
   *
   * ⚠️ A STABLE reference in every branch, because `options` memoises on it. The
   * no-company answer is the module-level `NO_SCOPE_RESULT` rather than a fresh
   * `{rows: []}`, and an inline literal here would rebuild the option list on
   * every render — which is the memo silently doing nothing.
   */
  const answered =
    scope === null
      ? NO_SCOPE_RESULT
      : result !== null && result.scope === scope
        ? result
        : null;
  // With no company there is nothing in flight and nothing to wait for, so the
  // field says why it is empty instead of spinning at a request never coming.
  const loading = enabled && scope !== null && (loadedTerm !== term || answered === null);

  /**
   * True when the pinned value is NOT among the rows the list returned — i.e.
   * the option exists only because it was pinned back in.
   *
   * The call site turns this into the "not one of this company's contacts"
   * label. Computed here rather than there because only this hook can tell an
   * injected option from a fetched one once they are all `ComboboxOption`s.
   */
  const pinInjected =
    pinned !== null && !(answered?.rows ?? []).some((row) => row.id === pinned.id);

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (answered?.rows ?? []).map((row) => ({
      value: String(row.id),
      label: row.label,
    }));
    /*
     * ⚠️ The pin is UNCONDITIONAL; only the "not on this company" REASON is
     * gated (at the call site, on `contactScopeIsAuthoritative`). A stored
     * contact dropped from the list renders as "Unassigned" over a record that
     * holds one, and the next save writes that emptiness back.
     */
    if (pinned && !list.some((option) => option.value === String(pinned.id))) {
      list.unshift({ value: String(pinned.id), label: pinned.label });
    }
    return list;
  }, [answered, pinned]);

  return {
    options,
    pinInjected,
    setTerm,
    // A disabled lookup never settles a term, so it must not read as loading
    // for ever — it reads as "there is nothing to load".
    loading,
    /*
     * 🔴 The TRUNCATION clause ("Showing the first 100 matches — keep typing to
     * narrow.") went in the owner's stricter pass, 2026-09-05. Same removal on
     * the quote form, where the reasoning for keeping `withheldCompanyNote` —
     * a scope rule this form applied, not a page size — is recorded in full.
     */
    footnote:
      [withheldCompanyNote(answered?.withheld ?? 0) ?? null]
        .filter((part): part is string => part !== null)
        .join(' ') || undefined,
    /** What the Contact field needs to word its own hint. */
    scope: {
      companyId: typeof scope === 'number' ? scope : null,
      loading,
      failed: answered?.failed ?? false,
      count: answered?.rows.length ?? 0,
      searching: term.trim() !== '',
    },
  };
}

/**
 * The answer for a scoped picker with no company chosen: no rows, not loading,
 * not failed.
 *
 * ⚠️ Module-level so the reference is STABLE. `useLookupSearch` memoises its
 * option list on this object's identity, and a literal rebuilt each render
 * would make the memo a no-op while still looking like one.
 */
const NO_SCOPE_RESULT = {
  scope: null as number | null | 'unscoped',
  rows: [] as { id: number; label: string }[],
  truncated: false,
  failed: false,
  // Nothing was read, so nothing was withheld. Only ever reached by the SCOPED
  // contacts picker, which does no approval filtering in any case.
  withheld: 0,
};
